import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { trackSignupSlackConnected, trackSignupProfileCompleted } from '../lib/analytics';
import { SignupShowcase } from '../components/SignupShowcase';
import { DarkGreenTexture } from '../components/DarkGreenTexture';
import { WaitlistModal } from '../components/WaitlistModal';
import { BillingModal } from '../components/BillingModal';
import { Check, ChevronDown, Shield, Lock, FileText, ArrowRight, ArrowLeft, ArrowUp, Info, X, Search, Hash, MessageSquare, Headphones, Users, Grid3x3, Bell, Star, CreditCard, Calendar, ShieldCheck, Loader2, ArrowLeftRight, Plug, UserPlus, Sparkles, CheckCircle, ChevronRight, LayoutGrid, Plus, PanelLeft, Folder, Route as RouteIcon, SquarePen, Zap, MoreHorizontal, Gauge, Settings2, PhoneCall, Paperclip, Home, GraduationCap, Activity, FolderOpen, Database, Globe, Monitor, Network, UserCircle, Copy } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../data/workspaceTracks';
import { ONBOARDING_INTERVIEW, ONBOARDING_OPENER, ONBOARDING_SUMMARY, stepChoices, INTERVIEW_ROLES, type InterviewRole } from '../data/onboardingInterview';
import { WorkspaceViews, WorkspaceViewId } from '../components/gigachat/WorkspaceViews';
import { TrackWorkspace, type TrackSection } from '../components/gigachat/TrackWorkspace';
import { TALK_TO_SALES_URL } from '../lib/links';
const ALL_TOOLS = [{
  id: 'slack',
  name: 'Slack',
  icon: 'slack',
  category: 'communication'
}, {
  id: 'gmail',
  name: 'Gmail',
  icon: 'gmail',
  category: 'communication'
}, {
  id: 'outlook',
  name: 'Outlook',
  icon: 'microsoftoutlook',
  category: 'communication'
}, {
  id: 'teams',
  name: 'Microsoft Teams',
  icon: 'microsoftteams',
  category: 'communication'
}, {
  id: 'discord',
  name: 'Discord',
  icon: 'discord',
  category: 'communication'
}, {
  id: 'googledrive',
  name: 'Google Drive',
  icon: 'googledrive',
  category: 'docs'
}, {
  id: 'googlecalendar',
  name: 'Google Calendar',
  icon: 'googlecalendar',
  category: 'docs'
}, {
  id: 'notion',
  name: 'Notion',
  icon: 'notion',
  category: 'docs'
}, {
  id: 'confluence',
  name: 'Confluence',
  icon: 'confluence',
  category: 'docs'
}, {
  id: 'coda',
  name: 'Coda',
  icon: 'coda',
  category: 'docs'
}, {
  id: 'dropbox',
  name: 'Dropbox',
  icon: 'dropbox',
  category: 'docs'
}, {
  id: 'github',
  name: 'GitHub',
  icon: 'github',
  category: 'engineering'
}, {
  id: 'gitlab',
  name: 'GitLab',
  icon: 'gitlab',
  category: 'engineering'
}, {
  id: 'bitbucket',
  name: 'Bitbucket',
  icon: 'bitbucket',
  category: 'engineering'
}, {
  id: 'sentry',
  name: 'Sentry',
  icon: 'sentry',
  category: 'engineering'
}, {
  id: 'datadog',
  name: 'Datadog',
  icon: 'datadog',
  category: 'engineering'
}, {
  id: 'linear',
  name: 'Linear',
  icon: 'linear',
  category: 'project'
}, {
  id: 'jira',
  name: 'Jira',
  icon: 'jira',
  category: 'project'
}, {
  id: 'asana',
  name: 'Asana',
  icon: 'asana',
  category: 'project'
}, {
  id: 'trello',
  name: 'Trello',
  icon: 'trello',
  category: 'project'
}, {
  id: 'monday',
  name: 'Monday.com',
  icon: 'mondaydotcom',
  category: 'project'
}, {
  id: 'salesforce',
  name: 'Salesforce',
  icon: 'salesforce',
  category: 'crm'
}, {
  id: 'hubspot',
  name: 'HubSpot',
  icon: 'hubspot',
  category: 'crm'
}, {
  id: 'pipedrive',
  name: 'Pipedrive',
  icon: 'pipedrive',
  category: 'crm'
}, {
  id: 'zendesk',
  name: 'Zendesk',
  icon: 'zendesk',
  category: 'support'
}, {
  id: 'intercom',
  name: 'Intercom',
  icon: 'intercom',
  category: 'support'
}, {
  id: 'front',
  name: 'Front',
  icon: 'front',
  category: 'support'
}, {
  id: 'stripe',
  name: 'Stripe',
  icon: 'stripe',
  category: 'finance'
}, {
  id: 'quickbooks',
  name: 'QuickBooks',
  icon: 'quickbooks',
  category: 'finance'
}, {
  id: 'figma',
  name: 'Figma',
  icon: 'figma',
  category: 'design'
}, {
  id: 'miro',
  name: 'Miro',
  icon: 'miro',
  category: 'design'
}, {
  id: 'googleanalytics',
  name: 'Google Analytics',
  icon: 'googleanalytics',
  category: 'analytics'
}, {
  id: 'mixpanel',
  name: 'Mixpanel',
  icon: 'mixpanel',
  category: 'analytics'
}, {
  id: 'amplitude',
  name: 'Amplitude',
  icon: 'amplitude',
  category: 'analytics'
}, {
  id: 'mailchimp',
  name: 'Mailchimp',
  icon: 'mailchimp',
  category: 'marketing'
}, {
  id: 'googleads',
  name: 'Google Ads',
  icon: 'googleads',
  category: 'marketing'
}, {
  id: 'hootsuite',
  name: 'Hootsuite',
  icon: 'hootsuite',
  category: 'marketing'
}];
function getScopesForTool(tool: any) {
  if (tool.id === 'slack') {
    return [{
      id: 'read_channels',
      label: 'Read channel messages',
      description: "View messages in public channels you're a member of.",
      required: true
    }, {
      id: 'search_history',
      label: 'Search history',
      description: "Search across messages so Giga can cite what's been said.",
      required: true
    }, {
      id: 'read_files',
      label: 'Read files & attachments',
      description: 'Read files shared in channels and DMs.',
      required: false
    }, {
      id: 'read_dms',
      label: 'Read your direct messages',
      description: 'Index your DMs so Giga can find specific conversations.',
      required: false
    }, {
      id: 'post',
      label: 'Post on your behalf',
      description: 'Reply in channels and send DMs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'docs') {
    return [{
      id: 'read_files',
      label: 'Read your files & folders',
      description: `Index docs, sheets, slides and PDFs in your ${tool.name}.`,
      required: true
    }, {
      id: 'read_shared',
      label: 'Read shared drives',
      description: 'Include drives shared with you by your team.',
      required: true
    }, {
      id: 'write_docs',
      label: 'Create & edit documents',
      description: 'Draft new docs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'communication') {
    return [{
      id: 'read_msgs',
      label: 'Read messages & channels',
      description: 'Index conversations you have access to.',
      required: true
    }, {
      id: 'send_msgs',
      label: 'Send messages',
      description: 'Post updates and replies on your behalf.',
      required: true
    }];
  }
  if (tool.category === 'engineering' || tool.category === 'project') {
    return [{
      id: 'read_issues',
      label: 'Read issues & projects',
      description: 'Index tickets, PRs, and project states.',
      required: true
    }, {
      id: 'write_issues',
      label: 'Update issues',
      description: 'Change statuses and add comments.',
      required: false
    }];
  }
  return [{
    id: 'read_data',
    label: `Read ${tool.name} data`,
    description: 'Index records and objects you have access to.',
    required: true
  }, {
    id: 'write_data',
    label: `Update ${tool.name} records`,
    description: 'Create and modify records on your behalf.',
    required: false
  }];
}
function SlackIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11937" data-mp="bjh2fxx8nlzz">
      
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-11938" data-mp="3tpc714jxafvy" />
      
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#36C5F0" data-id="element-11939" data-mp="3opz9npvfdqf4" />
      
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-11940" data-mp="apj1efbmi3rb" />
      
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-11941" data-mp="3s6ab0heuu2c6" />
      
    </svg>;
}
function GoogleIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-11942" data-mp="3e6rdeijo69e4">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09z" fill="#4285F4" data-id="element-11943" data-mp="399snkc71golq" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853" data-id="element-11944" data-mp="17h4h7tz3ll1q" />
      <path d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" fill="#FBBC05" data-id="element-11945" data-mp="1jynnq3cw2uf1" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" fill="#EA4335" data-id="element-11946" data-mp="1opdunl0yr5bu" />
    </svg>;
}

// The three Google Workspace tools whose scopes are requested inside the same
// Google consent, so a Google signup arrives with a useful brain already
// connected instead of an empty one.
const GOOGLE_WORKSPACE_TOOLS = ['gmail', 'googledrive', 'googlecalendar'];
const BUSINESS_TYPES = ['E-commerce / DTC', 'Consumer app', 'SaaS / B2B software', 'Marketplace', 'Fintech', 'Healthcare / health-tech', 'Education / ed-tech', 'Media & entertainment', 'Gaming', 'AI / ML', 'Hardware / IoT', 'Real estate / proptech', 'Travel & hospitality', 'Food & beverage', 'Retail (brick & mortar)', 'Logistics & supply chain', 'Professional services', 'Agency / consulting', 'Nonprofit', 'Government / public sector', 'Manufacturing', 'Energy & climate', 'Crypto / web3', 'Other'];

// The connected Slack workspace. In production this comes from the Slack OAuth
// handshake (team name) so the post-connect screens address the user by their
// real workspace instead of a placeholder. Mirrors the Brightwave account the
// user connects earlier in the flow (brightwave.slack.com).
const WORKSPACE_NAME = 'Brightwave';
const WORKSPACE_INITIAL = WORKSPACE_NAME.charAt(0);
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  ariaLabel,
  searchable = true
}: {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }, [query, options]);
  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Focus the search field when the panel opens (only when searchable)
  useEffect(() => {
    if (open && searchable) {
      setQuery('');
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);
  return <div ref={wrapperRef} className="relative" data-id="element-11947" data-mp="2yg64szrdhugb">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${value ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-11948" data-mp="2yda79cjft7se">
        
        <span className="truncate" data-id="element-11949" data-mp="qra3ol17850r">{value || placeholder}</span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11950" data-mp="1ek0fmu93qob1" />
        
      </button>

      <AnimatePresence data-id="element-11951" data-mp="1jfo3n6bulbey">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-11952" data-mp="15isgyb2oblgf">
          
            {searchable && <div className="p-2 border-b border-neutral-100" data-id="element-11953" data-mp="3mq13ubdwcjrf">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 focus-within:border-[#EA580C] transition-colors" data-id="element-11954" data-mp="3mseyij4nztyg">
                  <Search size={16} className="text-neutral-400 shrink-0" data-id="element-11955" data-mp="iu6wrgyeqjk" />
                  <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full bg-transparent text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none" data-id="element-11956" data-mp="3rpdoqql7jgrd" />
              
                </div>
              </div>}
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-11957" data-mp="3dq5t27o96qrx">
              {filtered.length === 0 ? <li className="px-4 py-3 text-sm text-neutral-400" data-id="element-11958" data-mp="38y4hknywt42y">
                  No matches found
                </li> : filtered.map(option => {
            const isSelected = option === value;
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-11959" data-mp="2g9gsehb1huk4">
                      <button type="button" onClick={() => {
                onChange(option);
                setOpen(false);
              }} className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-11960" data-mp="1jjmzeug5giz5">
                    
                        <span className="truncate" data-id="element-11961" data-mp="1xcckmwnutjv7">{option}</span>
                        {isSelected && <Check size={16} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-11962" data-mp="22bp9fmyad10f" />}
                      </button>
                    </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MultiSelectDropdown({
  values,
  onChange,
  options,
  placeholder = 'Select options'
}: {
  values: string[];
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  return <div ref={wrapperRef} className="relative" data-id="element-11963" data-mp="m9wtx8vr063">
      <button type="button" onClick={() => setOpen(o => !o)} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${values.length > 0 ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-11964" data-mp="xb7sdhogf24b">
        
        <span className="truncate" data-id="element-11965" data-mp="ja56b4xglrte">
          {values.length === 0 ? placeholder : values.length === 1 ? values[0] : `${values.length} selected`}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11966" data-mp="341fckyp6kgfq" />
        
      </button>

      <AnimatePresence data-id="element-11967" data-mp="35iuh8062ii3k">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-11968" data-mp="35gi01p84wbrk">
          
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-11969" data-mp="2mpqlm99ahv6b">
              {options.map(option => {
            const isSelected = values.includes(option);
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-11970" data-mp="2cuyrjfex1qga">
                    <button type="button" onClick={() => onChange(option)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5]' : 'hover:bg-neutral-50'}`} data-id="element-11971" data-mp="2hrys5aszy100">
                    
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-11972" data-mp="28u4yy8qj3nh2">
                      
                        {isSelected && <Check size={12} className="text-white" strokeWidth={3} data-id="element-11973" data-mp="1y771j6r97cui" />}
                      </div>
                      <span className={`truncate ${isSelected ? 'text-[#333333] font-medium' : 'text-[#333333]'}`} data-id="element-11974" data-mp="1u4adv1muwqjp">
                      
                        {option}
                      </span>
                    </button>
                  </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function VisibilityDropdown({
  value,
  onChange
}: {
  value: 'team' | 'private';
  onChange: (val: 'team' | 'private') => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const isPrivate = value === 'private';
  const OPTIONS: {
    id: 'team' | 'private';
    Icon: typeof Users;
    label: string;
  }[] = [{
    id: 'team',
    Icon: Users,
    label: 'Team-only'
  }, {
    id: 'private',
    Icon: Lock,
    label: 'Private (invite only)'
  }];
  return <div ref={wrapperRef} className="relative" data-id="element-11975" data-mp="3el408mc48zyw">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label="Who can use this connection?" className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${open ? 'border-[#333333] bg-white' : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'} text-neutral-600`} data-id="element-11976" data-mp="3ehpxhczjioqx">
        
        {isPrivate ? <Lock size={11} data-id="element-11977" data-mp="2o9tlr908serz" /> : <Users size={11} data-id="element-11978" data-mp="2oabpsrt5bxag" />}
        {isPrivate ? 'Private' : 'Team-only'}
        <ChevronDown size={12} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11979" data-mp="2jaz2y729yq7v" />
        
      </button>

      <AnimatePresence data-id="element-11980" data-mp="2eaunmkd3s1j8">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.14,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute right-0 z-30 mt-2 w-60 rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" role="listbox" data-id="element-11981" data-mp="2edyyg0l2pviv">
          
            <div className="px-3 pt-2.5 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11982" data-mp="1qncou4vf836r">
              Who should have access?
            </div>
            {OPTIONS.map(opt => {
          const active = opt.id === value;
          return <button key={opt.id} type="button" role="option" aria-selected={active} onClick={() => {
            onChange(opt.id);
            setOpen(false);
          }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${active ? 'text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-11983" data-mp="1hnzgjfj19knm">
                
                  <opt.Icon size={15} className="text-neutral-500 shrink-0" data-id="element-11984" data-mp="3ona367uh89ii" />
                  <span className="flex-1 truncate" data-id="element-11985" data-mp="3jpqk8umu38k8">{opt.label}</span>
                  {active && <Check size={15} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-11986" data-mp="3exgsd925ryfb" />}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function StickyRevealBar({
  children
}: {
  children: React.ReactNode;
}) {
  // A persistent fixed action bar pinned to the bottom of the screen. It stays
  // visible no matter where the user is in the scroll, with a one-time slide-up
  // entrance on mount. Self-contained so it never affects the page hook order.
  return <motion.div initial={{
    y: 90,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    stiffness: 380,
    damping: 34,
    delay: 0.1
  }} className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur-sm shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]" data-id="element-11987" data-mp="2lvz1oe8g1mzn">
      
      <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-11988" data-mp="30rorkngr2k9n">{children}</div>
    </motion.div>;
}
// The tool picker must show the real, familiar mark for every company — not
// a letter badge or a loosely drawn icon. Each entry maps directly to the
// product's domain; logo.dev serves the canonical brand asset and the brand
// favicon is a resilient fallback if that service is temporarily unavailable.
const TOOL_LOGO_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  microsoftoutlook: 'outlook.com',
  microsoftteams: 'teams.microsoft.com',
  discord: 'discord.com',
  googledrive: 'drive.google.com',
  notion: 'notion.so',
  confluence: 'atlassian.com',
  coda: 'coda.io',
  dropbox: 'dropbox.com',
  github: 'github.com',
  gitlab: 'gitlab.com',
  bitbucket: 'bitbucket.org',
  sentry: 'sentry.io',
  datadog: 'datadoghq.com',
  linear: 'linear.app',
  jira: 'atlassian.com',
  asana: 'asana.com',
  trello: 'trello.com',
  mondaydotcom: 'monday.com',
  salesforce: 'salesforce.com',
  hubspot: 'hubspot.com',
  pipedrive: 'pipedrive.com',
  zendesk: 'zendesk.com',
  intercom: 'intercom.com',
  front: 'front.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  miro: 'miro.com',
  googleanalytics: 'analytics.google.com',
  mixpanel: 'mixpanel.com',
  amplitude: 'amplitude.com',
  mailchimp: 'mailchimp.com',
  googleads: 'ads.google.com',
  hootsuite: 'hootsuite.com'
};
const LOGO_DEV_TOKEN = 'pk_E8dUI9PzTHmmaUwDeUCyiQ';
function ToolLogo({
  name,
  icon,
  className = 'w-12 h-12 mx-auto mb-3'
}: {
  name: string;
  icon: string;
  className?: string;
  fallbackTextSize?: string;
}) {
  const [source, setSource] = useState<'canonical' | 'favicon'>('canonical');
  const domain = TOOL_LOGO_DOMAINS[icon];
  const src = source === 'canonical' ? `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=128` : `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  return <img src={src} alt={name} onError={() => {
    if (source === 'canonical') setSource('favicon');
  }} className={`${className} object-contain`} data-id="element-11989" data-mp="2hdgsfjajglrw" />;
}
function TeamsIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11990" data-mp="3a0l2i2ohnqaj">
      
      <path d="M16.5 13.5c1.93 0 3.5-1.57 3.5-3.5s-1.57-3.5-3.5-3.5-3.5 1.57-3.5 3.5 1.57 3.5 3.5 3.5z" fill="#5059C9" data-id="element-11991" data-mp="2w9fykoe38bue" />
      
      <path d="M22.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#5059C9" data-id="element-11992" data-mp="3o0vtr6tpn6w8" />
      
      <path d="M7.5 12c1.93 0 3.5-1.57 3.5-3.5S9.43 5 7.5 5 4 6.57 4 8.5 5.57 12 7.5 12z" fill="#7B83EB" data-id="element-11993" data-mp="3srcb320njcnv" />
      
      <path d="M13.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#7B83EB" data-id="element-11994" data-mp="253j0g851ogm6" />
      
    </svg>;
}
function GigaHeader({
  hideOnMobile = false
}: {
  hideOnMobile?: boolean;
}) {
  return <div className={`border-b border-neutral-100 px-6 py-4 ${hideOnMobile ? 'hidden lg:block' : ''}`} data-id="element-11995" data-mp="1v64oakq5mdzo">
      
      <Link to="/" aria-label="Back to Giga home" className="inline-flex items-center gap-2 group w-fit" data-id="element-11996" data-mp="1r52kdqlaldrd">
        
        <div className="w-8 h-8 rounded-lg bg-[#EA580C] flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-11997" data-mp="2nsz6tc3ev3ai">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11998" data-mp="2e3pd5rsx5cka" />
          
        </div>
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-11999" data-mp="36m2xqpp8v21f" />
        
      </Link>
    </div>;
}
export function Signup() {
  const screenInit = useScreenInit();
  const location = useLocation();
  const navigate = useNavigate();
  // Returning users go through /app-screen-mockups/sign-in, which is its own
  // screen — this component is only the signup flow.
  const isLogin = false;
  // The feature isn't live yet — the public "Get started" CTAs send users here
  // with ?waitlist=1, which drops them on the Slack connect screen (step 2)
  // with the waitlist popup open over it.
  const waitlistParam = new URLSearchParams(location.search).get('waitlist') === '1';
  // Any screen in this flow can be opened directly with ?step=<n> (e.g.
  // ?step=22 for the Giga web chat landing), so reviewing a single screen
  // doesn't mean clicking through the whole dummy signup.
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  // A few screens also get their own real route, so they show up in the route
  // dropdown and can be opened without knowing a step number.
  const STEP_ROUTES: Record<string, number> = {
    '/app-screen-mockups/google-consent': 20,
    '/app-screen-mockups/google-permissions': 21,
    '/app-screen-mockups/giga-chat': 22
  };
  const stepFromPath = STEP_ROUTES[location.pathname];
  const stepFromUrl = stepFromPath ?? (Number.isFinite(stepParam) && stepParam > 0 ? stepParam : undefined);
  const [step, setStep] = useState(stepFromUrl ?? screenInit.step ?? (waitlistParam ? 2 : isLogin ? 10 : 1));
  const [showWaitlist, setShowWaitlist] = useState(waitlistParam);
  // Which front door the user came through. Slack signups end in Slack; Google
  // signups have no Slack to hand off to, so they land in Giga's web chat and
  // can add Slack later. Only the final destination differs — the setup steps
  // in between are shared.
  const [authProvider, setAuthProvider] = useState<'slack' | 'google'>(stepFromUrl && stepFromUrl >= 20 ? 'google' : 'slack');
  // Step 22: the web chat's nav stays out of the way. A collapsed rail holds
  // the brand mark and one toggle; the sidebar itself slides in over the page
  // only when asked for, so the composer is never competing with navigation.
  const [chatNavOpen, setChatNavOpen] = useState(false);
  const [chatNavMoreOpen, setChatNavMoreOpen] = useState(false);
  const [chatNavSearchOpen, setChatNavSearchOpen] = useState(false);
  const [chatNavQuery, setChatNavQuery] = useState('');
  const [chatAccountOpen, setChatAccountOpen] = useState(false);
  const [chatBillingOpen, setChatBillingOpen] = useState(false);
  // Step 22 composer: what this chat is allowed to draw on. Empty by default —
  // Giga uses everything the user can see unless they narrow it.
  const [contextOpen, setContextOpen] = useState(false);
  const [contextTab, setContextTab] = useState<'Groups' | 'Tracks' | 'Credentials' | 'Databases'>('Groups');
  const [contextQuery, setContextQuery] = useState('');
  const [chatContext, setChatContext] = useState<string[]>([]);
  const [chatModelOpen, setChatModelOpen] = useState(false);
  const [chatModel, setChatModel] = useState<'Auto' | 'Giga Pro' | 'Claude Sonnet' | 'GPT-5'>('Auto');
  // Which pane the chat shell is showing. 'chat' is the thread; everything
  // else is a workspace view rendered inside the same shell, so the nav never
  // navigates away. A selected track is separate from `chatView`: it owns the
  // whole canvas and deliberately bypasses WorkspaceViews' max-width wrapper.
  const [chatView, setChatView] = useState<'chat' | WorkspaceViewId>(screenInit.chatView ?? 'chat');
  const [chatTrack, setChatTrack] = useState<string | null>(screenInit.chatTrack ?? null);
  // Picking a destination does NOT close the nav — once opened it stays until
  // the user closes it, so moving between views doesn't cost a re-open.
  const openChatView = (v: 'chat' | WorkspaceViewId) => {
    setChatView(v);
    setChatTrack(null);
    setChatNavMoreOpen(false);
  };
  const openTrack = (trackName: string) => {
    setChatView('tracks');
    setChatTrack(trackName);
    setChatNavMoreOpen(false);
  };
  // DEBUG: a brand-new workspace with nothing connected yet, so the context
  // picker's empty state can be reviewed without unpicking the setup steps.
  const [chatNoConnections, setChatNoConnections] = useState(false);
  const [chatNotifOpen, setChatNotifOpen] = useState(false);
  const [chatChecklistOpen, setChatChecklistOpen] = useState(false);
  const [chatChecklistStep, setChatChecklistStep] = useState<string | null>('Connect your tools');
  // Step 22: the live thread. Empty until the first prompt, at which point the
  // greeting is replaced by the conversation and the composer stays put.
  type ChatMsg = {
    id: number;
    role: 'user' | 'giga';
    text?: string;
    steps?: {
      icon: string;
      label: string;
    }[];
    answer?: string[];
    sources?: {
      icon: string;
      label: string;
    }[];
    thinking?: boolean;
    /** Onboarding interview: suggested answers for this question. */
    choices?: string[];
    multi?: boolean;
    /** Set once the question has been answered, so the chips retire. */
    answered?: boolean;
    /** Closing summary of what the interview produced. */
    created?: readonly {
      icon: string;
      title: string;
      detail: string;
    }[];
    /** A Giga concept being introduced alongside the question. */
    concept?: {
      label: string;
      title: string;
      lines: string[];
    };
  };
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  // Conversational onboarding. `null` = never started; an index = mid-interview;
  // 'done' = finished, which is what ticks the setup checklist.
  const [interviewIndex, setInterviewIndex] = useState<number | 'done' | null>(null);
  const [interviewPicks, setInterviewPicks] = useState<string[]>([]);
  // Captured at step one; every later question branches on it.
  const [interviewRole, setInterviewRole] = useState<InterviewRole | null>(null);
  // Derived rather than hardcoded, so the ring and the list can never disagree.
  const setupProgress = (() => {
    const done = 2 + (interviewIndex === 'done' ? 1 : 0) + (chatMessages.length > 0 ? 1 : 0);
    return {
      done,
      total: 6,
      ratio: done / 6
    };
  })();
  const [chatCreditNoticeSeen, setChatCreditNoticeSeen] = useState(false);
  const [chatDraft, setChatDraft] = useState('');
  const chatThreadRef = useRef<HTMLDivElement>(null);
  const chatScripts: Record<string, {
    steps: {
      icon: string;
      label: string;
    }[];
    answer: string[];
    sources: {
      icon: string;
      label: string;
    }[];
  }> = {
    "What changed in my inbox while I was out?": {
      steps: [{
        icon: 'gmail',
        label: 'Read 63 unread threads in Gmail'
      }, {
        icon: 'googlecalendar',
        label: 'Cross-checked against your calendar'
      }],
      answer: ['**Three things need you today.**', '**Acme renewal** — their legal team sent redlines on Tuesday and asked for a reply by Friday. Two clauses changed: liability cap and data residency.', '**Northwind pilot** — Marcus confirmed the pilot start for 8 Sep and is waiting on your sign-off for seat count.', '**Invoice #2214** — finance flagged it as 21 days overdue and has already chased twice.', 'The remaining 60 threads are newsletters, calendar noise and CC traffic. I can archive those if you want.'],
      sources: [{
        icon: 'gmail',
        label: '63 threads'
      }, {
        icon: 'googlecalendar',
        label: '4 events'
      }]
    },
    'Summarise the deck in Drive from yesterday': {
      steps: [{
        icon: 'googledrive',
        label: 'Found “Q3 Board Review.pdf” — edited yesterday'
      }, {
        icon: 'googledrive',
        label: 'Read 24 slides'
      }],
      answer: ['**Q3 Board Review — 24 slides, edited by Priya at 18:40.**', 'The argument is that growth held while spend fell: revenue up 34% quarter over quarter, CAC down 12%, and net retention at 118%.', 'Two risks are named — hiring is a quarter behind plan, and one enterprise renewal worth 8% of ARR is unsigned.', 'Slide 19 has a gap: the ask is stated but there is no numbers slide behind it. Worth filling before Thursday.'],
      sources: [{
        icon: 'googledrive',
        label: 'Q3 Board Review.pdf'
      }]
    },
    "Who am I meeting tomorrow, and what do I need?": {
      steps: [{
        icon: 'googlecalendar',
        label: 'Read tomorrow’s 4 events'
      }, {
        icon: 'gmail',
        label: 'Pulled the last thread with each attendee'
      }],
      answer: ['**Four meetings tomorrow. Two need prep.**', '**09:30 — Acme, legal review (45m).** Bring a position on the liability cap; their redlines are still unanswered.', '**11:00 — Internal standup (15m).** Nothing needed.', '**14:00 — Northwind, pilot kickoff (60m).** Marcus wants seat count confirmed. Last thread says 25, your notes say 40.', '**16:30 — 1:1 with Priya (30m).** She flagged the slide 19 gap, so expect that.', 'I can draft the Acme reply and a one-pager for Northwind now.'],
      sources: [{
        icon: 'googlecalendar',
        label: '4 events'
      }, {
        icon: 'gmail',
        label: '6 threads'
      }]
    }
  };
  // ── Conversational onboarding ──────────────────────────────────────────
  // Giga asks one question per turn. Answers arrive either from the suggestion
  // chips or from the composer, so the interview never blocks free typing.
  const pushInterviewQuestion = (index: number, role: InterviewRole | null, delay = 900) => {
    const step = ONBOARDING_INTERVIEW[index];
    const choices = stepChoices(step, role);
    const id = Date.now() + index + 2;
    setChatMessages(prev => [...prev, {
      id,
      role: 'giga',
      thinking: true,
      steps: step.work
    }]);
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === id ? {
        ...m,
        thinking: false,
        answer: [...step.question],
        concept: step.concept ? {
          ...step.concept,
          lines: [...step.concept.lines]
        } : undefined,
        choices,
        multi: step.multi
      } : m));
      // Opt out, not in: the likely answers arrive already ticked.
      if (step.multi && step.preselectAll) setInterviewPicks(choices);
    }, delay);
  };
  const startOnboarding = () => {
    setChatChecklistOpen(false);
    setChatCreditNoticeSeen(true);
    setInterviewIndex(0);
    setInterviewPicks([]);
    setInterviewRole(null);
    setChatDraft('');
    // The composer is pre-populated and sent for them, so the interview starts
    // as a normal message in the thread rather than a modal takeover.
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      text: ONBOARDING_OPENER
    }]);
    pushInterviewQuestion(0, null, 1100);
  };
  const answerInterview = (text: string) => {
    if (typeof interviewIndex !== 'number') return;
    const next = interviewIndex + 1;
    // Step one sets the branch for every question after it. Free text still
    // works — anything unrecognised falls back to the generalist set.
    let role = interviewRole;
    if (ONBOARDING_INTERVIEW[interviewIndex].key === 'role') {
      role = INTERVIEW_ROLES.find(r => r.toLowerCase() === text.trim().toLowerCase()) ?? null;
      setInterviewRole(role);
    }
    setChatDraft('');
    setInterviewPicks([]);
    setChatMessages(prev => [...prev.map(m => m.choices ? {
      ...m,
      answered: true
    } : m), {
      id: Date.now(),
      role: 'user',
      text
    }]);
    if (next < ONBOARDING_INTERVIEW.length) {
      setInterviewIndex(next);
      pushInterviewQuestion(next, role, 1000);
      return;
    }
    // Final turn: state what the interview actually produced.
    setInterviewIndex('done');
    const id = Date.now() + 1;
    setChatMessages(prev => [...prev, {
      id,
      role: 'giga',
      thinking: true,
      steps: [...ONBOARDING_SUMMARY.work]
    }]);
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === id ? {
        ...m,
        thinking: false,
        answer: [...ONBOARDING_SUMMARY.answer],
        created: ONBOARDING_SUMMARY.created
      } : m));
    }, 1500);
  };
  const sendChatMessage = (text: string) => {
    const clean = text.trim();
    if (!clean) return;
    if (typeof interviewIndex === 'number') {
      answerInterview(clean);
      return;
    }
    // The credit welcome is a first-run message. Once they've asked something
    // it has done its job, so it never returns — even on a new chat.
    setChatCreditNoticeSeen(true);
    const script = chatScripts[clean] ?? {
      steps: [{
        icon: 'gmail',
        label: 'Searched your connected tools'
      }],
      answer: ["Here's what I found across Gmail, Drive and Calendar. Ask me to act on any of it and I'll take it from there."],
      sources: [{
        icon: 'gmail',
        label: 'Gmail'
      }, {
        icon: 'googledrive',
        label: 'Drive'
      }]
    };
    const base = Date.now();
    setChatDraft('');
    setChatMessages(prev => [...prev, {
      id: base,
      role: 'user',
      text: clean
    }, {
      id: base + 1,
      role: 'giga',
      thinking: true,
      steps: script.steps
    }]);
    // Reveal the steps, then the answer, so the thread reads as work being
    // done rather than a block of text appearing at once.
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === base + 1 ? {
        ...m,
        thinking: false,
        answer: script.answer,
        sources: script.sources
      } : m));
    }, 1600);
  };
  useEffect(() => {
    if (chatMessages.length > 0) {
      chatThreadRef.current?.scrollTo({
        top: chatThreadRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatMessages]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  // Step 10: once the user signs out of all workspaces, the remembered
  // workspace list disappears and the screen falls back to a clean sign-in.
  const [signedOut, setSignedOut] = useState(false);
  // Step 10: only the most recently used workspace is shown by default. The
  // full list is one click away, so returning users get a single obvious way
  // back in rather than a wall of near-identical rows competing with the
  // auth buttons above them.
  const [showAllWorkspaces, setShowAllWorkspaces] = useState(false);
  // Step 10: Microsoft Teams isn't live yet, so the "Join Microsoft Teams
  // waitlist" button opens an inline email capture and confirms once submitted.
  const [teamsWaitlistOpen, setTeamsWaitlistOpen] = useState(false);
  const [teamsWaitlistEmail, setTeamsWaitlistEmail] = useState('');
  const [teamsWaitlistDone, setTeamsWaitlistDone] = useState(false);
  const submitTeamsWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamsWaitlistEmail.trim()) return;
    setTeamsWaitlistDone(true);
  };
  // Toggle a tool in/out of the selected set (used by the step 3 tool grid)
  const toggleTool = (id: string) => {
    setSelectedTools(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [toolSearch, setToolSearch] = useState('');
  // Step 5: "Tell us more about you" select answers
  const [foundUs, setFoundUs] = useState('');
  const [role, setRole] = useState('');
  // Step 5: what kind of business the user runs (required, searchable)
  const [businessType, setBusinessType] = useState('');
  // Step 5: which AI Teammate(s) the user wants to create first (required, multi-select)
  const [firstTeammates, setFirstTeammates] = useState<string[]>([]);
  const toggleFirstTeammate = (option: string) => setFirstTeammates(prev => prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]);
  // Step 6: Functional roles for tool recommendations (multi-select)
  const [funcRoles, setFuncRoles] = useState<string[]>([]);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  // Step 6: integrations the user requested when not found in the catalog
  const [requestedTools, setRequestedTools] = useState<string[]>([]);
  // Step 6: optional link the user can paste for a tool we don't list yet
  const [requestedToolLink, setRequestedToolLink] = useState('');
  // Step 11: selected trial plan
  const [plan, setPlan] = useState('plus');
  // Step 11: mobile-only order-summary disclosure (Stripe Checkout pattern)
  const [payDetailsOpen, setPayDetailsOpen] = useState(false);
  // Step 7: per-tool OAuth-style connection flow
  const [connectedConns, setConnectedConns] = useState<string[]>([]);
  // Whether the user has kicked off the connection sequence with the start button
  const [connectionsStarted, setConnectionsStarted] = useState(false);
  const [permModal, setPermModal] = useState<null | {
    id: string;
    name: string;
    icon: string;
    scopes: any[];
  }>(null);
  const [connectPhase, setConnectPhase] = useState<'consent' | 'handing-off' | null>(null);
  // Step 3: Slack OAuth consent — per-permission checkbox state. The two
  // REQUIRED scopes stay locked on; the rest are user-toggleable.
  const [slackOAuthScopes, setSlackOAuthScopes] = useState<Record<string, boolean>>({
    readChannels: true,
    searchHistory: true,
    readFiles: true,
    readDMs: false,
    postOnBehalf: true
  });
  // Step 7: local state for optional permission checkboxes in the modal
  const [optionalScopes, setOptionalScopes] = useState<Record<string, boolean>>({});
  // Step 7: per-tool integration visibility — 'private' (only you) or 'team'
  // (shared with the whole workspace).
  const [connVisibility, setConnVisibility] = useState<Record<string, 'private' | 'team'>>({});
  const USE_CASE_RECS: Record<string, string[]> = {
    'Marketing & Ad Campaigns': ['googleanalytics', 'googleads', 'mailchimp', 'hubspot', 'notion', 'slack'],
    'Operations & Workflow Automation': ['notion', 'slack', 'googledrive', 'asana', 'monday', 'quickbooks'],
    'Data analysis & reporting': ['googleanalytics', 'mixpanel', 'amplitude', 'notion', 'slack', 'github'],
    'Sales & CRM': ['salesforce', 'hubspot', 'pipedrive', 'slack', 'gmail', 'googledrive'],
    'Content & Creative Production': ['figma', 'miro', 'notion', 'slack', 'googledrive', 'dropbox'],
    'Engineering & Development': ['github', 'linear', 'slack', 'sentry', 'googledrive', 'notion'],
    'Executive / Leadership Assistant': ['slack', 'gmail', 'notion', 'googledrive', 'stripe', 'salesforce'],
    'Agency/Consultancy & Client Operations': ['slack', 'notion', 'googledrive', 'asana', 'trello', 'zoom'],
    'eCommerce & Shopify': ['shopify', 'stripe', 'mailchimp', 'googleanalytics', 'slack', 'zendesk'],
    Other: ['slack', 'notion', 'googledrive', 'gmail']
  };
  useEffect(() => {
    if (step === 6 && selectedTools.length === 0) {
      const activeRoles = funcRoles.length > 0 ? funcRoles : firstTeammates.length > 0 ? firstTeammates : ['Engineering & Development'];
      if (funcRoles.length === 0) {
        setFuncRoles(activeRoles);
      }
      const initialTools = new Set<string>();
      activeRoles.forEach(r => {
        USE_CASE_RECS[r]?.forEach(t => initialTools.add(t));
      });
      if (initialTools.size === 0) {
        USE_CASE_RECS['Engineering & Development'].forEach(t => initialTools.add(t));
      }
      // A Google signup already granted Gmail, Drive and Calendar during
      // consent, so those always appear (and show as connected) rather than
      // being offered again as something to set up.
      if (authProvider === 'google') {
        GOOGLE_WORKSPACE_TOOLS.forEach(t => initialTools.add(t));
      }
      setSelectedTools(Array.from(initialTools));
    }
  }, [step, firstTeammates, funcRoles.length, selectedTools.length, authProvider]);
  // Step 7: connections to make — derived from the tools the user selected.
  // Falls back to a sensible default set when nothing was selected (e.g. when
  // the screen is opened directly via screen state).
  const step7Connections = useMemo(() => {
    const ids = selectedTools.length > 0 ? selectedTools : ['slack', 'googledrive', 'notion', 'github', 'linear', 'sentry'];
    return ids.map(id => {
      const tool = ALL_TOOLS.find(t => t.id === id) || {
        id,
        name: id,
        icon: id,
        category: 'other'
      };
      return {
        ...tool,
        scopes: getScopesForTool(tool)
      };
    });
  }, [selectedTools]);
  const step7ActiveIdx = step7Connections.findIndex(c => !connectedConns.includes(c.id));
  const step7AllDone = step7Connections.length > 0 && connectedConns.length === step7Connections.length;
  // Where setup ends. A Slack signup gets handed off to Slack; a Google signup
  // has nothing to hand off to, so it lands in Giga's own web chat.
  const finalStep = authProvider === 'google' ? 22 : 8;
  // Landing/left-pane animation state. These hooks MUST live above the
  // per-step early returns below — otherwise renders that return early would
  // run fewer hooks than renders that fall through, tripping React #300.
  const LANDING_TESTIMONIAL_COUNT = 3;
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % LANDING_TESTIMONIAL_COUNT);
    }, 7000);
    return () => clearInterval(testimonialInterval);
  }, []);
  // Setup checklist progress — the rows resolve one-by-one (pending → done) so
  // the card reads like a live, sub-60-second setup actually happening.
  const SETUP_STEPS = 3;
  const [setupStep, setSetupStep] = useState(0);
  useEffect(() => {
    const start = setTimeout(() => {
      const id = setInterval(() => {
        setSetupStep(prev => {
          if (prev >= SETUP_STEPS) {
            clearInterval(id);
            return prev;
          }
          return prev + 1;
        });
      }, 750);
      (window as any).__gigaSetupInterval = id;
    }, 600);
    return () => {
      clearTimeout(start);
      clearInterval((window as any).__gigaSetupInterval);
    };
  }, []);
  // Step 22: where a Google signup lands. There is no Slack to hand off to,
  // so Giga's own web chat is the product surface. Opens on a real composer
  // with starters drawn from what was just connected, so the first question is
  // one click rather than a blank page. Slack is offered, not required.
  if (step === 22) {
    const starters = ["What changed in my inbox while I was out?", 'Summarise the deck in Drive from yesterday', "Who am I meeting tomorrow, and what do I need?"];
    const sidebarChats = ['Inbox catch-up', 'Tomorrow’s meetings', 'Q3 pipeline review', 'Renewal risk in Salesforce', 'Draft reply to Acme legal', 'Summarise the board deck'];
    const visibleSidebarTracks = TRACKS.filter(track => track.status === 'active').slice(0, 6);
    const visibleSidebarChats = sidebarChats;
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className={`min-h-screen bg-app-canvas font-body text-app transition-[padding] duration-250 ease-[cubic-bezier(0.23,1,0.32,1)] ${chatNavOpen ? 'md:pl-[260px]' : 'md:pl-14'}`} data-id="element-12000" data-mp="3qe2l60u8ah8b">
        {/* Collapsed rail: just the mark and the one control that reveals the
         nav. Mirrors the reference — brand top-left, nothing else claiming
         space until the user asks for it. Hidden while the nav is pinned open,
         since the panel carries its own header. */}
        <div className={`fixed left-0 top-0 z-30 flex items-center gap-1 p-3 md:hidden ${chatNavOpen ? 'hidden' : ''}`} data-id="element-12001" data-mp="10uioyr1e6sjp">
          <Link to="/" aria-label="Brightwave workspace" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[13px] font-bold tracking-tight text-white transition-transform hover:scale-105" data-id="element-12002" data-mp="vwqp9auia24y">
            BW
          </Link>
          <button type="button" onClick={() => setChatNavOpen(true)} aria-label="Open navigation" aria-expanded={chatNavOpen} className="flex h-9 w-9 items-center justify-center rounded-lg text-app-subtle transition-colors hover:bg-app-hover hover:text-app" data-id="element-12003" data-mp="1ke7uj3shu8h7">
            <PanelLeft size={17} data-id="element-12004" data-mp="wq7wdisurfsl" />
          </button>
        </div>

        {/* Collapsed desktop rail. Closing the nav should not cost access to
         the handful of things people reach for constantly, so the labels go
         and the icons stay — same order, same destinations. */}
        {!chatNavOpen && <div className="fixed left-0 top-0 z-30 hidden h-full w-14 flex-col items-center gap-1 border-r border-app bg-app-surface py-3 md:flex" data-id="element-12005" data-mp="rsep0mftcfqq">
            <Link to="/" aria-label="Brightwave workspace" className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[11px] font-bold tracking-tight text-white transition-transform duration-200 ease-out hover:scale-105" data-id="element-12006" data-mp="hth4dulid6d0">
              BW
            </Link>
            <button type="button" onClick={() => setChatNavOpen(true)} aria-label="Open navigation" className="flex h-9 w-9 items-center justify-center rounded-lg text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12007" data-mp="35xzsstazbk08">
              <PanelLeft size={18} strokeWidth={1.8} data-id="element-12008" data-mp="3jv6a0jg21anc" />
            </button>
            {[{
          label: 'New chat',
          icon: SquarePen,
          onClick: () => {
            openChatView('chat');
            setChatMessages([]);
          }
        }, {
          label: 'Search chats',
          icon: Search,
          onClick: () => setChatNavSearchOpen(true)
        }, {
          label: 'Tracks',
          icon: RouteIcon,
          onClick: () => openChatView('tracks')
        }, {
          label: 'Chats',
          icon: MessageSquare,
          onClick: () => openChatView('chats')
        }].map(item => <button key={item.label} type="button" onClick={item.onClick} aria-label={item.label} title={item.label} className="flex h-9 w-9 items-center justify-center rounded-lg text-app-muted transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12009" data-mp="1rwtor3ts0af3">
                <item.icon size={18} strokeWidth={1.8} data-id="element-12010" data-mp="1i0icgdu6rkkn" />
              </button>)}
          </div>}

        {/* Search is a dialog, not a filter strip: it spans chats and tracks,
         and it opens with the things most likely to be wanted before a single
         character is typed. */}
        <AnimatePresence data-id="element-12011" data-mp="25uiaedvo3ll7">
          {chatNavSearchOpen && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.16,
            ease: 'easeOut'
          }} onClick={() => {
            setChatNavSearchOpen(false);
            setChatNavQuery('');
          }} className="fixed inset-0 z-[60] bg-[#20202408]" data-id="element-12012" data-mp="20y1jjbx32t4f" />
              <motion.div initial={{
            opacity: 0,
            y: 8,
            scale: 0.985
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 8,
            scale: 0.985
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} role="dialog" aria-label="Search chats and tracks" className="fixed left-1/2 top-[14vh] z-[61] w-[min(640px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-2xl border border-app bg-app-surface shadow-[0_40px_90px_-30px_rgba(20,20,24,0.35)]" data-id="element-12013" data-mp="voxdzf6e2pqx">
                <div className="flex items-center gap-3 px-5 py-4" data-id="element-12014" data-mp="3z2mbbv26nz3">
                  <input type="text" autoFocus value={chatNavQuery} onChange={event => setChatNavQuery(event.target.value)} placeholder="Search…" aria-label="Search chats and tracks" className="flex-1 bg-transparent text-[16px] text-app outline-none placeholder:text-app-subtle" data-id="element-12015" data-mp="1tesa5iokuufd" />
                  <button type="button" onClick={() => {
                setChatNavSearchOpen(false);
                setChatNavQuery('');
              }} aria-label="Close search" className="flex size-8 shrink-0 items-center justify-center rounded-lg text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12016" data-mp="23iguf18gmdsw">
                    <X size={16} data-id="element-12017" data-mp="2r5fba00hrnrc" />
                  </button>
                </div>
                <div className="max-h-[52vh] overflow-y-auto border-t border-app-divider px-2 py-2" data-id="element-12018" data-mp="2m7nht1k77tl1">
                  {(() => {
                const query = chatNavQuery.trim().toLowerCase();
                const groups = [{
                  label: query ? 'Chats' : 'Last opened',
                  icon: MessageSquare,
                  items: (query ? sidebarChats : sidebarChats.slice(0, 3)).filter(chat => !query || chat.toLowerCase().includes(query)),
                  onSelect: () => openChatView('chats')
                }, {
                  label: query ? 'Tracks' : 'Recent tracks',
                  icon: RouteIcon,
                  items: TRACKS.filter(track => track.status === 'active' && (!query || track.name.toLowerCase().includes(query))).slice(0, query ? 8 : 4).map(track => track.name),
                  onSelect: undefined
                }].filter(group => group.items.length > 0);
                if (groups.length === 0) {
                  return <p className="px-3 py-10 text-center text-[13.5px] text-app-subtle" data-id="element-12019" data-mp="ok36whgmy850">
                        Nothing matches “{chatNavQuery}”.
                      </p>;
                }
                return groups.map(group => <div key={group.label} className="mb-2 last:mb-0" data-id="element-12020" data-mp="ybfxl5qp6s9g">
                      <p className="px-3 pb-1 pt-2 text-[12.5px] font-medium text-app-subtle" data-id="element-12021" data-mp="va4c25id21f">
                        {group.label}
                      </p>
                      {group.items.map(item => <button key={item} type="button" onClick={() => {
                    if (group.onSelect) group.onSelect();else openTrack(item);
                    setChatNavSearchOpen(false);
                    setChatNavQuery('');
                  }} className="flex h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-[14px] font-medium text-app-muted transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12022" data-mp="3ufyl1kd0zc1e">
                          <group.icon size={17} strokeWidth={1.8} className="shrink-0 text-app-subtle" data-id="element-12023" data-mp="3bhw1hl38y7az" />
                          <span className="truncate" data-id="element-12024" data-mp="36ot6r9mqgrt7">{item}</span>
                        </button>)}
                    </div>);
              })()}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {/* Top-right: what needs your attention, and the one human escape
         hatch. Both stay out of the composer's way. */}
        <div className="fixed right-0 top-0 z-30 flex items-center gap-1.5 p-3" data-id="element-12025" data-mp="2nr03u7c40hvt">
          {/* Once a thread exists it has weight and an audience — its size and
           who can see it belong here, not buried in a settings panel. */}
          {chatView === 'chat' && chatMessages.length > 0 && <span className="mr-1 hidden items-center gap-2.5 text-[12.5px] text-app-muted sm:flex" data-id="element-12026" data-mp="245zuhurkbu77">
              <span data-id="element-12027" data-mp="294kvhwen2hwp">
                {(0.01 + chatMessages.length * 0.006).toFixed(2)} MB
              </span>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors hover:bg-app-hover hover:text-app" data-id="element-12028" data-mp="2wtvrz1oyhq51">
                <Copy size={12} className="text-app-subtle" data-id="element-12029" data-mp="31vl12bsmbtqh" />
                Private
              </button>
            </span>}
          {/* Setup progress sits with the other status affordances rather than
           floating over the composer. A ring and a count — status, not promo. */}
          <button type="button" onClick={() => setChatChecklistOpen(o => !o)} aria-expanded={chatChecklistOpen} className={`relative flex items-center gap-2 rounded-full border py-1.5 pl-2.5 pr-3 transition-colors ${chatChecklistOpen ? 'border-app-strong bg-app-surface' : 'border-app bg-app-surface hover:border-app-strong hover:bg-app-hover'}`} data-id="element-12030" data-mp="3fryjbn4z7z60">
            {/* A single unresolved to-do. Red rather than brand orange, because
             its job is to be the one thing on screen you want to clear. */}
            {interviewIndex === null && <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#DC2626] px-1 text-[10.5px] font-bold leading-none text-white ring-2 ring-[var(--app-canvas)]" data-id="element-12031" data-mp="q6518p282smo">
                <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#DC2626] opacity-60" data-id="element-12032" data-mp="z739xknlvobl" />
                1
              </span>}
            <svg viewBox="0 0 20 20" className="h-[15px] w-[15px] -rotate-90" aria-hidden="true" data-id="element-12033" data-mp="u9uq7fmt7c88">
              <circle cx="10" cy="10" r="8" fill="none" stroke="var(--app-border)" strokeWidth="3" data-id="element-12034" data-mp="2ao32mhdv8565" />
              <circle cx="10" cy="10" r="8" fill="none" stroke="var(--app-text)" strokeWidth="3" strokeLinecap="round" strokeDasharray={2 * Math.PI * 8} strokeDashoffset={2 * Math.PI * 8 * (1 - setupProgress.ratio)} data-id="element-12035" data-mp="25oztolurlesw" />
            </svg>
            <span className="hidden text-[12.5px] font-medium text-app-muted sm:inline" data-id="element-12036" data-mp="2yp6dh7zqm2d8">
              Finish setup
            </span>
            <span className="text-[12.5px] text-app-subtle" data-id="element-12037" data-mp="33h7x5ez8ivda">{setupProgress.done}/{setupProgress.total}</span>
          </button>
          <div className="relative" data-id="element-12038" data-mp="38hltw0vdyiw0">
            <button type="button" onClick={() => setChatNotifOpen(o => !o)} aria-label="Notifications" aria-expanded={chatNotifOpen} className="relative flex h-9 w-9 items-center justify-center rounded-lg text-app-subtle transition-colors hover:bg-app-hover hover:text-app" data-id="element-12039" data-mp="3de2u3tfv0npg">
              <Bell size={17} data-id="element-12040" data-mp="1nznycp6fc0kg" />
              <span className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-app-accent ring-2 ring-[var(--app-canvas)]" data-id="element-12041" data-mp="1o0fvi4fo66cu" />
            </button>
            <AnimatePresence data-id="element-12042" data-mp="1s3c91gqcrc9x">
              {chatNotifOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setChatNotifOpen(false)} data-id="element-12043" data-mp="1kiie2ndif4z5" />
                  <motion.div initial={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute right-0 top-[calc(100%+6px)] z-40 w-[300px] origin-top-right overflow-hidden rounded-xl border border-app bg-app-surface shadow-[0_24px_60px_-24px_rgba(20,20,24,0.28)]" data-id="element-12044" data-mp="1fq8hwplsx8of">
                    <p className="border-b border-app-divider px-4 py-2.5 text-[12px] font-semibold text-app-muted" data-id="element-12045" data-mp="1pj61z06fz5p5">
                      Notifications
                    </p>
                    <ul className="divide-y divide-[var(--app-divider)]" data-id="element-12046" data-mp="e0d3eqde88nn">
                      {[{
                    title: 'Gmail, Drive and Calendar connected',
                    time: 'Just now'
                  }, {
                    title: 'Your $100 in credits is active',
                    time: 'Just now'
                  }].map(n => <li key={n.title} className="px-4 py-3" data-id="element-12047" data-mp="2wr5w05z3jbbe">
                          <p className="text-[13.5px] font-medium leading-snug text-app" data-id="element-12048" data-mp="2wt0gjj6gh1ey">
                            {n.title}
                          </p>
                          <p className="mt-0.5 text-[12px] text-app-subtle" data-id="element-12049" data-mp="3pfl5ye3df3fv">
                            {n.time}
                          </p>
                        </li>)}
                    </ul>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>
        </div>

        {/* Setup checklist, hung off the top-right control. Only the step you
         are on expands, so the panel stays short. */}
        <div className="fixed right-3 top-[52px] z-40 w-[300px]" data-id="element-12050" data-mp="240xb46d6ect4">
          <AnimatePresence initial={false} data-id="element-12051" data-mp="1tzctz3jdboet">
            {chatChecklistOpen && <motion.div key="checklist" initial={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="origin-top-right overflow-hidden rounded-xl border border-app bg-app-surface shadow-[0_28px_70px_-28px_rgba(20,20,24,0.32)]" data-id="element-12052" data-mp="1yxo31c1u9uvz">
                <div className="border-b border-app-divider px-4 py-3.5" data-id="element-12053" data-mp="2moa94xsa953c">
                  <div className="flex items-center justify-between gap-3" data-id="element-12054" data-mp="2rm23rzkbb5b8">
                    <span className="text-[14px] font-semibold text-app" data-id="element-12055" data-mp="2rjzc7b3ih44e">
                      Finish setup
                    </span>
                    <button type="button" onClick={() => setChatChecklistOpen(false)} aria-label="Close checklist" className="text-app-subtle transition-colors duration-200 ease-out hover:text-app" data-id="element-12056" data-mp="x7iyxqcaspq1">
                      <X size={15} data-id="element-12057" data-mp="k9bhinnbr71z" />
                    </button>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2.5" data-id="element-12058" data-mp="z6bxjtw5ldkm">
                    <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-app-subtle" data-id="element-12059" data-mp="z3g0e7s4qsx8">
                      <div className="h-full rounded-full bg-app-accent transition-[width] duration-300 ease-out" style={{
                    width: `${setupProgress.ratio * 100}%`
                  }} data-id="element-12060" data-mp="u92ceqfsro2l" />
                    </div>
                    <span className="text-[12px] text-app-subtle" data-id="element-12061" data-mp="1hwtnjqplpv2a">
                      {setupProgress.done} of {setupProgress.total}
                    </span>
                  </div>
                </div>

                <ul className="divide-y divide-[var(--app-divider)]" data-id="element-12062" data-mp="1qw6yyoxactpn">
                  {[{
                label: 'Create your Giga account',
                done: true
              }, {
                label: 'Onboard with Giga',
                done: interviewIndex === 'done',
                body: interviewIndex === 'done' ? 'Done — your tracks, pulses and memory files are running.' : 'Answer a few questions in the chat and Giga builds the rest for you.',
                cta: interviewIndex === null ? 'Start onboarding' : undefined,
                action: 'onboard'
              }, {
                label: 'Connect your tools',
                done: true,
                body: 'Gmail, Drive and Calendar are in. Add Notion, Slack or Linear so Giga can act across the rest of your stack.',
                cta: 'Connect other tools',
                view: 'integrations'
              }, {
                label: 'Ask your first question',
                done: chatMessages.length > 0,
                body: 'Try one of the prompts above — it answers from the tools you just connected.'
              }, {
                label: 'Set up a Pulse',
                done: false,
                body: 'Put a recurring job on a schedule, like a Monday pipeline recap.',
                cta: 'Create a Pulse',
                view: 'pulses'
              }, {
                label: 'Build your first Workflow',
                done: false,
                body: 'Chain a few steps together so one prompt runs the whole job.',
                cta: 'Build a Workflow',
                view: 'journeys'
              }].map(item => {
                const open = chatChecklistStep === item.label;
                return <li key={item.label} data-id="element-12063" data-mp="1ng6xr8uey8xb">
                        <button type="button" onClick={() => setChatChecklistStep(open ? null : item.label)} className="flex w-full items-center gap-2.5 px-4 py-3 text-left" data-id="element-12064" data-mp="1njclajne92qx">
                          {item.done ? <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-app-text" data-id="element-12065" data-mp="1eiekea895bck">
                              <Check size={11} strokeWidth={3.5} className="text-white" data-id="element-12066" data-mp="38ul4xzn1d4hj" />
                            </span> : <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-dashed border-app" data-id="element-12067" data-mp="3ge3uypup5xzm" />}
                          <span className={`flex-1 text-[13.5px] font-semibold ${item.done ? 'text-app-subtle line-through' : 'text-app'}`} data-id="element-12068" data-mp="3lcnhosh47dt1">
                            {item.label}
                          </span>
                          <ChevronDown size={14} className={`shrink-0 text-app-subtle transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-12069" data-mp="3v39ru762bc4c" />
                        </button>
                        <AnimatePresence initial={false} data-id="element-12070" data-mp="32dt1rg5x07ba">
                          {open && item.body && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: [0.23, 1, 0.32, 1]
                    }} className="overflow-hidden" data-id="element-12071" data-mp="37dfezplqtnk3">
                              <div className="px-4 pb-4 pl-[42px]" data-id="element-12072" data-mp="2nnl81gnv9fnd">
                                <p className="text-[12.5px] leading-relaxed text-app-muted" data-id="element-12073" data-mp="2ip9y75xkn555">
                                  {item.body}
                                </p>
                                {item.cta && <button type="button" onClick={() => (item as any).action === 'onboard' ? startOnboarding() : openChatView((item as any).view === 'journeys' ? 'workflows' : (item as any).view === 'integrations' ? 'connections' : (item as any).view)} className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-app-accent px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)]" data-id="element-12074" data-mp="2rqrlp9db693v">
                                    {item.cta}
                                    <ArrowRight size={13} data-id="element-12075" data-mp="1l9ml2jik8nvy" />
                                  </button>}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </li>;
              })}
                </ul>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* DEBUG: preview the picker with an unconnected workspace. */}
        <button type="button" onClick={() => setChatNoConnections(v => !v)} title="Debug: empty workspace" aria-label="Debug: empty workspace" aria-pressed={chatNoConnections} className={`fixed bottom-4 left-4 z-30 flex h-7 w-7 items-center justify-center rounded-full border transition-colors duration-200 ease-out ${chatNoConnections ? 'border-[var(--app-text)] bg-app-text text-white' : 'border-app bg-app-surface text-app-subtle hover:text-app'}`} data-id="element-12076" data-mp="3j0cn0oebblac">
          <Settings2 size={13} data-id="element-12077" data-mp="ai6oxxi4y1gr" />
        </button>

        {/* The sidebar itself — Tracks and Chats are the content; everything
         else is one quiet "More" disclosure at the bottom. */}
        <AnimatePresence data-id="element-12078" data-mp="2v8nmikrc7qib">
          {chatNavOpen && <>
              {/* Scrim only on narrow screens, where the pinned panel would
               cover the content. On desktop the layout makes room instead. */}
              <motion.div key="nav-scrim" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} onClick={() => setChatNavOpen(false)} className="fixed inset-0 z-40 bg-[#2B211A]/20 md:hidden" data-id="element-12079" data-mp="3hx5on6calzg8" />
              <motion.aside key="nav-panel" initial={{
            x: -260,
            opacity: 0.6
          }} animate={{
            x: 0,
            opacity: 1
          }} exit={{
            x: -260,
            opacity: 0.6
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="fixed left-0 top-0 z-50 flex h-full w-[260px] flex-col border-r border-app bg-app-surface" data-id="element-12080" data-mp="9jgbn8by7kyx">
                <div className="flex h-12 items-center justify-between gap-2 px-2" data-id="element-12081" data-mp="ngbz45sy5eyi">
                  <span className="flex min-w-0 items-center gap-2" data-id="element-12082" data-mp="vqiz2htw6e4g">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1F3A3D] text-[11px] font-bold tracking-tight text-white" data-id="element-12083" data-mp="vsdoa8c74sec">
                      BW
                    </span>
                    <span className="truncate text-[14px] font-semibold text-app" data-id="element-12084" data-mp="19piqfx8sh74b">
                      Brightwave
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center" data-id="element-12085" data-mp="1eon0sr7g9aw8">
                    <button type="button" onClick={() => setChatNavSearchOpen(true)} aria-label="Search chats and tracks" className="flex h-9 w-9 items-center justify-center rounded-lg text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12086" data-mp="1ipf99i8hzbrg">
                      <Search size={18} strokeWidth={1.8} data-id="element-12087" data-mp="1xfwj9pz779zy" />
                    </button>
                    <button type="button" onClick={() => setChatNavOpen(false)} aria-label="Close navigation" className="flex h-9 w-9 items-center justify-center rounded-lg text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12088" data-mp="22e7ua418bqjr">
                      <PanelLeft size={18} strokeWidth={1.8} data-id="element-12089" data-mp="39340w4bie3cp" />
                    </button>
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto px-2 pb-4" data-id="element-12090" data-mp="2z6rbvjqwsdoa">
                  <div className="mb-4" data-id="element-12091" data-mp="2z93pxltjq3w2">
                    <button type="button" onClick={() => {
                  openChatView('chat');
                  setChatMessages([]);
                }} className="flex h-10 w-full items-center gap-3 rounded-lg px-2.5 text-left text-[14px] font-medium text-app transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-12092" data-mp="3mwbsvx3i50ux">
                      <SquarePen size={18} strokeWidth={1.8} className="shrink-0 text-app" data-id="element-12093" data-mp="3i4kbod68k7hg" />
                      New chat
                    </button>
                  </div>

                  {/* The four surfaces people set up early and revisit often —
                   promoted out of the launcher so they're one click, not two. */}
                  <ul className="mb-4" data-id="element-12094" data-mp="1z4uhsc7bj1yo">
                    {[{
                  label: 'Agents',
                  icon: UserCircle,
                  view: 'assistants'
                }, {
                  label: 'Pulses',
                  icon: Activity,
                  view: 'pulses'
                }, {
                  label: 'Connections',
                  icon: Plug,
                  view: 'connections'
                }, {
                  label: 'Groups',
                  icon: Users,
                  view: 'groups'
                }].map(item => <li key={item.label} data-id="element-12095" data-mp="1q7ka10tpluyi">
                        <button type="button" onClick={() => openChatView(item.view as WorkspaceViewId)} className={`flex h-10 w-full items-center gap-3 rounded-lg px-2.5 text-left text-[14px] transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app ${chatView === item.view ? 'bg-app-subtle font-medium text-app' : 'font-medium text-app-muted'}`} data-id="element-12096" data-mp="1q465pn1hjbof">
                          <item.icon size={18} strokeWidth={1.8} className={`shrink-0 ${chatView === item.view ? 'text-app' : 'text-app-subtle'}`} data-id="element-12097" data-mp="33p9is81cd2sv" />
                          {item.label}
                        </button>
                      </li>)}
                    {/* The occasional surfaces continue the same list rather
                     than living in a popover that covered the sidebar. */}
                    {chatNavMoreOpen && [{
                  label: 'Workflows',
                  icon: Network,
                  view: 'workflows'
                }, {
                  label: 'Skills',
                  icon: GraduationCap,
                  view: 'skills'
                }, {
                  label: 'Power Ups',
                  icon: Zap,
                  view: 'power-ups'
                }, {
                  label: 'Files',
                  icon: FolderOpen,
                  view: 'files'
                }, {
                  label: 'Tables',
                  icon: Database,
                  view: 'tables'
                }, {
                  label: 'Browser',
                  icon: Globe,
                  view: 'browser'
                }, {
                  label: 'Viewer',
                  icon: Monitor,
                  view: 'viewer'
                }].map(item => <li key={item.label} data-id="element-12098" data-mp="achgegg6li1f">
                          <button type="button" onClick={() => setChatView(item.view as WorkspaceViewId)} className={`flex h-10 w-full items-center gap-3 rounded-lg px-2.5 text-left text-[14px] transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app ${chatView === item.view ? 'bg-app-subtle font-medium text-app' : 'font-medium text-app-muted'}`} data-id="element-12099" data-mp="5do58uoda34f">
                            <item.icon size={18} strokeWidth={1.8} className={`shrink-0 ${chatView === item.view ? 'text-app' : 'text-app-subtle'}`} data-id="element-12100" data-mp="3npt0051umeik" />
                            {item.label}
                          </button>
                        </li>)}
                    <li data-id="element-12101" data-mp="1hqbgfvyudsfw">
                      <button type="button" onClick={() => setChatNavMoreOpen(o => !o)} aria-expanded={chatNavMoreOpen} className="flex h-9 w-full items-center gap-1.5 rounded-lg px-2.5 text-left text-[13px] font-medium text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12102" data-mp="1hr3b94km7m54">
                        {chatNavMoreOpen ? 'View less' : 'View more'}
                        <ChevronDown size={13} className={`transition-transform duration-200 ${chatNavMoreOpen ? 'rotate-180' : ''}`} data-id="element-12103" data-mp="yz28vs8mtdqy" />
                      </button>
                    </li>
                  </ul>

                  {/* Six each, then a way to the full list — a sidebar that
                   scrolls for 18 rows stops being scannable. */}
                  <p className="px-2.5 pb-1.5 text-[12px] font-medium text-app-subtle" data-id="element-12104" data-mp="2jfxbyrkmhlkh">
                    Tracks
                  </p>
                  <ul className="mb-1" data-id="element-12105" data-mp="2odyu9aa85guw">
                    {visibleSidebarTracks.map(t => <li key={t.name} data-id="element-12106" data-mp="cretjf7exah4">
                        <button type="button" onClick={() => openTrack(t.name)} className={`flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13.5px] font-medium transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app ${chatTrack === t.name ? 'bg-app-subtle text-app' : 'text-app-muted'}`} data-id="element-12107" data-mp="csg4ljofhwyu">
                          <RouteIcon size={16} strokeWidth={1.8} className={`shrink-0 ${chatTrack === t.name ? 'text-app' : 'text-app-subtle'}`} data-id="element-12108" data-mp="hoy8yifujtan" />
                          <span className="truncate" data-id="element-12109" data-mp="rhcg5qpp1s47">{t.name}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('tracks')} className="mb-5 h-8 w-full rounded-lg px-2.5 text-left text-[12.5px] font-medium text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12110" data-mp="15e9ba4cuhvqw">
                    All tracks
                  </button>

                  <p className="px-2.5 pb-1.5 text-[12px] font-medium text-app-subtle" data-id="element-12111" data-mp="32aymmg4ycj1i">
                    Chats
                  </p>
                  <ul className="mb-1" data-id="element-12112" data-mp="1b3skcqazvosy">
                    {visibleSidebarChats.map(c => <li key={c} data-id="element-12113" data-mp="1f65q3epqrmpq">
                        <button type="button" onClick={() => openChatView('chats')} className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-[13.5px] font-medium text-app-muted transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12114" data-mp="1f549yvtvca15">
                          <span className="truncate" data-id="element-12115" data-mp="1u36hfh40tfno">{c}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('chats')} className="h-8 w-full rounded-lg px-2.5 text-left text-[12.5px] font-medium text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12116" data-mp="1yuoi0424te84">
                    All chats
                  </button>
                </div>

                {/* Account — usage, billing and settings belong to the person,
                 not the workspace, so they sit under the avatar rather than in
                 the feature launcher. */}
                <div className="relative border-t border-app p-3" data-id="element-12117" data-mp="2i0uklpyjt63d">
                  <button type="button" onClick={() => setChatAccountOpen(o => !o)} aria-expanded={chatAccountOpen} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-app-hover" data-id="element-12118" data-mp="2d0gggj02igam">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-app-accent text-[11px] font-bold text-white" data-id="element-12119" data-mp="35qgkkfmqvpcf">
                      SC
                    </span>
                    <span className="min-w-0 flex-1 text-left" data-id="element-12120" data-mp="2vwg6vpdedbs3">
                      <span className="block truncate text-[13px] font-semibold text-app" data-id="element-12121" data-mp="2rw63mkgzozgx">
                        Sarah Chen
                      </span>
                      <span className="block truncate text-[11.5px] text-app-subtle" data-id="element-12122" data-mp="3jo57dt0dh6k4">
                        $100 credit · 0 used
                      </span>
                    </span>
                    <ChevronDown size={14} className={`shrink-0 text-app-subtle transition-transform duration-200 ${chatAccountOpen ? 'rotate-180' : ''}`} data-id="element-12123" data-mp="74x4e5phhepe" />
                  </button>
                  <AnimatePresence data-id="element-12124" data-mp="3tfsy7sqzrhta">
                    {chatAccountOpen && <motion.ul initial={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1]
                }} className="absolute bottom-[calc(100%-4px)] left-3 right-3 z-10 origin-bottom rounded-xl border border-app bg-app-surface p-1.5 shadow-[0_24px_60px_-24px_rgba(20,20,24,0.24)]" data-id="element-12125" data-mp="26vnnrfj5rh3">
                        {[{
                    label: 'Usage & billing',
                    icon: CreditCard,
                    onSelect: () => {
                      setChatAccountOpen(false);
                      setChatBillingOpen(true);
                    }
                  }, {
                    label: 'Settings',
                    icon: Settings2,
                    // No dedicated settings view exists yet, so this lands on
                    // the dashboard root rather than a blank screen.
                    onSelect: () => navigate('/app-screen-mockups/dashboard')
                  }].map(item => <li key={item.label} data-id="element-12126" data-mp="zzd3v6g1bip8">
                            <button type="button" onClick={item.onSelect} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13.5px] font-medium text-app-muted transition-colors hover:bg-app-hover hover:text-app" data-id="element-12127" data-mp="1dtmh1wircj2w">
                              <item.icon size={15} className="shrink-0 text-app-subtle" data-id="element-12128" data-mp="l9xh01jmjxet" />
                              {item.label}
                            </button>
                          </li>)}
                      </motion.ul>}
                  </AnimatePresence>
                </div>
              </motion.aside>
            </>}
        </AnimatePresence>

        {/* Chat layout: greeting and starters occupy the empty thread area,
         composer parked at the bottom where it will stay once messages start
         filling the space above it. A selected track is a full workspace — it
         renders edge to edge here, not inside the dashboard's constrained
         content wrapper and not inside a rounded chatbox. */}
        {chatTrack ? <TrackWorkspace trackName={chatTrack} initialSection={screenInit.trackSection as TrackSection | undefined ?? 'chat'} data-id="element-12129" data-mp="1piq2n37kmsj5" /> : chatView !== 'chat' ? <div className="min-h-screen" data-id="element-12130" data-mp="1liz60ivd5i6p">
            <WorkspaceViews view={chatView} onOpenTrack={openTrack} data-id="element-12131" data-mp="30x3m7vvakltd" />
          </div> : <div className={`flex h-screen flex-col ${chatMessages.length === 0 ? 'bg-app-welcome' : 'bg-app-canvas'}`} data-id="element-12132" data-mp="2ww1mzf1dk9re">
          {chatMessages.length === 0 ? <div className="flex flex-1 items-center justify-center px-6 pt-20 md:pt-16" data-id="element-12133" data-mp="34ercvn4l1c0b">
              <div className="w-full max-w-3xl" data-id="element-12134" data-mp="39fospt24dtyn">
                <h1 className="text-center text-[26px] font-semibold tracking-[-0.02em] text-app md:text-[30px]" data-id="element-12135" data-mp="39car4n1a5os9">
                  {chatCreditNoticeSeen ? 'What can I help with?' : "You're all set, Sarah."}
                </h1>
                <p className="mx-auto mt-3 max-w-[38ch] text-center text-[15px] leading-relaxed text-app-muted" data-id="element-12136" data-mp="3n5ij4ft9wmah">
                  {chatCreditNoticeSeen ? 'Ask anything across your connected tools.' : <>
                      Ask anything across your connected tools. Your{' '}
                      <span className="font-semibold text-app" data-id="element-12137" data-mp="3ifbvai6l55ta">
                        $100 in credits
                      </span>{' '}
                      is ready — no card needed.
                    </>}
                </p>

                {/* Starters sit close to the composer and provide an immediate
                 path into the product; onboarding remains in Finish setup. */}
                <div className="mt-7 flex flex-wrap justify-center gap-2" data-id="element-12138" data-mp="ojux3u8ko16m">
                  {starters.map(s => <button key={s} type="button" onClick={() => sendChatMessage(s)} className="h-9 rounded-full border border-app bg-app-surface px-4 text-[13.5px] font-medium text-app-muted transition-colors duration-200 ease-out hover:border-app-strong hover:text-app" data-id="element-12139" data-mp="jlbb5ttzape9">
                      {s}
                    </button>)}
                </div>
              </div>
            </div> : <div ref={chatThreadRef} className="flex-1 overflow-y-auto px-6 pt-20 md:pt-16" data-id="element-12140" data-mp="v38tdy3s2z4">
              <div className="mx-auto flex max-w-3xl flex-col gap-7 pb-6" data-id="element-12141" data-mp="3s3h5c9n1bp2k">
                {chatMessages.map(m => m.role === 'user' ? <div key={m.id} className="flex justify-end" data-id="element-12142" data-mp="3nz9g8bco073t">
                      <p className="max-w-[80%] rounded-2xl rounded-br-md bg-app-text px-4 py-2.5 text-[15px] leading-relaxed text-white" data-id="element-12143" data-mp="3j0y5lyejpskj">
                        {m.text}
                      </p>
                    </div> : <motion.div key={m.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="min-w-0" data-id="element-12144" data-mp="1tkcddxakxw1o">
                      <div className="min-w-0" data-id="element-12145" data-mp="27isumi62j4y2">
                        {/* What it did, before what it found. */}
                        {m.steps && <ul className="mb-3 space-y-1" data-id="element-12146" data-mp="1fthcp8a8il96">
                            {m.steps.map(s => <li key={s.label} className="flex items-center gap-2 text-[12.5px] text-neutral-400" data-id="element-12147" data-mp="1frmn3go0q4yr">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain" data-id="element-12148" data-mp="1om13xno75njz" />
                                {s.label}
                                {m.thinking ? <Loader2 size={11} className="animate-spin text-neutral-300" data-id="element-12149" data-mp="w4os2nlf08he" /> : <Check size={11} strokeWidth={3} className="text-neutral-300" data-id="element-12150" data-mp="d0vc3s5pbf5j" />}
                              </li>)}
                          </ul>}
                        {m.thinking && <p className="text-[14.5px] text-neutral-400" data-id="element-12151" data-mp="7zxmwvm9xkuj">
                            Working…
                          </p>}
                        {m.answer && <div className="space-y-3" data-id="element-12152" data-mp="hz6lk1s33nn8">
                            {m.answer.map((para, i) => <p key={i} className="text-[15px] leading-relaxed text-[#2B211A]" dangerouslySetInnerHTML={{
                    __html: para.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                  }} data-id="element-12153" data-mp="vr38zsit59da" data-mpi={i} />)}
                          </div>}
                        {/* A Giga idea introduced at the moment it becomes
                         relevant, rather than in a tour up front. */}
                        {m.concept && <div className="mt-3.5 rounded-xl border border-[#F3DECB] bg-[#FFF9F5] px-4 py-3.5" data-id="element-12154" data-mp="37hp20tsym1ns">
                            <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-[#EA580C]" data-id="element-12155" data-mp="32k5f75i74gk8">
                              {m.concept.label}
                            </p>
                            <p className="mt-1.5 text-[14px] font-semibold text-[#2B211A]" data-id="element-12156" data-mp="1mybk673wh4vm">
                              {m.concept.title}
                            </p>
                            {m.concept.lines.map((line, i) => <p key={i} className="mt-1.5 text-[13px] leading-relaxed text-neutral-600" data-id="element-12157" data-mp="1d7gzv8a7sfct" data-mpi={i}>
                                {line}
                              </p>)}
                          </div>}

                        {/* What the interview produced, as artefacts rather
                         than a paragraph claiming it happened. */}
                        {m.created && <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-12158" data-mp="ubierbfkfhfa">
                            {m.created.map(c => <li key={c.title} className="rounded-xl border border-[#EFEAE1] bg-white px-3.5 py-3" data-id="element-12159" data-mp="2jqf577w0maq3">
                                <p className="text-[13px] font-semibold text-[#2B211A]" data-id="element-12160" data-mp="2onyjg7spqni1">
                                  {c.title}
                                </p>
                                <p className="mt-0.5 text-[12px] leading-relaxed text-neutral-500" data-id="element-12161" data-mp="2i7ynxxigreif">
                                  {c.detail}
                                </p>
                              </li>)}
                          </ul>}

                        {/* Suggested answers. They retire once answered so the
                         thread reads as a transcript, not a live form. */}
                        {m.choices && m.choices.length > 0 && !m.answered && <div className="mt-4" data-id="element-12162" data-mp="310srzwejtm7v">
                            <div className="flex flex-wrap gap-2" data-id="element-12163" data-mp="2w77vk8slt0as">
                              {m.choices.map(choice => {
                      const picked = interviewPicks.includes(choice);
                      return <button key={choice} type="button" onClick={() => m.multi ? setInterviewPicks(prev => picked ? prev.filter(p => p !== choice) : [...prev, choice]) : answerInterview(choice)} aria-pressed={m.multi ? picked : undefined} className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${picked ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#EFEAE1] bg-white text-neutral-600 hover:border-[#EA580C] hover:text-[#EA580C]'}`} data-id="element-12164" data-mp="3oottildb82ds">
                                    {m.multi && picked && <Check size={11} strokeWidth={3.5} className="mr-1.5 inline" data-id="element-12165" data-mp="a1mq0e975bsv" />}
                                    {choice}
                                  </button>;
                    })}
                            </div>
                            {m.multi && <button type="button" disabled={interviewPicks.length === 0} onClick={() => answerInterview(interviewPicks.join(', '))} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:bg-[#E7E0D4] disabled:text-neutral-400" data-id="element-12166" data-mp="iyx3m69hld0y">
                                {interviewPicks.length === 0 ? 'Pick what applies' : `Continue with ${interviewPicks.length}`}
                                <ArrowRight size={13} data-id="element-12167" data-mp="1nye6k5m7qqoi" />
                              </button>}
                          </div>}
                        {m.sources && <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-[#EFEAE1] pt-3" data-id="element-12168" data-mp="1x0ncei6jdvvk">
                            <span className="text-[11.5px] font-medium uppercase tracking-wider text-neutral-400" data-id="element-12169" data-mp="1s1kj1uzx5e3f">
                              Sources
                            </span>
                            {m.sources.map(s => <span key={s.label} className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[12px] font-medium text-neutral-600" data-id="element-12170" data-mp="10bo4x5ds0y4f">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3 w-3 object-contain" data-id="element-12171" data-mp="19b2wy99ldgr1" />
                                {s.label}
                              </span>)}
                          </div>}
                      </div>
                    </motion.div>)}
              </div>
            </div>}

          <div className={`sticky bottom-0 px-6 pb-6 pt-4 ${chatMessages.length === 0 ? 'bg-app-welcome' : 'bg-app-canvas'}`} data-id="element-12172" data-mp="2x190rkd4fprf">
            <div className="mx-auto max-w-3xl" data-id="element-12173" data-mp="2jadri6ypn6nb">
              <div className="rounded-2xl border border-app bg-app-surface p-4 shadow-[0_18px_50px_-28px_rgba(20,20,24,0.28)]" data-id="element-12174" data-mp="2j7hsefb4mbxa">
                <label htmlFor="giga-first-question" className="sr-only" data-id="element-12175" data-mp="3bxgh29les71f">
                  Ask Giga a question
                </label>
                <textarea id="giga-first-question" rows={1} value={chatDraft} onChange={e => setChatDraft(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage(chatDraft);
                }
              }} placeholder={typeof interviewIndex === 'number' ? ONBOARDING_INTERVIEW[interviewIndex].placeholder : 'Ask about anything in your connected tools…'} className="min-h-[28px] w-full resize-none bg-transparent text-[16px] leading-relaxed text-app outline-none placeholder:text-app-subtle" data-id="element-12176" data-mp="3fz1ubw1uunc5" />
                <div className="mt-2.5 flex items-center justify-between gap-4" data-id="element-12177" data-mp="28xu79lgrvpvk">
                  {/* Scoping the chat, not advertising the tool count. Giga
                   already uses everything the user can see; this is for
                   narrowing it to one group, track or database. */}
                  <span className="relative flex flex-wrap items-center gap-1.5" data-id="element-12178" data-mp="1lbxj051svl2l">
                    {/* One paperclip for both photos and files — the OS picker
                     already handles the distinction. */}
                    <button type="button" aria-label="Attach a photo or file" title="Attach a photo or file" className="flex h-7 w-7 items-center justify-center rounded-full text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12179" data-mp="1v6fmb93b1elv">
                      <Paperclip size={15} data-id="element-12180" data-mp="17h4uih3xmujv" />
                    </button>
                    {chatContext.map(c => <span key={c} className="inline-flex items-center gap-1 rounded-full bg-app-subtle py-1 pl-2.5 pr-1.5 text-[12.5px] font-medium text-app" data-id="element-12181" data-mp="3kro298kpbco0">
                        {c}
                        <button type="button" onClick={() => setChatContext(prev => prev.filter(v => v !== c))} aria-label={`Remove ${c}`} className="text-app-subtle transition-colors duration-200 ease-out hover:text-app" data-id="element-12182" data-mp="3br80q4niqfls">
                          <X size={12} data-id="element-12183" data-mp="31vekaa6oi1yq" />
                        </button>
                      </span>)}
                    <button type="button" onClick={() => {
                    setContextOpen(open => !open);
                    setChatModelOpen(false);
                  }} aria-expanded={contextOpen} aria-haspopup="dialog" className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12.5px] font-medium transition-colors duration-200 ease-out ${contextOpen ? 'bg-app-subtle text-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-12184" data-mp="2x23m0xfxmtif">
                      Context
                      {chatContext.length > 0 && <span className="text-[11px] tabular-nums text-app-subtle" data-id="element-12185" data-mp="24j9j3hp09qac">{chatContext.length}</span>}
                    </button>

                    <AnimatePresence data-id="element-12186" data-mp="1vh09qv5h42yj">
                      {contextOpen && <>
                          <div className="fixed inset-0 z-30" onClick={() => setContextOpen(false)} data-id="element-12187" data-mp="1zhk4xt6hkwi1" />
                          <motion.div initial={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} transition={{
                        duration: 0.18,
                        ease: [0.23, 1, 0.32, 1]
                      }} role="dialog" aria-label="Add context" className="absolute bottom-[calc(100%+8px)] left-0 z-40 w-[420px] max-w-[calc(100vw-32px)] origin-bottom-left overflow-hidden rounded-xl border border-app bg-app-surface shadow-[0_24px_60px_-24px_rgba(20,20,24,0.3)]" data-id="element-12188" data-mp="2n7o67tw7ck5v">
                            {/* Tabs only earn their place once there is more
                             than one kind of context to switch between. */}
                            {!chatNoConnections && <div className="flex gap-1 overflow-x-auto border-b border-app-divider px-2 py-2" data-id="element-12189" data-mp="gimbphg0wewe">
                                {(['Groups', 'Tracks', 'Credentials', 'Databases'] as const).map(t => <button key={t} type="button" onClick={() => setContextTab(t)} className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-colors duration-200 ease-out ${contextTab === t ? 'bg-app-subtle text-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-12190" data-mp="qh22tkf1vo5w">
                                      {t}
                                    </button>)}
                              </div>}
                            {!chatNoConnections && <div className="border-b border-app-divider p-2.5" data-id="element-12191" data-mp="6i4rmc6ui5cs">
                                <div className="relative" data-id="element-12192" data-mp="bg6c91jx6cqd">
                                  <Search size={13.5} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-app-subtle" data-id="element-12193" data-mp="fh8b3h9xcmrt" />
                                  <input type="search" value={contextQuery} onChange={e => setContextQuery(e.target.value)} placeholder="Search groups, tracks, and sources" aria-label="Search context" className="h-9 w-full rounded-lg border border-app bg-app-surface pl-8 pr-3 text-[13px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-12194" data-mp="khmkb2n9i2rf" />
                                </div>
                              </div>}
                            {/* Fixed height so switching tabs never resizes the
                             panel under the pointer. */}
                            <div className={`overflow-y-auto p-2 ${chatNoConnections ? '' : 'h-[280px]'}`} data-id="element-12195" data-mp="1d2mh4dhfavx4">
                              {chatNoConnections ? (
                          /* Nothing set up yet. Each tab explains its own
                          primitive and offers the one action that creates
                          it, so the empty state is never a dead end. */
                          <div className="px-2 py-4" data-id="element-12196" data-mp="199o41s5fxn47">
                                  <p className="text-[13.5px] font-semibold text-app" data-id="element-12197" data-mp="19b8y7gnuf41j">
                                    Nothing to draw on yet
                                  </p>
                                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-app-muted" data-id="element-12198" data-mp="2125b2h9wl8ot">
                                    Context is what Giga is allowed to look at
                                    when it answers — the accounts it can sign
                                    into, the tracks and groups your work lives
                                    in, the tables it can query. Connect a tool
                                    and it starts filling in here.
                                  </p>
                                  <button type="button" onClick={() => {
                              setContextOpen(false);
                              setStep(6);
                            }} className="mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-app-accent px-4 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)]" data-id="element-12199" data-mp="n0h8tpmh9at4">
                                    Connect your tools
                                    <ArrowRight size={14} data-id="element-12200" data-mp="4bkejlr2yiaw" />
                                  </button>
                                  <p className="mt-2 text-center text-[11.5px] text-app-subtle" data-id="element-12201" data-mp="3ljnzf19ml51a">
                                    Takes about a minute
                                  </p>
                                </div>) : (() => {
                            const sections = [{
                              label: 'Groups',
                              items: GROUP_NAMES
                            }, {
                              label: 'Tracks',
                              items: TRACKS.filter(t => t.status === 'active').slice(0, 8).map(t => t.name)
                            }, {
                              label: 'Credentials',
                              items: ['Gmail — sarah@brightwave.io', 'Google Drive', 'Google Calendar']
                            }, {
                              label: 'Databases',
                              items: ['Pipeline (Notion)', 'Support tickets (Airtable)']
                            }].filter(s => s.label === contextTab).map(s => ({
                              ...s,
                              items: s.items.filter(i => i.toLowerCase().includes(contextQuery.trim().toLowerCase()))
                            })).filter(s => s.items.length > 0);
                            if (sections.length === 0) {
                              return <p className="px-2 py-6 text-center text-[13px] text-app-subtle" data-id="element-12202" data-mp="22jycxld1f1sj">
                                      Nothing matches “{contextQuery}”.
                                    </p>;
                            }
                            return sections.map(s => <div key={s.label} className="mb-3 last:mb-0" data-id="element-12203" data-mp="1ln9dxxaaza2h">
                                      {s.items.map(item => {
                                const on = chatContext.includes(item);
                                return <button key={item} type="button" onClick={() => setChatContext(prev => on ? prev.filter(v => v !== item) : [...prev, item])} aria-pressed={on} className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-[13.5px] font-medium transition-colors duration-200 ease-out ${on ? 'bg-app-subtle text-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-12204" data-mp="xresqca2v4uo">
                                            <span className="truncate" data-id="element-12205" data-mp="2j5j6t6mczd4z">{item}</span>
                                            {on && <Check size={14} strokeWidth={3} className="shrink-0 text-app-accent" data-id="element-12206" data-mp="2o3b98ukmqhnf" />}
                                          </button>;
                              })}
                                    </div>);
                          })()}
                            </div>
                          </motion.div>
                        </>}
                    </AnimatePresence>
                  </span>

                  {/* Right-aligned, next to Send: this picks the model the
                   message is sent with, so it belongs beside the action it
                   affects rather than mixed in with context scoping. */}
                  <span className="flex shrink-0 items-center gap-1.5" data-id="element-12207" data-mp="3kuzn33ni91it">
                    <span className="relative" data-id="element-12208" data-mp="3fy99qo334k13">
                      <button type="button" onClick={() => {
                      setChatModelOpen(open => !open);
                      setContextOpen(false);
                    }} aria-expanded={chatModelOpen} aria-haspopup="listbox" className={`inline-flex h-8 items-center gap-1 rounded-lg px-2 text-[12.5px] font-medium transition-colors duration-200 ease-out ${chatModelOpen ? 'bg-app-subtle text-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-12209" data-mp="3fz109khtrrki">
                        {chatModel}
                        <ChevronDown size={12} className={`text-app-subtle transition-transform duration-200 ${chatModelOpen ? 'rotate-180' : ''}`} data-id="element-12210" data-mp="11bmgoniwocw7" />
                      </button>
                      <AnimatePresence data-id="element-12211" data-mp="168mlyw7xlbm9">
                        {chatModelOpen && <>
                            <div className="fixed inset-0 z-30" onClick={() => setChatModelOpen(false)} data-id="element-12212" data-mp="8orwzstwxhjs" />
                            <motion.div initial={{
                          opacity: 0,
                          y: 6,
                          scale: 0.98
                        }} animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1
                        }} exit={{
                          opacity: 0,
                          y: 6,
                          scale: 0.98
                        }} transition={{
                          duration: 0.18,
                          ease: [0.23, 1, 0.32, 1]
                        }} role="listbox" aria-label="Choose model" className="absolute bottom-[calc(100%+8px)] right-0 z-40 w-[220px] origin-bottom-right overflow-hidden rounded-xl border border-app bg-app-surface p-1.5 shadow-[0_24px_60px_-24px_rgba(20,20,24,0.3)]" data-id="element-12213" data-mp="djyxkrrvy64h">
                              {(['Auto', 'Giga Pro', 'Claude Sonnet', 'GPT-5'] as const).map(model => <button key={model} type="button" role="option" aria-selected={chatModel === model} onClick={() => {
                            setChatModel(model);
                            setChatModelOpen(false);
                          }} className="flex h-10 w-full items-center justify-between rounded-lg px-3 text-left text-[13.5px] font-medium text-app-muted transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-12214" data-mp="3ehsu7e6hziri">
                                  <span data-id="element-12215" data-mp="2vprllk3x8e4w">{model}</span>
                                  {chatModel === model && <Check size={14} strokeWidth={2.5} className="text-app" data-id="element-12216" data-mp="2qt9kqvbpozxu" />}
                                </button>)}
                            </motion.div>
                          </>}
                      </AnimatePresence>
                    </span>
                    <button type="button" onClick={() => sendChatMessage(chatDraft)} disabled={!chatDraft.trim()} aria-label="Send" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ease-out ${chatDraft.trim() ? 'bg-app-text text-white hover:bg-black' : 'bg-app-subtle text-app-subtle'}`} data-id="element-12217" data-mp="2csgqmbpbo6v9">
                      <ArrowUp size={17} strokeWidth={2.4} data-id="element-12218" data-mp="1t919hi3maq1c" />
                    </button>
                  </span>
                </div>
              </div>

              {/* Slack is optional, so it is one quiet line under the composer. */}
              <p className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-center text-[13px] text-neutral-400" data-id="element-12219" data-mp="2px7liq2vckpn">
                <SlackIcon size={14} data-id="element-12220" data-mp="2c4zu7ductlyc" />
                Your team already lives in Slack?
                <button type="button" onClick={() => {
                setAuthProvider('slack');
                setStep(2);
              }} className="font-semibold text-neutral-600 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-[#EA580C] hover:decoration-[#EA580C]" data-id="element-12221" data-mp="2h4ccrme706v4">
                  Add Slack
                </button>
              </p>
            </div>
          </div>
        </div>}

        {/* Same Usage & billing surface as the dashboard, opened on the
         starter-credit model this account is actually on. */}
        <BillingModal open={chatBillingOpen} onClose={() => setChatBillingOpen(false)} initialVariant="B" data-id="element-12222" data-mp="3srwbco0qdfwy" />
      </motion.div>;
  }
  // Step 20: Google consent. Modelled on Google's own account-chooser +
  // scope screen. The Workspace scopes are requested here, in the same
  // consent, so the user arrives with Gmail, Drive and Calendar already
  // readable instead of facing an empty brain.
  if (step === 20) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-12223" data-mp="350quq6myujll">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-12224" data-mp="kccktwrg2bll">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-12225" data-mp="2uwu08bv27p6w">
            <div className="mb-6 flex items-center justify-center" data-id="element-12226" data-mp="2zwq2a5k756jt">
              <GoogleIcon size={28} data-id="element-12227" data-mp="2zvey44mu9ws6" />
            </div>
            <h1 className="mb-1 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-12228" data-mp="33yth9462qt9u">
              Choose an account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-12229" data-mp="38wv6y7y09qua">
              to continue to <span className="font-semibold" data-id="element-12230" data-mp="3dv6h6js7q3d1">Giga</span>
            </p>

            <ul className="mb-6 divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200" data-id="element-12231" data-mp="3imxz65xaz0rk">
              {[{
              name: 'Sarah Chen',
              email: 'sarah.chen@brightwave.io',
              initial: 'S',
              tone: 'bg-[#EA580C]'
            }, {
              name: 'Sarah Chen',
              email: 'sarah@gmail.com',
              initial: 'S',
              tone: 'bg-[#3D2F25]'
            }].map(acct => <li key={acct.email} data-id="element-12232" data-mp="1kxj2k5an9j6e">
                  <button type="button" onClick={() => setStep(21)} className="group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[#FFF7F0]" data-id="element-12233" data-mp="1g01458u96mcn">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${acct.tone}`} data-id="element-12234" data-mp="16zuhoxgkuc1c">
                      {acct.initial}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-12235" data-mp="1zorzkzgvm6tk">
                      <span className="block truncate text-[15px] font-semibold text-[#333333]" data-id="element-12236" data-mp="1zp1r4rhkkm1d">
                        {acct.name}
                      </span>
                      <span className="block truncate text-[13px] text-neutral-500" data-id="element-12237" data-mp="1pzi7vkpy2q5z">
                        {acct.email}
                      </span>
                    </span>
                    <ChevronRight size={16} className="shrink-0 text-neutral-300 transition-colors group-hover:text-[#EA580C]" data-id="element-12238" data-mp="2indyq8su1nuu" />
                  </button>
                </li>)}
              <li data-id="element-12239" data-mp="31lplaen313ge">
                <button type="button" onClick={() => setStep(21)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-neutral-50" data-id="element-12240" data-mp="36iprcpjxm67i">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-400" data-id="element-12241" data-mp="2sr2lpz43nlc2">
                    <Plus size={16} data-id="element-12242" data-mp="2islqur21imkq" />
                  </span>
                  <span className="text-[15px] font-medium text-neutral-600" data-id="element-12243" data-mp="2iv7ysrsuf43h">
                    Use another account
                  </span>
                </button>
              </li>
            </ul>

            <p className="text-center text-[12.5px] leading-relaxed text-neutral-400" data-id="element-12244" data-mp="29ujexggrq9io">
              To continue, Google will share your name, email address and
              profile picture with Giga.
            </p>

            <button type="button" onClick={() => setStep(1)} className="mx-auto mt-6 block text-[13px] font-semibold text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-12245" data-mp="c2p2c88ybezh">
              Back
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 21: Google Workspace scope grant. Kept separate from the account
  // chooser so the permissions being granted are a deliberate read, not
  // something buried under an account row.
  if (step === 21) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-12246" data-mp="21hzog2w01ro2">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-12247" data-mp="2p8vd4r55s6r3">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-12248" data-mp="uv449tunlqwp">
            <div className="mb-5 flex items-center justify-center gap-4" data-id="element-12249" data-mp="uvm7jafqh5e4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-neutral-200 bg-white shadow-sm" data-id="element-12250" data-mp="pxkqsw5s5i5t">
                <GoogleIcon size={28} data-id="element-12251" data-mp="4yik4e4qnigc" />
              </div>
              <div className="flex gap-1" aria-hidden="true" data-id="element-12252" data-mp="915aealrgvbf">
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-12253" data-mp="dt6sid5m1g9d" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-12254" data-mp="duzxlo0krsf9" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-12255" data-mp="iri3wsbjzytp" />
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EA580C] shadow-sm" data-id="element-12256" data-mp="3i8pqkfuyq8uo">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12257" data-mp="3m9072mgdshns" />
              </div>
            </div>

            <h1 className="mb-2 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-12258" data-mp="16hca4r1dap4z">
              Giga wants access to your Google Account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-12259" data-mp="2vxod56s44ij7">
              sarah.chen@brightwave.io
            </p>

            <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-12260" data-mp="2r0er813xmp04">
              This will allow Giga to
            </p>
            <ul className="mb-6 space-y-2.5" data-id="element-12261" data-mp="2had5hfz1xiod">
              {[{
              label: 'Read your Gmail messages and threads',
              detail: 'So it can answer from your inbox and draft replies.'
            }, {
              label: 'See and open files in Google Drive',
              detail: 'So documents and spreadsheets become part of your brain.'
            }, {
              label: 'View and manage your Google Calendar',
              detail: 'So it can prep for meetings and schedule work.'
            }].map(scope => <li key={scope.label} className="flex gap-3 rounded-xl border border-neutral-200 p-3.5" data-id="element-12262" data-mp="1hmfb5z8tbmh">
                  <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#EA580C]" data-id="element-12263" data-mp="3sqa2lrh1n8wo" />
                  <span data-id="element-12264" data-mp="o8p81wiskc9g">
                    <span className="block text-[14.5px] font-semibold text-[#333333]" data-id="element-12265" data-mp="o870w4ulcdmr">
                      {scope.label}
                    </span>
                    <span className="mt-0.5 block text-[13px] leading-relaxed text-neutral-500" data-id="element-12266" data-mp="y032vqzssnw9">
                      {scope.detail}
                    </span>
                  </span>
                </li>)}
            </ul>

            <div className="flex flex-col gap-3 sm:flex-row-reverse" data-id="element-12267" data-mp="5bxij5cnp0wy">
              <button type="button" onClick={() => {
              // Consent granted here, so these arrive already connected.
              setConnectedConns(prev => Array.from(new Set([...prev, ...GOOGLE_WORKSPACE_TOOLS])));
              setStep(5);
            }} className="w-full rounded-xl bg-[#2B211A] px-6 py-3.5 font-bold text-white transition-colors hover:bg-black sm:w-auto" data-id="element-12268" data-mp="cksycuehfqo">
                Allow
              </button>
              <button type="button" onClick={() => setStep(20)} className="w-full rounded-xl border border-neutral-300 px-6 py-3.5 font-bold text-[#2B211A] transition-colors hover:bg-neutral-50 sm:w-auto" data-id="element-12269" data-mp="1low2bxfmpdvm">
                Cancel
              </button>
            </div>

            <p className="mt-5 text-[12.5px] leading-relaxed text-neutral-400" data-id="element-12270" data-mp="1k90dymkedn9k">
              You can revoke access at any time from your Google Account, or
              disconnect any tool from Giga's settings.
            </p>
          </div>
        </div>
      </motion.div>;
  }
  // Step 2: Slack Consent (Workspace Selection)
  if (step === 2) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-12271" data-mp="2h0ls7ws84whv">
        
        {/* Waitlist popup — opens immediately over the Slack connect screen
                                                                                  while the feature is invite-only. */}
        <AnimatePresence data-id="element-12272" data-mp="39imwyzy9cijf">
          {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => {
          setShowWaitlist(false);
          navigate('/');
        }} data-id="element-12273" data-mp="35kfh07bmbl9a" />}
        </AnimatePresence>

        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-12274" data-mp="2vkok5yzl4u61">
          <div className="flex-1 p-6 sm:p-10 overflow-auto" data-id="element-12275" data-mp="2lvn64eclomup">
            <h1 className="text-2xl font-title font-bold text-[#333333] text-center mb-6" data-id="element-12276" data-mp="2lu3mcdrqjto6">
              Sign in to Giga with Slack
            </h1>

            {/* Slack ⇄ Giga lockup */}
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-12277" data-mp="2cwjn1adfo7g7">
              <div className="w-14 h-14 rounded-2xl bg-[#2B211A] flex items-center justify-center shadow-sm" data-id="element-12278" data-mp="27ukivy9vd6i7">
                <SlackIcon size={28} data-id="element-12279" data-mp="22xu6bkx9wt1j" />
              </div>
              <ArrowLeftRight size={22} className="text-neutral-400" data-id="element-12280" data-mp="1ssuf5tonflso" />
              <div className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-12281" data-mp="1suoveuoz6pqf">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12282" data-mp="22qs5cmq2jsnx" />
                
              </div>
            </div>

            {/* Account / workspace selector */}
            <p className="text-sm font-bold text-[#2B211A] mb-2" data-id="element-12283" data-mp="1f1z5wga8n2ok">
              Select a Slack account or workspace
            </p>
            <button onClick={() => setStep(3)} className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-[#FFEDD5]/30 transition-all group mb-6 text-left" data-id="element-12284" data-mp="1jy7iblx1whs1">
              
              <div className="flex items-center gap-3 min-w-0" data-id="element-12285" data-mp="w9wj0n706q5v">
                <div className="w-11 h-11 rounded-lg bg-[#611f69] flex items-center justify-center text-white font-bold shrink-0" data-id="element-12286" data-mp="2xzvdjtm27fe8">
                  F
                </div>
                <div className="min-w-0" data-id="element-12287" data-mp="2t2lqgkm57ftk">
                  <div className="font-bold text-[#2B211A] text-[15px] truncate" data-id="element-12288" data-mp="37pf13ckpvxff">
                    Faith Alqahtani
                  </div>
                  <div className="text-xs text-neutral-500 truncate" data-id="element-12289" data-mp="32xdqdv376eri">
                    sarah.chen@brightwave.io · brightwave.slack.com
                  </div>
                </div>
              </div>
              <ChevronDown size={18} className="text-neutral-400 group-hover:text-[#EA580C] shrink-0" data-id="element-12290" data-mp="3co8bgw4pj8bd" />
              
            </button>

            {/* Account-details usage */}
            <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-5 mb-6" data-id="element-12291" data-mp="3lqhj9e8lcnl2">
              <p className="text-sm font-bold text-[#2B211A] mb-4" data-id="element-12292" data-mp="1wbkwbrrcocf0">
                Your account details will be used to:
              </p>
              <ul className="space-y-4" data-id="element-12293" data-mp="1rdjf7cdhin66">
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-12294" data-mp="6susemj8uclb">
                  <Plug size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-12295" data-mp="bshd1h1eiv45" />
                  <span data-id="element-12296" data-mp="buk6yc5od8e5">
                    Establish a verified connection between an existing Giga
                    account and Slack.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-12297" data-mp="20uybapuudwn">
                  <UserPlus size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-12298" data-mp="3k7hjakxkeyyb" />
                  
                  <span data-id="element-12299" data-mp="3p8ylqkvp6th0">
                    Help you create a new Giga account, if you don't already
                    have one.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-12300" data-mp="3f8ejbewfzix7">
                  <Sparkles size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-12301" data-mp="18i8anp4zoye6" />
                  
                  <span data-id="element-12302" data-mp="1dicqdcy2pp3c">
                    Unlock Giga features in Slack, like configurable
                    notifications, rich link previews, and quick actions.
                  </span>
                </li>
              </ul>
            </div>

            {/* Privacy disclosure */}
            <p className="text-xs text-neutral-500 leading-relaxed mb-0 sm:mb-6" data-id="element-12303" data-mp="12281wd5qbswm">
              Slack will share your name, email address, profile image, user ID,
              and team ID with Giga. View Slack's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-12304" data-mp="1urf2dhoquski">
                privacy policy
              </a>
              . By agreeing to allow Slack to share your identity, you are also
              agreeing to Giga's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-12305" data-mp="1kzj2q270eue6">
                privacy agreement
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-12306" data-mp="2di6ir375fj8e">
                terms of service
              </a>
              .
            </p>
          </div>

          <div className="p-6 border-t border-neutral-100 sm:border-none sm:p-0 sm:pb-10 sm:px-10 flex flex-col-reverse sm:flex-row items-center sm:justify-center gap-3 bg-white sticky bottom-0 z-10" data-id="element-12307" data-mp="28p3muoewfxp1">
            <button onClick={() => setStep(1)} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl border border-neutral-300 text-[#2B211A] font-bold hover:bg-neutral-50 transition-colors" data-id="element-12308" data-mp="9821rdhk1xd">
              
              Cancel
            </button>
            <button onClick={() => {
            trackSignupSlackConnected();
            setStep(3);
          }} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-sm" data-id="element-12309" data-mp="e6d5djltemox">

              Accept and Continue
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 3: Slack Permissions
  if (step === 3) {
    const viewItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-12310" data-mp="wvt7gbd5en7g" />,
      label: 'Content and info about channels & conversations'
    }, {
      icon: <LayoutGrid size={18} className="text-neutral-500" data-id="element-12311" data-mp="cssb5443pg6n" />,
      label: 'Content and info about your workspace'
    }];
    const actionItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-12312" data-mp="7wuvl00nwdux" />,
      label: 'Perform actions in channels & conversations'
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12313" data-mp="hser3lbbdaj5">
        
        {/* Thin top bar */}
        <div className="h-2 w-full bg-[#2B211A]" data-id="element-12314" data-mp="3lag8al2o7xlz" />

        {/* Slack header */}
        <header className="w-full border-b border-neutral-200 px-6 sm:px-10 py-5" data-id="element-12315" data-mp="2vnpd5e63mqd">
          <div className="flex items-center gap-2" data-id="element-12316" data-mp="3uabkmt7ypynl">
            <SlackIcon size={28} data-id="element-12317" data-mp="3u8s4d2mb3nlt" />
            <span className="text-[22px] font-bold text-[#333333] tracking-tight" data-id="element-12318" data-mp="36kh8yezdqg4x">
              slack
            </span>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 w-full max-w-5xl mx-auto px-6 sm:px-10 py-8 sm:py-16 pb-32 sm:pb-16" data-id="element-12319" data-mp="32gt25yiknokq">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-12320" data-mp="2scvv9wcrllit">
            {/* Left column */}
            <div data-id="element-12321" data-mp="2x7i9066bjq62">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm mb-5" data-id="element-12322" data-mp="2ih176lpruy8n">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 sm:w-8 sm:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12323" data-mp="2ngx7oaz9g7jg" />
                
              </div>

              <h1 className="text-2xl sm:text-[32px] leading-tight font-title font-bold text-[#333333] mb-4" data-id="element-12324" data-mp="316h7b7xjoooj">
                Allow the "Giga" app to access Slack
              </h1>

              <div className="flex items-center gap-2 text-[#2B211A] font-bold text-sm sm:text-[15px] mb-2" data-id="element-12325" data-mp="1pru6ph6rbykr">
                <CheckCircle size={18} className="text-[#2B211A]" data-id="element-12326" data-mp="1us9hq23kgtzk" />
                App is approved by Slack
              </div>
              <p className="text-sm sm:text-[15px] text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-12327" data-mp="un9qoic9muz7">
                Apps are reviewed for quality before they are listed in the
                Slack Marketplace.{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-12328" data-mp="lo4tpggcr9p5">
                  Learn more about app reviews.
                </a>
              </p>

              <p className="text-sm sm:text-[15px] font-bold text-[#333333] mb-2" data-id="element-12329" data-mp="bv78h2jz76mu">
                Workspace
              </p>
              <button type="button" className="w-full max-w-md flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg border border-neutral-300 bg-white hover:border-neutral-400 transition-colors text-left" data-id="element-12330" data-mp="gos972cyeepq">
                
                <div className="flex items-center gap-3 min-w-0" data-id="element-12331" data-mp="3p2sk858erc0d">
                  <div className="w-7 h-7 rounded-md bg-[#4A90D9] flex items-center justify-center text-white text-[12px] font-bold shrink-0" data-id="element-12332" data-mp="3p4loxezgnm5q">
                    T
                  </div>
                  <span className="font-bold text-[#333333] text-[16px] truncate" data-id="element-12333" data-mp="3ag24vzxunivs">
                    Tonic
                  </span>
                </div>
                <ChevronDown size={18} className="text-neutral-500 shrink-0" data-id="element-12334" data-mp="3ke9l2gkbdwwq" />
              </button>
            </div>

            {/* Right column */}
            <div data-id="element-12335" data-mp="3fgzmobey6awr">
              <h2 className="text-lg sm:text-[20px] font-bold text-[#333333] mb-4 sm:mb-5" data-id="element-12336" data-mp="3teo8pmkr3o3e">
                Review app permissions
              </h2>

              <div className="rounded-2xl bg-[#F8F8F8] border border-neutral-200/70 p-5 sm:p-6 space-y-6 sm:space-y-7" data-id="element-12337" data-mp="3oetill8sv2je">
                <div data-id="element-12338" data-mp="6ybjrzi1l4jn">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-12339" data-mp="200c9vf7z64l">
                    Information "Giga" can view
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-12340" data-mp="g0t7kidci58r">
                    {viewItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-12341" data-mp="bz7ouge4qwqn" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-12342" data-mp="j37hig3giklw">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-12343" data-mp="o1ircqtr4v44">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-12344" data-mp="xq0o8wywui2h" />
                      
                      </div>)}
                  </div>
                </div>

                <div data-id="element-12345" data-mp="11u8bsqtmtrz4">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-12346" data-mp="16qfmewwz3rne">
                    Actions "Giga" can take
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-12347" data-mp="1bql6ns0wybvg">
                    {actionItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-12348" data-mp="1gnto1qe8mbvn" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-12349" data-mp="3djhobxpbwcoj">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-12350" data-mp="3ihaz741ktuv5">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-12351" data-mp="1uuewdf6fijki" />
                      
                      </div>)}
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-[13px] text-neutral-500 leading-relaxed mt-5" data-id="element-12352" data-mp="1lurvcuhm3frz">
                Slack will share your granted permissions with Giga. View
                Slack's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-12353" data-mp="2eie8ougn5ys1">
                  privacy policy
                </a>
                . By agreeing to allow Slack to share your granted permissions,
                you are also agreeing to Giga's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-12354" data-mp="24mkqovk5ld2v">
                  privacy agreement
                </a>{' '}
                and{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-12355" data-mp="t49w64k0dyju">
                  terms of service
                </a>
                . Please review Giga's terms before installing.
              </p>

              <div className="fixed sm:static bottom-0 inset-x-0 p-4 sm:p-0 bg-white sm:bg-transparent border-t border-neutral-200 sm:border-none flex flex-col-reverse sm:flex-row items-center sm:justify-end gap-3 mt-0 sm:mt-6 z-10" data-id="element-12356" data-mp="2ltitsw1ctb6k">
                <button onClick={() => setStep(2)} className="w-full sm:w-auto px-6 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg border border-neutral-300 text-[#333333] text-[15px] font-bold hover:bg-neutral-50 transition-colors" data-id="element-12357" data-mp="2cy1g80h68vxi">
                  
                  Cancel
                </button>
                <button onClick={() => setStep(4)} className="w-full sm:w-auto px-7 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg bg-[#007A5A] hover:bg-[#148567] text-white text-[15px] font-bold transition-colors shadow-sm" data-id="element-12358" data-mp="2qu6kthpl1s0s">
                  
                  Allow
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 4: Channel Selection
  if (step === 4) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12359" data-mp="17tnup9pc1fe4">
        
        <GigaHeader data-id="element-12360" data-mp="1gu2jij1i9cmd" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-12361" data-mp="1guvrtog44a6m">
          <div className="w-full max-w-md" data-id="element-12362" data-mp="t3ynoxuk6h5x">
            <div className="mb-7 sm:mb-10" data-id="element-12363" data-mp="t4rmncna02di">
              <div className="mb-2.5" data-id="element-12364" data-mp="p2wogh0n653y">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12365" data-mp="p3xzwmlkktm7">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12366" data-mp="y32y1rt7ykxu">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-12367" data-mp="ac5vv6r871zs">
                  <SlackIcon size={24} data-id="element-12368" data-mp="5dmb39odbw90" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-12369" data-mp="25gn65e79ye3z">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-12370" data-mp="2f819jx2cjy2e">
              <button onClick={() => setStep(5)} className="w-full py-3.5 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors" data-id="element-12371" data-mp="2k5tcdm4j54ld">
                
                Invite Giga to public channels only
              </button>
              <button onClick={() => setStep(12)} className="w-full py-3.5 px-6 rounded-xl border-2 border-neutral-200 text-[#333333] font-medium hover:border-[#EA580C] hover:bg-[#FAF8F4]/30 transition-all" data-id="element-12372" data-mp="1rhxam303smhq">
                
                Select channels
              </button>
            </div>

            <div className="flex items-center gap-2.5 text-[13px] text-neutral-500 bg-neutral-50 rounded-xl px-4 py-3" data-id="element-12373" data-mp="37um1ozqnce70">
              <Info size={16} className="shrink-0" data-id="element-12374" data-mp="3bv6169yoztwb" />
              <p className="[text-wrap:balance]" data-id="element-12375" data-mp="109vn87xa999v">
                You can remove Giga from any channel anytime.
              </p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 12: Specific Channel Selection
  if (step === 12) {
    const DUMMY_CHANNELS = ['all-faith-test', 'new-channel', 'social'];
    // Auto-select all by default to match the screenshot's "3 selected" state
    // if the user hasn't interacted yet.
    const activeChannels = selectedChannels.length > 0 || !selectedChannels.includes('initialized') ? selectedChannels.includes('initialized') ? selectedChannels.filter(c => c !== 'initialized') : DUMMY_CHANNELS : [];
    const toggleChannel = (ch: string) => {
      let next = [...activeChannels];
      if (next.includes(ch)) {
        next = next.filter(c => c !== ch);
      } else {
        next.push(ch);
      }
      // Add a hidden flag so we know the user has interacted and we shouldn't auto-fill
      if (!next.includes('initialized')) next.push('initialized');
      setSelectedChannels(next);
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12376" data-mp="1br4wb2tirkqd">
        
        <GigaHeader data-id="element-12377" data-mp="x0e4hurxgln9" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-12378" data-mp="120s4cpjdkpa7">
          <div className="w-full max-w-md" data-id="element-12379" data-mp="o1k37r3nj73d">
            <div className="mb-7 sm:mb-10" data-id="element-12380" data-mp="o0hb5fuleylv">
              <div className="mb-2.5" data-id="element-12381" data-mp="o3wuwvz7ovtm">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12382" data-mp="s3dzln6tdoxq">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12383" data-mp="j27gr7gp021b">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-12384" data-mp="28ifc572bq94t">
                  <SlackIcon size={24} data-id="element-12385" data-mp="2dh0at21xgkr5" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-12386" data-mp="1twtpup45y1g">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden mb-4" data-id="element-12387" data-mp="bj6ojibcdut9">
              {/* Search */}
              <div className="relative border-b border-neutral-200" data-id="element-12388" data-mp="6qww9vmr8int">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-12389" data-mp="kil6tuhcuz2o" />
                
                <input type="text" placeholder={`Search ${WORKSPACE_NAME} workspace`} className="w-full pl-11 pr-4 py-4 text-[15px] text-[#2B211A] placeholder:text-neutral-400 focus:outline-none bg-transparent" data-id="element-12390" data-mp="fka0vuu6l8pq" />
                
              </div>

              {/* List */}
              <div className="bg-[#FAF8F4]/50 p-2" data-id="element-12391" data-mp="2w9yotnqlmquh">
                <div className="px-3 py-2 text-sm font-medium text-[#2B211A]" data-id="element-12392" data-mp="2ra2llqpq73fz">
                  {activeChannels.length} selected
                </div>
                <div className="flex flex-col gap-1 pb-2" data-id="element-12393" data-mp="psujtklri5w8">
                  {DUMMY_CHANNELS.map(ch => {
                  const isSelected = activeChannels.includes(ch);
                  return <button key={ch} onClick={() => toggleChannel(ch)} className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-white transition-colors group" data-id="element-12394" data-mp="g3krq4qx4n84">
                        
                        <div className="flex items-center gap-3 text-[15px] text-[#2B211A]" data-id="element-12395" data-mp="74h36l8zffm8">
                          <Hash size={16} className="text-neutral-400" data-id="element-12396" data-mp="23jrmrih9jtp" />
                          {ch}
                        </div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C] text-white' : 'border-neutral-300 text-transparent group-hover:border-neutral-400'}`} data-id="element-12397" data-mp="3tcyuxfhe7692">
                          
                          <Check size={12} strokeWidth={3} data-id="element-12398" data-mp="1nifmvrnlznar" />
                        </div>
                      </button>;
                })}
                </div>
              </div>
            </div>

            <button onClick={() => setStep(5)} disabled={activeChannels.length === 0} className="w-full py-4 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4 shadow-sm" data-id="element-12399" data-mp="3ad2qoklxg272">
              
              Invite Giga to {activeChannels.length} channels
            </button>

            <div className="flex items-center justify-center gap-2 text-[13px] text-neutral-500" data-id="element-12400" data-mp="2rf9ndh7e5q95">
              <Info size={14} data-id="element-12401" data-mp="2vgv81oq638tw" />
              <p data-id="element-12402" data-mp="2vglgvxtdyvmm">You can remove Giga from any channel anytime</p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 5: Tell us about you
  if (step === 5) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12403" data-mp="aq478c6wtg4p">
        
        <GigaHeader data-id="element-12404" data-mp="2bslvn5elujv1" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-12405" data-mp="2bs29fchx99b7">
          <div className="w-full max-w-md" data-id="element-12406" data-mp="27sjq2l5r16ah">
            <div className="mb-7 sm:mb-10" data-id="element-12407" data-mp="27tuv0oaxkk36">
              <div className="mb-2.5" data-id="element-12408" data-mp="2ls2vh676e3j3">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12409" data-mp="2lr1d0gntyerc">
                  STEP 2 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12410" data-mp="1t3x9yh8asai7">
                Tell us more about you
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-12411" data-mp="22sf8ert3u5io">
                A couple quick questions so Giga can hit the ground running.
              </p>
            </div>

            <div className="space-y-6 mb-8" data-id="element-12412" data-mp="1ace84dq0f2hr">
              <div data-id="element-12413" data-mp="1ab36okk1e6tt">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-12414" data-mp="1aajrhblrtwjh">
                  What best describes you?
                </label>
                <SearchableSelect value={role} onChange={setRole} searchable={false} options={['I am a founder or executive and wear many hats at my company', 'I am a real estate agent or real estate broker', 'I am a consultant or work with many different clients', 'I work in sales or rev ops', 'I work on a marketing or growth team', 'I work in finance or operations', 'I am a content creator or social media influencer', 'I am a product manager or work with an engineering team', 'Other']} placeholder="Select an option" ariaLabel="What best describes you?" data-id="element-12415" data-mp="3h04vcgdjbhvd" />
                
              </div>

              <div data-id="element-12416" data-mp="3qqzlvwxtd3mm">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-12417" data-mp="3qpofdpczhhrt">
                  What kind of business is it?
                </label>
                <SearchableSelect value={businessType} onChange={setBusinessType} options={BUSINESS_TYPES} placeholder="Select an option" searchPlaceholder="Search business types…" ariaLabel="What kind of business is it?" data-id="element-12418" data-mp="2fbawz0101uj6" />
                
              </div>

              <div data-id="element-12419" data-mp="2zllsfvpeu0ah">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-12420" data-mp="2zm3vbb6kvcrd">
                  What would Giga help you with? Select from use cases below:
                </label>
                <MultiSelectDropdown values={firstTeammates} onChange={toggleFirstTeammate} options={['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other']} placeholder="Select use cases" data-id="element-12421" data-mp="2puhft2n7irit" />
                
              </div>

              <div data-id="element-12422" data-mp="34jncojyko5nj">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-12423" data-mp="34i2k2zvtiwsb">
                  How did you find us?{' '}
                  <span className="text-neutral-400 font-normal" data-id="element-12424" data-mp="1y4czbf2kiomv">
                    (optional)
                  </span>
                </label>
                <div className="relative" data-id="element-12425" data-mp="8mg25atf7ebb">
                  <select value={foundUs} onChange={e => setFoundUs(e.target.value)} className={`w-full px-4 py-3 rounded-xl border border-neutral-200 appearance-none bg-white focus:outline-none focus:border-[#EA580C] transition-colors ${foundUs ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-12426" data-mp="8nhb99qvlqqe">
                    
                    <option value="" disabled data-id="element-12427" data-mp="mffgh9hgv2ha">
                      Select an option
                    </option>
                    <option data-id="element-12428" data-mp="rerztk94prf4">Google search</option>
                    <option data-id="element-12429" data-mp="wafolyjp8ik3">Slack Marketplace</option>
                    <option data-id="element-12430" data-mp="11alnnzsat7c7">Community</option>
                    <option data-id="element-12431" data-mp="11bwrfzlquf3b">Podcast</option>
                    <option data-id="element-12432" data-mp="118r2qlh11f84">Reddit</option>
                    <option data-id="element-12433" data-mp="15cgl2t73z0kp">Product Hunt</option>
                    <option data-id="element-12434" data-mp="15a2r6no5zueq">Facebook</option>
                    <option data-id="element-12435" data-mp="332qcv3iexqj8">YouTube</option>
                    <option data-id="element-12436" data-mp="3341f2yw7mq88">Twitter / X</option>
                    <option data-id="element-12437" data-mp="3730j8ha4aiym">Newsletter</option>
                    <option data-id="element-12438" data-mp="373tqrkgwhcht">Other</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" data-id="element-12439" data-mp="ea4m2ycjxcx5" />
                  
                </div>
              </div>

              <div data-id="element-12440" data-mp="nat19wdoljbm">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-12441" data-mp="nbb79kq5bbwq">
                  Keep me posted with Giga tips and updates
                </label>
                <div className="flex items-center gap-4" data-id="element-12442" data-mp="x75tsqxhpr4s">
                  <button onClick={() => setEmailOptIn(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-12443" data-mp="x5kyv079k66e">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-12444" data-mp="11y4f64xc5few">
                      
                      {emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-12445" data-mp="1b0x4w3qvdj2m"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-12446" data-mp="1ulnlikyzllhu">Of course!</span>
                  </button>
                  <button onClick={() => setEmailOptIn(false)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${!emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-12447" data-mp="1zjp4l5wexktb">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${!emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-12448" data-mp="1bumpo5t5y62i">
                      
                      {!emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-12449" data-mp="11x8asamw1pcb"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-12450" data-mp="16wl5tinwy2pz">No thanks</span>
                  </button>
                </div>
              </div>
            </div>

            <button onClick={() => {
            trackSignupProfileCompleted(role, businessType);
            setStep(6);
          }} disabled={!role || !businessType || firstTeammates.length === 0} className={`w-full py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${role && businessType && firstTeammates.length > 0 ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-300 text-white cursor-not-allowed'}`} data-id="element-12451" data-mp="sy4qj3bzj3w8">
              
              Continue
              <ArrowRight size={18} data-id="element-12452" data-mp="zfg4xskyt7dv" />
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 6: Tool Selection
  if (step === 6) {
    const CATEGORIES: {
      key: string;
      label: string;
    }[] = [{
      key: 'communication',
      label: 'COMMUNICATION'
    }, {
      key: 'docs',
      label: 'DOCS & DRIVE'
    }, {
      key: 'engineering',
      label: 'ENGINEERING'
    }, {
      key: 'project',
      label: 'PROJECT MANAGEMENT'
    }, {
      key: 'crm',
      label: 'CRM & SALES'
    }, {
      key: 'support',
      label: 'SUPPORT'
    }, {
      key: 'finance',
      label: 'FINANCE'
    }, {
      key: 'design',
      label: 'DESIGN'
    }, {
      key: 'analytics',
      label: 'ANALYTICS'
    }, {
      key: 'marketing',
      label: 'MARKETING'
    }];
    const filteredTools = ALL_TOOLS.filter(t => t.name.toLowerCase().includes(toolSearch.toLowerCase()));
    const POPULAR_TOOL_IDS = ['slack', 'gmail', 'googledrive', 'notion', 'github', 'linear', 'jira', 'salesforce', 'hubspot', 'stripe'];
    const popularTools = POPULAR_TOOL_IDS.map(id => ALL_TOOLS.find(t => t.id === id)).filter(Boolean) as typeof ALL_TOOLS;
    const isSearching = toolSearch.trim() !== '';
    const renderToolCard = (tool: (typeof ALL_TOOLS)[number]) => {
      const isSelected = selectedTools.includes(tool.id);
      return <button key={tool.id} onClick={() => toggleTool(tool.id)} aria-pressed={isSelected} className={`group relative p-3 rounded-2xl border transition-all duration-200 ease-out active:scale-[0.97] ${isSelected ? 'border-[#F8C9A4] bg-[#FFF7ED] shadow-[0_1px_3px_rgba(234,88,12,0.08)]' : 'border-neutral-200/80 bg-white hover:border-[#F3D2BB] hover:bg-[#FFFBF7]'}`} data-id="element-12453" data-mp="2ddgnecsqzx81">
          
          <AnimatePresence data-id="element-12454" data-mp="5vnlxi9gc6tp">
            {isSelected && <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.5
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} className="absolute top-1.5 right-1.5 w-[18px] h-[18px] bg-[#EA580C] rounded-full flex items-center justify-center shadow-[0_1px_2px_rgba(234,88,12,0.3)]" data-id="element-12455" data-mp="5ucgle0cyr0h">
              
                <Check size={11} strokeWidth={2.25} className="text-white" data-id="element-12456" data-mp="tk6ttwolahl9" />
              </motion.div>}
          </AnimatePresence>
          <ToolLogo name={tool.name} icon={tool.icon} className="w-8 h-8 mx-auto mb-1.5 transition-transform duration-200 group-hover:scale-105" fallbackTextSize="text-sm" data-id="element-12457" data-mp="2vdw7q9ec7pi8" />
          
          <div className="text-xs font-medium text-[#333333] text-center leading-tight" data-id="element-12458" data-mp="2ssojd6mti07a">
            {tool.name}
          </div>
        </button>;
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12459" data-mp="2o0n7vmxh4dib">
        
        <GigaHeader hideOnMobile data-id="element-12460" data-mp="2e5daxdlxmwbe" />

        <div className="flex-1 px-6 pt-6 lg:pt-12 pb-36 overflow-auto" data-id="element-12461" data-mp="2e27j3qlx5gbz">
          <div className="max-w-4xl mx-auto" data-id="element-12462" data-mp="3br15dsytv4ka">
            <div className="mb-2.5" data-id="element-12463" data-mp="36szdqjnc5wx7">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12464" data-mp="36u0xrdmbxtr2">
                STEP 3 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12465" data-mp="2wv1r20l4k86p">
              Where does your work actually live?
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-12466" data-mp="1qc3i7r047uoo">
              GitHub, Linear, the docs you reference — pick everything. Giga
              reads across them with citations back to source.
            </p>

            <div className="relative mb-6 sm:mb-8" data-id="element-12467" data-mp="hg7zx5izo3qj">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} data-id="element-12468" data-mp="3hqexfkgx5cb" />
              
              <input type="text" placeholder="Search tools..." value={toolSearch} onChange={e => setToolSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 sm:py-3.5 rounded-xl border border-neutral-200 text-[#333333] bg-white focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15 transition-all" data-id="element-12469" data-mp="8gbfjg3mtr1a" />
              
            </div>

            <div className="relative inline-block mb-7 sm:mb-10" data-id="element-12470" data-mp="3gujuhld8ejaz">
              <button onClick={() => setRoleMenuOpen(!roleMenuOpen)} aria-expanded={roleMenuOpen} aria-label={`Suggested for ${funcRoles.join(', ')}. Change use cases`} className="group inline-flex items-center gap-2 pl-3.5 pr-3 py-2 rounded-full border border-neutral-200 bg-white text-sm shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-12471" data-mp="3lwix2t15dc6v">
                
                <Sparkles size={14} className="text-neutral-400 shrink-0" data-id="element-12472" data-mp="2obr4a07befwv" />
                <span className="text-neutral-500" data-id="element-12473" data-mp="32az3uu7e3q1l">Suggested for</span>
                <span className="font-semibold text-[#333333] max-w-[200px] truncate" data-id="element-12474" data-mp="2xb4dqsvfv4hl">
                  {funcRoles.length === 0 ? 'Select use cases' : funcRoles.length <= 1 ? funcRoles[0] : `${funcRoles.length} use cases`}
                </span>
                <ChevronDown size={15} className={`text-neutral-400 transition-transform ${roleMenuOpen ? 'rotate-180' : ''}`} data-id="element-12475" data-mp="378iteq9jfp8u" />
                
              </button>

              <AnimatePresence data-id="element-12476" data-mp="3g12mzcaz33mt">
                {roleMenuOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setRoleMenuOpen(false)} data-id="element-12477" data-mp="3uymouackoior" />
                  
                    <motion.div initial={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-neutral-200 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-12478" data-mp="3q01vmuvqmz9t">
                    
                      {Object.keys(USE_CASE_RECS).map(r => {
                    const isSelected = funcRoles.includes(r);
                    return <button key={r} onClick={e => {
                      e.preventDefault();
                      setFuncRoles(prev => {
                        const next = isSelected ? prev.filter(x => x !== r) : [...prev, r];
                        const newTools = new Set<string>();
                        next.forEach(role => {
                          USE_CASE_RECS[role]?.forEach(t => newTools.add(t));
                        });
                        setSelectedTools(Array.from(newTools));
                        return next;
                      });
                    }} className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'hover:bg-neutral-50 text-neutral-600'}`} data-id="element-12479" data-mp="1eb592bk1rydp">
                          
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-12480" data-mp="3vlt1amz56rcx">
                            
                              {isSelected && <Check size={12} strokeWidth={3} className="text-white" data-id="element-12481" data-mp="3hl8kfwcsf5m2" />}
                            </div>
                            <span className="flex-1 leading-snug" data-id="element-12482" data-mp="1kr3yha3sl5sb">{r}</span>
                          </button>;
                  })}
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>

            {!isSearching && popularTools.length > 0 && <div className="mb-5 sm:mb-6" data-id="element-12483" data-mp="3f0snrtbqutw4">
                <h2 className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-[#EA580C] uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-12484" data-mp="3f1lk00d2u100">
                  <Sparkles size={13} className="shrink-0" data-id="element-12485" data-mp="359xz6ib0zi7i" />
                  Most popular
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-12486" data-mp="3szsct2365ast">
                  {popularTools.map(tool => renderToolCard(tool))}
                </div>
              </div>}

            {CATEGORIES.map(cat => {
            const items = filteredTools.filter(t => t.category === cat.key);
            if (items.length === 0) return null;
            return <div key={cat.key} className="mb-5 sm:mb-6" data-id="element-12487" data-mp="27mp6o3clx8w0">
                  <h2 className="text-[11px] sm:text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-12488" data-mp="27l5sciaih7wv">
                    {cat.label}
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-12489" data-mp="un47zfoym6nx">
                    {items.map(tool => renderToolCard(tool))}
                  </div>
                </div>;
          })}

            {filteredTools.length === 0 && (() => {
            const term = toolSearch.trim();
            const requested = term !== '' && requestedTools.includes(term.toLowerCase());
            return <div className="text-center py-12 px-6 rounded-2xl border-2 border-dashed border-neutral-200 bg-[#FAF8F4]" data-id="element-12490" data-mp="byh24uyyld9a">
                    {requested ? <div className="max-w-md mx-auto" data-id="element-12491" data-mp="6wrod7kcqlhi">
                        <div className="w-12 h-12 rounded-full bg-[#FFEDD5] flex items-center justify-center mx-auto mb-4" data-id="element-12492" data-mp="kqsredic6uno">
                          <Check size={22} className="text-[#EA580C]" data-id="element-12493" data-mp="fxznfruozqcn" />
                        </div>
                        <h3 className="font-bold text-[#333333] text-lg mb-1" data-id="element-12494" data-mp="3od9hccofhp9c">
                          On it
                        </h3>
                        <p className="text-neutral-600 text-sm" data-id="element-12495" data-mp="d35nqc1g52ce">
                          We&rsquo;ve got your request for{' '}
                          <span className="font-semibold" data-id="element-12496" data-mp="i2ry8dpw44hi">{term}</span>
                          {requestedToolLink ? ' and the link' : ''}. If it has
                          an API or a login page, our team usually wires it up{' '}
                          <span className="font-semibold text-[#333333]" data-id="element-12497" data-mp="r0kfhnf69m7j">
                            within 1 hour
                          </span>{' '}
                          — we&rsquo;ll email you the moment it&rsquo;s live.
                        </p>
                      </div> : <div className="max-w-xl mx-auto text-left" data-id="element-12498" data-mp="15sunyn6c0ycy">
                        <h3 className="font-bold text-[#333333] text-lg mb-1 text-center" data-id="element-12499" data-mp="19toc9hnosf6d">
                          {term ? `Don't see ${term}?` : 'Looking for something specific?'}
                        </h3>
                        <p className="text-neutral-600 text-sm mb-5 text-center" data-id="element-12500" data-mp="1jqjas0jymfkn">
                          Giga connects to virtually anything. Tell us what you
                          need — drop a link and we&rsquo;ll usually have it
                          connected within 1 hour.
                        </p>
                        <label className="block text-[13px] font-semibold text-[#333333] mb-1.5" data-id="element-12501" data-mp="1jxv06hwk9f1u">
                          Link{' '}
                          <span className="font-normal text-neutral-400" data-id="element-12502" data-mp="1fxulcw5jv1u9">
                            (optional)
                          </span>
                        </label>
                        <input type="url" value={requestedToolLink} onChange={e => setRequestedToolLink(e.target.value)} placeholder="https://yourtool.com" className="w-full mb-4 px-4 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#333333]/20 focus:border-[#333333]" data-id="element-12503" data-mp="27n7efw6qwdb0" />
                    
                        <button onClick={() => term && setRequestedTools(prev => [...prev, term.toLowerCase()])} disabled={!term} className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-colors ${term ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`} data-id="element-12504" data-mp="2qjyxc4idkqej">
                      
                          {term ? `Request ${term}` : 'Type a name to request it'}
                        </button>
                        <p className="flex items-start justify-center gap-1.5 text-xs text-neutral-400 leading-relaxed text-center mt-4" data-id="element-12505" data-mp="f2klo5c7y6zy">
                          <Info size={13} className="shrink-0 mt-0.5" data-id="element-12506" data-mp="a2y8fvwe4qr5" />
                          <span data-id="element-12507" data-mp="ot5kntvt1dje">
                            Connect anything later from Settings — API token,
                            MCP server, or OAuth.
                          </span>
                        </p>
                      </div>}
                  </div>;
          })()}

            <StickyRevealBar data-id="element-12508" data-mp="tu2zbwhlvpg5">
              <div className="flex items-center justify-between gap-3" data-id="element-12509" data-mp="tqowkn515e86">
                <button onClick={() => setStep(5)} aria-label="Back" className="flex items-center justify-center w-11 h-11 rounded-full text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors shrink-0" data-id="element-12510" data-mp="1cidgry39173l">
                  
                  <ArrowLeft size={20} data-id="element-12511" data-mp="18fgt3syuqkss" />
                </button>
                <div className="flex items-center gap-2 sm:gap-4" data-id="element-12512" data-mp="18dxcu2d749r0">
                  <button onClick={() => setStep(7)} className="px-2 sm:px-4 py-3 text-sm font-medium text-neutral-500 hover:text-[#333333] transition-colors whitespace-nowrap" data-id="element-12513" data-mp="tqc8640oa2gz">
                    
                    Later
                  </button>
                  <button onClick={() => setStep(7)} aria-label={selectedTools.length > 0 ? `Continue with ${selectedTools.length} tools` : 'Continue'} className="px-5 sm:px-7 py-3 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors flex items-center gap-2 whitespace-nowrap" data-id="element-12514" data-mp="kntcq4z7us4r">
                    
                    Continue
                    {selectedTools.length > 0 && <span className="hidden sm:inline text-sm" data-id="element-12515" data-mp="2hha3oeuohp2d">
                        with {selectedTools.length}{' '}
                        {selectedTools.length === 1 ? 'tool' : 'tools'}
                      </span>}
                    <ArrowRight size={18} strokeWidth={2} data-id="element-12516" data-mp="28laxcwn1ldq5" />
                  </button>
                </div>
              </div>
            </StickyRevealBar>
          </div>
        </div>
      </motion.div>;
  }
  // Step 7: Connections — per-tool OAuth permission flow
  if (step === 7) {
    const connections = step7Connections;
    const total = connections.length;
    const doneCount = connectedConns.length;
    const activeIdx = step7ActiveIdx;
    const allDone = step7AllDone;
    const allow = (conn: any) => {
      const next = connectedConns.includes(conn.id) ? connectedConns : [...connectedConns, conn.id];
      setConnectedConns(next);
      // Lock in the chosen visibility (default to shared with team if untouched)
      setConnVisibility(prev => ({
        ...prev,
        [conn.id]: prev[conn.id] ?? 'team'
      }));
      // Reset optional permission toggles for the next tool's fresh card
      setOptionalScopes({});
      if (next.length === total) {
        // All done — close the consent screen and advance.
        setConnectPhase(null);
        setPermModal(null);
        setTimeout(() => setStep(finalStep), 600);
      } else {
        // Visibility was set up front on the list, so once the user kicks off
        // the sequence we chain straight to the next tool's consent screen.
        const nextConn = connections.find(c => !next.includes(c.id));
        if (nextConn) {
          setPermModal(nextConn);
          setConnectPhase('consent');
        } else {
          setConnectPhase(null);
          setPermModal(null);
        }
      }
    };
    const handleAllowClick = (conn: any) => {
      setConnectPhase('handing-off');
      setTimeout(() => {
        allow(conn);
      }, 2000);
    };
    const startConnecting = () => {
      const firstConn = connections.find(c => !connectedConns.includes(c.id));
      if (!firstConn) return;
      setConnectionsStarted(true);
      setPermModal(firstConn);
      setConnectPhase('consent');
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12517" data-mp="23l4zjqtgteta">
        
        <GigaHeader hideOnMobile data-id="element-12518" data-mp="1awr3hglxsskd" />

        <div className="flex-1 px-4 sm:px-6 py-6 sm:py-12 pb-40 overflow-auto" data-id="element-12519" data-mp="16xrzby814ztz">
          <div className="max-w-4xl mx-auto" data-id="element-12520" data-mp="1kon16qk42go2">
            <div className="mb-2.5" data-id="element-12521" data-mp="1kpexy4pg2kfx">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12522" data-mp="1kqhna874cetq">
                STEP 4 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12523" data-mp="s6rhx3pb1vlp">
              Let's plug Giga into your stack.
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-12524" data-mp="2ywcnccwpvozp">
              Approve each one to connect. You can adjust permissions anytime in
              Settings.
            </p>

            <div className="bg-neutral-50 rounded-2xl p-4 sm:p-6 mb-6" data-id="element-12525" data-mp="2pwzf1nkbx6gk">
              <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-12526" data-mp="2uuhmtamm1fmz">
                <h2 className="text-lg sm:text-xl md:text-2xl font-title font-bold text-[#333333] italic" data-id="element-12527" data-mp="3iklqfhzstfdz">
                  {total} connections to make
                </h2>
                <span className="text-xs sm:text-sm font-medium text-neutral-500" data-id="element-12528" data-mp="3mldxq5p41a7m">
                  {doneCount}/{total} done
                </span>
              </div>

              {/* progress bar */}
              <div className="h-1.5 w-full rounded-full bg-neutral-200 mb-5 sm:mb-6 overflow-hidden" data-id="element-12529" data-mp="3dnlkr86v2ene">
                <motion.div className="h-full rounded-full bg-[#EA580C]" animate={{
                width: `${doneCount / total * 100}%`
              }} transition={{
                type: 'spring',
                stiffness: 120,
                damping: 20
              }} data-id="element-12530" data-mp="1x9bwsf6j6rx3" />
                
              </div>

              <div className="space-y-2" data-id="element-12531" data-mp="19bxexgm3skgr">
                {connections.map(conn => {
                const connected = connectedConns.includes(conn.id);
                return <div key={conn.id} className={`flex items-center gap-3 p-3 sm:p-3.5 rounded-xl bg-white border transition-all ${connected ? 'border-[#EA580C]' : 'border-neutral-200'}`} data-id="element-12532" data-mp="14cb0x4ygb06w">
                      
                      {/* Tool info */}
                      <ToolLogo name={conn.name} icon={conn.icon} className="w-9 h-9 shrink-0" fallbackTextSize="text-sm" data-id="element-12533" data-mp="qcjhgop60rli" />
                      
                      <div className="min-w-0 flex-1" data-id="element-12534" data-mp="lfjewnriy6z5">
                        <div className="font-bold text-sm sm:text-base text-[#333333] truncate" data-id="element-12535" data-mp="2krm32bb3fpj5">
                          {conn.name}
                        </div>
                        <div className="text-xs text-neutral-500" data-id="element-12536" data-mp="2fq54wnjzuh69">
                          {conn.scopes.length} permission
                          {conn.scopes.length > 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="shrink-0" data-id="element-12537" data-mp="2x23lgzh0p7ku">
                        {connected ? <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full bg-[#FFF0E6] text-xs font-semibold text-[#EA580C]" data-id="element-12538" data-mp="320ofui9l8x1d">
                            <span className="w-4 h-4 rounded-full bg-[#EA580C] flex items-center justify-center" data-id="element-12539" data-mp="24eza8crbzx0m">
                              <Check size={11} strokeWidth={3} className="text-white" data-id="element-12540" data-mp="2eb2cdim6nvnk" />
                            
                            </span>
                            Connected
                          </span> : <span className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border border-neutral-200 bg-white text-xs font-medium text-neutral-500" data-id="element-12541" data-mp="3pq97rqnpz4ms">
                            <span className="w-2 h-2 rounded-full border-[1.5px] border-neutral-300" data-id="element-12542" data-mp="3ttfe0yau0vt9" />
                            Pending
                          </span>}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom action bar — keeps the primary action reachable as the
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         tool list scrolls. Hidden while a consent screen is open. */}
        {!permModal && <StickyRevealBar data-id="element-12543" data-mp="1tm8bhniyscfz">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3" data-id="element-12544" data-mp="1tkf2w2osji5c">
              {allDone ? <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors shrink-0" data-id="element-12545" data-mp="1ew13k1ql2btj">
              
                  Continue
                  <ArrowRight size={18} data-id="element-12546" data-mp="19y90qcoeh5ak" />
                </button> : <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 shrink-0" data-id="element-12547" data-mp="19z273cjg5ss6">
                  <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto px-4 py-3 rounded-xl text-sm font-semibold text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors" data-id="element-12548" data-mp="10x1ewu32lwxd">
                
                    {doneCount > 0 ? 'Finish the rest later' : "I'll connect later"}
                  </button>
                  <button onClick={startConnecting} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors" data-id="element-12549" data-mp="2ot70itwcemf">
                
                    {doneCount > 0 ? 'Resume connecting' : 'Start connecting'}
                    <ArrowRight size={18} data-id="element-12550" data-mp="3twyobfz8gjbi" />
                  </button>
                </div>}
            </div>
          </StickyRevealBar>}

        {/* OAuth permission modal / full-screen flow — sits OUTSIDE the page
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         wrapper concerns; full-screen cover. */}
        <AnimatePresence data-id="element-12551" data-mp="33lmwc5uiyu9k">
          {permModal && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex flex-col items-center bg-white md:bg-[#F5F5F5] overflow-y-auto" data-id="element-12552" data-mp="2ynbjdm8xo3n4">
            
              {connectPhase === 'consent' && <div className="w-full h-full flex flex-col items-center md:pt-12 md:pb-24 md:px-6" data-id="element-12553" data-mp="2knajoa4en298">
                  {/* Fake URL Bar - Full width header on mobile, floating pill on desktop */}
                  <div className="flex items-center justify-between w-full md:max-w-md bg-neutral-100 md:bg-white md:rounded-full px-4 py-3 md:py-2 md:shadow-sm border-b md:border border-neutral-200 mb-0 md:mb-8 shrink-0" data-id="element-12554" data-mp="1s6zsm68fxo1k">
                    <div className="flex items-center gap-2 text-neutral-500" data-id="element-12555" data-mp="3naxvhqwgtu5z">
                      <Lock size={14} className="text-[#EA580C]" data-id="element-12556" data-mp="3s8ze6apzbrgx" />
                      <span className="font-mono text-xs truncate max-w-[200px] sm:max-w-none" data-id="element-12557" data-mp="3s7eg4b4ghqeb">
                        {permModal.id}.com{' '}
                        <span className="text-neutral-400" data-id="element-12558" data-mp="4wokcz3werao">
                          /oauth/authorize
                        </span>
                      </span>
                    </div>
                    <span className="font-mono text-xs text-neutral-400 shrink-0" data-id="element-12559" data-mp="39a3jyze4fd33">
                      {doneCount + 1} of {total}
                    </span>
                  </div>

                  {/* Consent Card - Full width on mobile, rounded card on desktop */}
                  <div className="w-full max-w-md bg-white md:rounded-3xl md:shadow-xl md:border border-neutral-200 flex flex-col min-h-screen md:min-h-0" data-id="element-12560" data-mp="3i8ys4h0q6x91">
                    <div className="flex-1 overflow-auto" data-id="element-12561" data-mp="xjswrp5bm7wh">
                      <div className="p-6 md:p-8 pb-6 flex flex-col items-center" data-id="element-12562" data-mp="17orepvs58xg4">
                        <div className="flex items-center justify-center gap-4 mb-6" data-id="element-12563" data-mp="17qvkkj9ls6j1">
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl border border-neutral-200 flex items-center justify-center shadow-sm bg-white" data-id="element-12564" data-mp="12qg3phq44gwb">
                            <ToolLogo name={permModal.name} icon={permModal.icon} className="w-7 h-7 md:w-8 md:h-8" data-id="element-12565" data-mp="ty5rjuvnq1cf" />
                        
                          </div>
                          <ArrowRight size={16} className="text-neutral-300" data-id="element-12566" data-mp="jzgb86jesoya" />
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-12567" data-mp="k1jbdje9zygq">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 md:w-8 md:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12568" data-mp="aza1mvqu094e" />
                        
                          </div>
                        </div>

                        <h3 className="text-xl md:text-2xl font-title font-bold text-[#333333] text-center mb-3 leading-tight" data-id="element-12569" data-mp="2cdwqs831oasa">
                          Gigabrain is requesting access to your{' '}
                          {permModal.name} account
                        </h3>

                        <p className="text-[13px] md:text-[14px] text-neutral-500 text-center mb-6 max-w-sm leading-relaxed" data-id="element-12570" data-mp="27etkylf8q0j9">
                          Choose what Giga can do. You can change this anytime
                          in Settings → Connections, and revoke from{' '}
                          {permModal.name} directly.
                        </p>

                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neutral-200 bg-neutral-50" data-id="element-12571" data-mp="1opddtf91v9tv">
                          <div className="w-5 h-5 rounded-full bg-[#2B211A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-12572" data-mp="1ar5l5jj1quoi">
                            L
                          </div>
                          <span className="text-sm text-neutral-700" data-id="element-12573" data-mp="w5rkd33duz2d">
                            Signed in as{' '}
                            <span className="font-semibold" data-id="element-12574" data-mp="154dapkf3jidp">l@acme.co</span>
                          </span>
                        </div>
                      </div>

                      <div className="px-6 md:px-8 py-6 bg-white border-t border-neutral-100" data-id="element-12575" data-mp="111gl39r52m09">
                        <div className="flex items-center justify-between mb-4" data-id="element-12576" data-mp="1f0yfc4n7erh0">
                          <span className="text-sm font-semibold text-[#333333]" data-id="element-12577" data-mp="1a2n53sszyey9">
                            This will allow Giga to:
                          </span>
                          <span className="text-xs font-mono text-neutral-400" data-id="element-12578" data-mp="1jthompeuz0g0">
                            {permModal.scopes.filter((s: any) => s.required || optionalScopes[s.id]).length}
                            /{permModal.scopes.length} permissions
                          </span>
                        </div>

                        <div className="space-y-0 divide-y divide-neutral-100 border-b border-neutral-100 mb-6" data-id="element-12579" data-mp="qhb3o1ex4woo">
                          {permModal.scopes.map((scope: any) => <label key={scope.id} className={`flex items-start gap-4 py-4 ${scope.required ? 'cursor-default' : 'cursor-pointer'}`} data-id="element-12580" data-mp="vd8gvyuvxmx8">
                        
                              <div className="mt-0.5" data-id="element-12581" data-mp="149hk7olzh5on">
                                <div className={`w-5 h-5 rounded flex items-center justify-center ${scope.required || !!optionalScopes[scope.id] ? 'bg-[#2B211A] border-[#2B211A]' : 'border-neutral-300 border'} ${scope.required ? 'opacity-100' : ''}`} data-id="element-12582" data-mp="197j1c3zumuxh">
                            
                                  {(scope.required || !!optionalScopes[scope.id]) && <Check size={12} className="text-white" strokeWidth={3} data-id="element-12583" data-mp="1n5h9grnktv4j" />}
                                </div>
                              </div>
                              <div data-id="element-12584" data-mp="3oxm10215yqeq">
                                <div className="flex items-center gap-2 mb-0.5" data-id="element-12585" data-mp="3oynmz1jpwxb8">
                                  <span className="text-[15px] font-bold text-[#333333]" data-id="element-12586" data-mp="3hfo3kvw9b5rv">
                                    {scope.label}
                                  </span>
                                  {scope.required && <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] font-bold text-neutral-500 tracking-wide uppercase" data-id="element-12587" data-mp="37putjzbriypq">
                                      Required
                                    </span>}
                                </div>
                                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-12588" data-mp="3vdvv2oaixevn">
                                  {scope.description}
                                </p>
                              </div>
                            </label>)}
                        </div>

                        <div className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 flex gap-3" data-id="element-12589" data-mp="2fxnaks8sba86">
                          <ShieldCheck className="w-5 h-5 text-[#EA580C] shrink-0 mt-0.5" data-id="element-12590" data-mp="2ayu1rd4g07ec" />
                          <p className="text-[13px] text-neutral-600 leading-relaxed" data-id="element-12591" data-mp="evnvnxcrih7p">
                            Giga stores access tokens encrypted at rest and
                            isolated per workspace. Your {permModal.name} data
                            is never shared with other Gigabrain customers or
                            used to train shared models.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 md:p-6 bg-white border-t border-neutral-100 flex flex-col md:flex-row items-center justify-between sticky bottom-0 z-10" data-id="element-12592" data-mp="12l0d1ki8tvl3">
                      <p className="hidden md:block text-[11px] text-neutral-500 max-w-[180px] leading-relaxed" data-id="element-12593" data-mp="nvkn26qjdu6g">
                        By continuing, you agree to Gigabrain's{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-12594" data-mp="1gjzpzuwe8m1f">
                      
                          Terms
                        </a>{' '}
                        and{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-12595" data-mp="360c3x5oofzuf">
                      
                          Privacy Notice
                        </a>
                        .
                      </p>
                      <div className="flex flex-col-reverse md:flex-row md:items-center gap-3 w-full md:w-auto" data-id="element-12596" data-mp="3asln82vae1nq">
                        <button onClick={() => {
                    setPermModal(null);
                    setConnectPhase(null);
                    setConnectionsStarted(false);
                  }} className="w-full md:w-auto px-5 py-3.5 md:py-2.5 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-12597" data-mp="2n2rgm31k99bx">
                      
                          Cancel
                        </button>
                        <button onClick={() => handleAllowClick(permModal)} className="w-full md:w-auto px-6 py-3.5 md:py-2.5 rounded-xl bg-[#2B211A] text-white text-sm font-semibold hover:bg-[#EA580C] transition-colors shadow-sm whitespace-nowrap" data-id="element-12598" data-mp="bc6wsmd7myqn">
                      
                          Allow access
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="hidden md:block mt-6 font-mono text-xs text-neutral-400 text-center max-w-sm leading-relaxed" data-id="element-12599" data-mp="c0jvep91o9vf">
                    You'll be returned to Gigabrain automatically. This stays in
                    the same tab.
                  </p>
                </div>}

              {connectPhase === 'handing-off' && <div className="flex-1 flex flex-col items-center justify-center w-full" data-id="element-12600" data-mp="zs8klwqkki85">
                  <div className="flex items-center gap-6 mb-6" data-id="element-12601" data-mp="ux31f5ojxhnr">
                    <div className="w-12 h-12 rounded-xl border border-neutral-200 bg-white flex items-center justify-center shadow-sm" data-id="element-12602" data-mp="pz9uubjc6lmy">
                      <ToolLogo name={permModal.name} icon={permModal.icon} className="w-6 h-6" data-id="element-12603" data-mp="1nh9xbw6fqfj8" />
                  
                    </div>
                    <Loader2 size={24} className="text-neutral-400 animate-spin" data-id="element-12604" data-mp="1q0oglo2zpsoa" />
                
                    <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-12605" data-mp="1uy6gyq2xyzko">
                      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-12606" data-mp="1zwrfml2jpb70" />
                  
                    </div>
                  </div>
                  <p className="font-mono text-sm text-neutral-500" data-id="element-12607" data-mp="2iqwq6rjgn4r9">
                    Handing off to {permModal.name}...
                  </p>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  }
  // Step 8: Now go to Slack
  if (step === 8) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12608" data-mp="16c58d0jpc2bt">
        
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 md:px-10 py-6" data-id="element-12609" data-mp="1g9t67zcltpy7">
          <Link to="/" aria-label="Back to Giga home" className="flex items-center gap-2 group w-fit" data-id="element-12610" data-mp="1gbw2300fudau">
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain group-hover:scale-105 transition-transform" data-id="element-12611" data-mp="1pahj2qq9hk9i" />
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain" data-id="element-12612" data-mp="3n0iuv0a5cosm" />
            
          </Link>
        </div>

        <div className="flex-1 flex items-center" data-id="element-12613" data-mp="3gww5n81hv0le">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-10" data-id="element-12614" data-mp="336sqt24y295z">
            {/* Left — call to action */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-12615" data-mp="383rfh55u9nws">
              
              <h1 className="text-5xl md:text-6xl font-title font-bold text-[#333333] mb-5 tracking-tight" data-id="element-12616" data-mp="2k8oq8w34hifs">
                Giga's waiting in Slack.
              </h1>
              <p className="text-lg text-neutral-600 mb-8 max-w-md leading-relaxed" data-id="element-12617" data-mp="igk2zxwkj9be">
                Open Slack and find Giga under{' '}
                <span className="font-semibold text-[#333333]" data-id="element-12618" data-mp="nfwly7kbjw8p">"Apps"</span>, or
                just <span className="font-semibold text-[#333333]" data-id="element-12619" data-mp="2jjb4je47mpq0">@Giga</span>{' '}
                them in any channel in {WORKSPACE_NAME}.
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-12620" data-mp="2ehlt3xd2sa1e">
                <button onClick={() => setStep(9)} className="inline-flex items-center justify-center gap-2.5 bg-[#2B211A] text-white px-7 py-4 rounded-xl font-bold text-base hover:bg-black transition-colors shadow-lg shadow-black/20" data-id="element-12621" data-mp="29jk7p5s6fymr">
                  
                  <SlackIcon size={20} data-id="element-12622" data-mp="1vt757iy21c4t" />
                  Message Giga
                </button>
                <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 font-medium hover:text-[#333333] transition-colors" data-id="element-12623" data-mp="1qu3xfqqowrt5">
                  
                  <ArrowLeft size={18} data-id="element-12624" data-mp="1lwlz0uaatuze" />
                  Back to home
                </Link>
              </div>
            </motion.div>

            {/* Right — Slack window mock */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="hidden lg:block rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white" data-id="element-12625" data-mp="1cve32iv9968h">
              
              {/* macOS chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#3F0E40]" data-id="element-12626" data-mp="6ba4j4pp7joi">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-12627" data-mp="35si05hr3541m" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-12628" data-mp="39s0hy4nm0z08" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-12629" data-mp="3erv989barqlt" />
                <div className="flex-1 mx-4" data-id="element-12630" data-mp="3eqk4o7a12etn">
                  <div className="flex items-center gap-2 rounded-md bg-black/20 px-3 py-1.5 text-white/60 text-xs" data-id="element-12631" data-mp="3jj4wj39dihuj">
                    <Search size={12} data-id="element-12632" data-mp="2m3zbd19zrsng" />
                    Search
                  </div>
                </div>
              </div>

              <div className="flex h-[420px]" data-id="element-12633" data-mp="2r0hgi298ht0b">
                {/* Sidebar */}
                <div className="w-40 shrink-0 bg-[#3F0E40] text-white/80 py-3 flex flex-col" data-id="element-12634" data-mp="3ubwwznjotjg4">
                  <div className="px-4 pb-3 mb-1 border-b border-white/10 flex items-center gap-1.5 font-bold text-white text-sm" data-id="element-12635" data-mp="3pd21pm4ja2f4">
                    {WORKSPACE_NAME}
                    <ChevronDown size={14} data-id="element-12636" data-mp="2ysf9ik5z4gz" />
                  </div>
                  <div className="px-2 space-y-0.5 text-[13px]" data-id="element-12637" data-mp="30n2x4mtlaor">
                    {navItems.map(n => <div key={n.label} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12638" data-mp="3bem0gapfx44d">
                      
                        <n.icon size={15} className="opacity-70" data-id="element-12639" data-mp="3kfacizv9wuem" />
                        {n.label}
                      </div>)}
                    <div className="h-px bg-white/10 my-2 mx-2" data-id="element-12640" data-mp="3fee01xwuvzxl" />
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12641" data-mp="zo05h3vqa1r8">
                      <Hash size={15} className="opacity-70" data-id="element-12642" data-mp="uowy3cs9zjg3" />
                      Channels
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12643" data-mp="135fg4udln6ir">
                      <MessageSquare size={15} className="opacity-70" data-id="element-12644" data-mp="175zeu2dtmi70" />
                      Direct messages
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12645" data-mp="y6ufitufqkts">
                      <Grid3x3 size={15} className="opacity-70" data-id="element-12646" data-mp="1lyjegt1ri7jp" />
                      Apps
                    </div>
                    {/* Active Giga DM */}
                    <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-[#1164A3] text-white mt-0.5" data-id="element-12647" data-mp="1c7op3ft7yru1">
                      <span className="flex items-center gap-2.5" data-id="element-12648" data-mp="1h5gike9iim0c">
                        <span className="w-4 h-4 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12649" data-mp="24wcftqwqm9ez">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-2.5 h-2.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12650" data-mp="29naz8zd0vnmg" />
                          
                        </span>
                        Giga
                      </span>
                      <span className="w-4 h-4 rounded-full bg-[#E01E5A] text-white text-[10px] font-bold flex items-center justify-center" data-id="element-12651" data-mp="3rystmcyd8rcn">
                        1
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main column */}
                <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-12652" data-mp="jiha8imva3ep">
                  {/* DM header + tabs */}
                  <div className="px-4 pt-3 border-b border-neutral-200" data-id="element-12653" data-mp="ekfnnnq8flyh">
                    <div className="flex items-center gap-2 mb-3" data-id="element-12654" data-mp="348oqe6p3atm6">
                      <span className="w-6 h-6 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12655" data-mp="346vgmij6jt9y">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12656" data-mp="2zbybhmp0u42c" />
                        
                      </span>
                      <span className="font-bold text-[#333333] text-sm" data-id="element-12657" data-mp="1sizeoy1rznu0">
                        Giga
                      </span>
                      <ChevronDown size={14} className="text-neutral-400" data-id="element-12658" data-mp="1xhiyoswt6pjq" />
                    </div>
                    <div className="flex items-center gap-5 text-sm" data-id="element-12659" data-mp="22g402vnsrf9r">
                      <span className="pb-2 text-neutral-500" data-id="element-12660" data-mp="27fytb5v1ogxz">Home</span>
                      <span className="pb-2 -mb-px border-b-2 border-[#333333] font-semibold text-[#333333]" data-id="element-12661" data-mp="27enkgrmqsj00">
                        Messages
                      </span>
                      <span className="pb-2 text-neutral-500" data-id="element-12662" data-mp="d4a9oqdvbq79">About</span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-auto px-4 py-4" data-id="element-12663" data-mp="356vv5t74ri1g">
                    <div className="text-center text-[11px] text-neutral-400 mb-4" data-id="element-12664" data-mp="3a575e51c7uk7">
                      This is the very beginning of your direct message history
                      with <span className="text-[#1164A3]" data-id="element-12665" data-mp="3ewp6ct3d1v56">@Giga</span>
                    </div>
                    <div className="flex items-start gap-2.5" data-id="element-12666" data-mp="3jugz1pbtxlaf">
                      <span className="w-9 h-9 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-12667" data-mp="2m7rrz129sihp">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12668" data-mp="2r7e0wyb2fckp" />
                        
                      </span>
                      <div className="min-w-0" data-id="element-12669" data-mp="bex9p1ea4n0n">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-12670" data-mp="fg8zp812fa9d">
                          <span className="font-bold text-sm text-[#333333]" data-id="element-12671" data-mp="1zytastr61hje">
                            Giga
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12672" data-mp="1vyah0u5pq1ek">
                            App
                          </span>
                          <span className="text-[11px] text-neutral-400 hover:underline cursor-pointer" data-id="element-12673" data-mp="1m5bstde4ocvh">
                            9:16 AM
                          </span>
                        </div>
                        <p className="text-sm text-[#333333] leading-snug mb-2" data-id="element-12674" data-mp="1cbctsn9cp97r">
                          Hi 👋, great to meet you. I'm Giga, your new AI
                          coworker. Here are two ways to work with me:
                        </p>
                        <p className="text-sm text-[#333333] leading-snug mb-1.5" data-id="element-12675" data-mp="32kmrg9rkak2z">
                          💬 <span className="font-bold" data-id="element-12676" data-mp="2xmbie193cdlt">DM me here</span> —
                          just message me like a coworker. Research, analysis,
                          reports, automation.
                        </p>
                        <p className="text-sm text-[#333333] leading-snug" data-id="element-12677" data-mp="3lipz6i64okap">
                          <span className="text-[#1164A3] font-medium" data-id="element-12678" data-mp="37rb4vf6rswoc">
                            @Giga
                          </span>{' '}
                          <span className="font-bold" data-id="element-12679" data-mp="3cnibzbawke7v">in any channel</span> —
                          mention me in context and I'll jump in with the full
                          thread as background.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Slack mock (Mobile) */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="lg:hidden rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white flex flex-col w-full max-w-md mx-auto" data-id="element-12680" data-mp="2f1ueso6mhrva">
              
              {/* Mobile Slack Header */}
              <div className="bg-[#3F0E40] text-white px-4 py-3 flex items-center gap-3" data-id="element-12681" data-mp="2lhkwsad0km63">
                <div className="w-9 h-9 rounded bg-white/20 flex items-center justify-center shrink-0" data-id="element-12682" data-mp="2gjjbdis48arg">
                  <span className="font-bold text-base" data-id="element-12683" data-mp="1styowpwr6vdf">
                    {WORKSPACE_INITIAL}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-12684" data-mp="22iyrskedmaww">
                  <div className="text-xs font-medium text-white/70 truncate" data-id="element-12685" data-mp="22ktdw21dw92k">
                    {WORKSPACE_NAME}
                  </div>
                  <div className="font-bold text-[15px] truncate leading-tight" data-id="element-12686" data-mp="1a2xveheejs19">
                    Giga
                  </div>
                </div>
                <Search size={20} className="text-white/80" data-id="element-12687" data-mp="15335ag2gb6h9" />
              </div>

              {/* Mobile Messages Area */}
              <div className="flex-1 bg-white p-5" data-id="element-12688" data-mp="1iogxwzyz27y8">
                <div className="text-center text-[11px] text-neutral-400 mb-5" data-id="element-12689" data-mp="1nov9grzs8010">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3]" data-id="element-12690" data-mp="1xfhghxtfni83">@Giga</span>
                </div>
                <div className="flex items-start gap-3" data-id="element-12691" data-mp="1xhkhte01cxs4">
                  <span className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-12692" data-mp="21g1fz81haxxe">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12693" data-mp="26el0r54c63o6" />
                    
                  </span>
                  <div className="min-w-0 pt-0.5" data-id="element-12694" data-mp="h32duk8adpme">
                    <div className="flex items-center gap-1.5 mb-1" data-id="element-12695" data-mp="lzj2dfjee600">
                      <span className="font-bold text-[15px] text-[#333333]" data-id="element-12696" data-mp="2zakh9yodamqu">
                        Giga
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12697" data-mp="2ujm2j3j11wpp">
                        App
                      </span>
                      <span className="text-xs text-neutral-400" data-id="element-12698" data-mp="2lkqsthgry4hn">9:16 AM</span>
                    </div>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2.5" data-id="element-12699" data-mp="3i8wv3xq84ksr">
                      Hi 👋, great to meet you. I'm Giga, your new AI coworker.
                      Here are two ways to work with me:
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2" data-id="element-12700" data-mp="349xendcv7c7o">
                      💬 <span className="font-bold" data-id="element-12701" data-mp="34c1k3zqewja2">DM me here</span> — just
                      message me like a coworker. Research, analysis, reports,
                      automation.
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug" data-id="element-12702" data-mp="1ubxckjbqr7n7">
                      <span className="text-[#1164A3] font-medium" data-id="element-12703" data-mp="1u9l0gmsnzri2">@Giga</span>{' '}
                      <span className="font-bold" data-id="element-12704" data-mp="1z9z6y0ematdz">in any channel</span> —
                      mention me in context and I'll jump in with the full
                      thread as background.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>;
  }
  if (step === 9) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="h-screen w-full bg-white font-body flex flex-col overflow-hidden" data-id="element-12705" data-mp="16jqp69nzuazx">
        
        {/* macOS chrome & Slack header */}
        <div className="flex items-center gap-4 px-4 py-2 bg-[#3F0E40] border-b border-white/10 shrink-0" data-id="element-12706" data-mp="dz3bb2ff81e1">
          <div className="flex items-center gap-2 w-16" data-id="element-12707" data-mp="911witp12o8d">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-12708" data-mp="92mku1kr1axs" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-12709" data-mp="nyke9c3ag16b" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-12710" data-mp="iy6iotivijp6" />
          </div>
          <div className="flex-1 flex justify-center" data-id="element-12711" data-mp="wyh8dt8g5s8r">
            <div className="flex items-center gap-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors px-4 py-1.5 text-white/80 text-sm w-full max-w-2xl cursor-text" data-id="element-12712" data-mp="wwwem5tyidby">
              <Search size={14} data-id="element-12713" data-mp="16ok3q031jia9" />
              Search {WORKSPACE_NAME}
            </div>
          </div>
          <div className="w-16 flex justify-end" data-id="element-12714" data-mp="11q8rxjt6qxpe">
            <div className="w-7 h-7 rounded bg-white/20" data-id="element-12715" data-mp="11rjw3kqjm7h1" />
          </div>
        </div>

        <div className="flex flex-1 min-h-0" data-id="element-12716" data-mp="1fmw0ykyexebo">
          {/* Sidebar */}
          <div className="w-64 shrink-0 bg-[#3F0E40] text-white/80 py-4 flex flex-col overflow-y-auto" data-id="element-12717" data-mp="1booldtfoqj22">
            <div className="px-4 pb-4 mb-2 border-b border-white/10 flex items-center justify-between font-bold text-white text-lg" data-id="element-12718" data-mp="2i1tamrruokjd">
              <span className="flex items-center gap-1.5" data-id="element-12719" data-mp="2s0ivmtf1ml3u">
                {WORKSPACE_NAME}
                <ChevronDown size={16} data-id="element-12720" data-mp="2ryoapf3ruszr" />
              </span>
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-12721" data-mp="2w0t2s9eknhka">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12722" data-mp="1zd4s2cguarar">
                  
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-12723" data-mp="28c9pffgo0elc"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-12724" data-mp="2d3i16i5gwc1o"></path>
                </svg>
              </div>
            </div>
            <div className="px-3 space-y-0.5 text-[15px]" data-id="element-12725" data-mp="1gj9dqbbkc14n">
              {navItems.map(n => <div key={n.label} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer" data-id="element-12726" data-mp="1kle4916pshn2">
                
                  <n.icon size={18} className="opacity-70" data-id="element-12727" data-mp="183tnd3vdykiy" />
                  {n.label}
                </div>)}
              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-12728" data-mp="141ykvw1py13l" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-12729" data-mp="144kpbmtbc0hl">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-12730" data-mp="u4k4txjpjebt">
                  <ChevronDown size={14} data-id="element-12731" data-mp="pdtxierq11eh" />
                  Channels
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-12732" data-mp="ke90i9w4334e">
                <Hash size={16} className="opacity-70" data-id="element-12733" data-mp="gegrxx6pwwz1" />
                general
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-12734" data-mp="bcreybzxq98b">
                <Hash size={16} className="opacity-70" data-id="element-12735" data-mp="6hc29ndnyjdh" />
                engineering
              </div>

              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-12736" data-mp="37gz9zpan3y22" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-12737" data-mp="37gfojyls6rja">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-12738" data-mp="3lg8e3qlkn1po">
                  <ChevronDown size={14} data-id="element-12739" data-mp="3gigkms5a37jd" />
                  Direct messages
                </span>
              </div>

              {/* Active Giga DM */}
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-[#1164A3] text-white mt-1 cursor-pointer" data-id="element-12740" data-mp="2ntrh6mgr2q4g">
                <span className="flex items-center gap-3" data-id="element-12741" data-mp="32j757uowchgg">
                  <span className="relative w-5 h-5 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12742" data-mp="2xqw5aru5ovnw">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12743" data-mp="2a0jpyvwkzwna" />
                    
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#1164A3]" data-id="element-12744" data-mp="pbg27stukiu5" />
                  </span>
                  Giga
                </span>
              </div>

              {[{
              name: 'Priya Patel',
              initial: 'P',
              color: 'bg-[#4A9E8E]',
              online: true
            }, {
              name: 'Marcus Lee',
              initial: 'M',
              color: 'bg-[#9B6DD6]',
              online: true
            }, {
              name: 'Jordan Kim',
              initial: 'J',
              color: 'bg-[#E0727F]',
              online: false
            }, {
              name: 'Elena Rodriguez',
              initial: 'E',
              color: 'bg-[#D9A441]',
              online: false
            }, {
              name: 'Sarah (you)',
              initial: 'S',
              color: 'bg-[#4F84D6]',
              online: true
            }].map(person => <div key={person.name} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-1" data-id="element-12745" data-mp="1kk9t00iuz6qd">
                
                  <span className={`relative w-5 h-5 rounded ${person.color} flex items-center justify-center text-xs font-bold text-white shrink-0`} data-id="element-12746" data-mp="1frqhd93qelo7">
                  
                    {person.initial}
                    {person.online ? <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#3F0E40]" data-id="element-12747" data-mp="11ts303peup8p" /> : <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#3F0E40] border-2 border-white/50" data-id="element-12748" data-mp="wufeleiw5a40" />}
                  </span>
                  {person.name}
                </div>)}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-12749" data-mp="2r6drprkbmq56">
            {/* Header */}
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between shrink-0" data-id="element-12750" data-mp="2n50hz6e3y2sz">
              <div className="flex items-center gap-3" data-id="element-12751" data-mp="dygnmonbo3dz">
                <span className="w-8 h-8 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12752" data-mp="dx5h4h2hshj6">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12753" data-mp="iqggjx4z5w12" />
                  
                </span>
                <div className="flex flex-col" data-id="element-12754" data-mp="3r5ibltzfaq5k">
                  <div className="flex items-center gap-2" data-id="element-12755" data-mp="3v6tttefyw93q">
                    <span className="font-bold text-[#333333] text-[15px]" data-id="element-12756" data-mp="3v592rysv38am">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12757" data-mp="1aglaonijdxfh">
                      App
                    </span>
                    <span className="w-2 h-2 rounded-full bg-[#2BAC76]" data-id="element-12758" data-mp="1dxqxavyxb53y" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-neutral-500" data-id="element-12759" data-mp="190qpafiijsa7">
                <span className="font-semibold text-sm pb-1 border-b-2 border-[#333333] text-[#333333] cursor-pointer" data-id="element-12760" data-mp="1n003j9n5scbj">
                  Messages
                </span>
                <span className="font-semibold text-sm pb-1 border-b-2 border-transparent hover:text-[#333333] cursor-pointer" data-id="element-12761" data-mp="1wwv19qbly8or">
                  About
                </span>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-5 flex flex-col" data-id="element-12762" data-mp="26men94umainu">
              {/* Intro Block */}
              <div className="mb-8 mt-4" data-id="element-12763" data-mp="21omt046i2kgh">
                <div className="flex items-center gap-4 mb-4" data-id="element-12764" data-mp="2fncojfdo8c2q">
                  <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-sm" data-id="element-12765" data-mp="2fljjg4iphzwu">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-10 h-10 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12766" data-mp="2amosugehz726" />
                    
                  </div>
                  <div data-id="element-12767" data-mp="2payjdxvuul1o">
                    <div className="flex items-center gap-2 mb-1" data-id="element-12768" data-mp="2pa6z3j7pk9qp">
                      <span className="font-bold text-2xl text-[#333333]" data-id="element-12769" data-mp="2bk3hx6nor684">
                        Giga
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12770" data-mp="2gblhprdz30ri">
                        App
                      </span>
                      <span className="w-2.5 h-2.5 rounded-full bg-[#2BAC76]" data-id="element-12771" data-mp="26l0bpivyytyk" />
                    </div>
                  </div>
                </div>
                <p className="text-[15px] text-[#333333] mb-2" data-id="element-12772" data-mp="1xkkakg2p7ywv">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded" data-id="element-12773" data-mp="3552ifvz3zao">
                    @Giga
                  </span>
                </p>
                <div className="flex items-center gap-1.5 text-[#1164A3] text-[15px] hover:underline cursor-pointer w-fit" data-id="element-12774" data-mp="3piwyk3gd3d8t">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12775" data-mp="3lj4kxcc42gwl">
                    
                    <circle cx="12" cy="12" r="10" data-id="element-12776" data-mp="h3d8teewgcyi"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-12777" data-mp="h1sf1r0esy1p"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-12778" data-mp="d06sfdy2p5eb"></line>
                  </svg>
                  How does Giga work?
                </div>
              </div>

              {/* Date Divider */}
              <div className="relative flex items-center justify-center mb-6" data-id="element-12779" data-mp="2xtk1ytfyjjnb">
                <div className="absolute inset-0 flex items-center" data-id="element-12780" data-mp="2sscpih2vdwab">
                  <div className="w-full border-t border-neutral-200" data-id="element-12781" data-mp="18bn2gijc7x8l"></div>
                </div>
                <div className="relative bg-white px-4" data-id="element-12782" data-mp="1d9onva48k8n8">
                  <span className="text-[13px] font-bold text-[#333333] border border-neutral-200 rounded-full px-4 py-1 shadow-sm" data-id="element-12783" data-mp="1d83uvoxkkxrh">
                    Today <ChevronDown size={12} className="inline ml-1" data-id="element-12784" data-mp="1m31qj3s3qomi" />
                  </span>
                </div>
              </div>

              {/* Giga Onboarding Message */}
              <div className="flex items-start gap-3 hover:bg-neutral-50 -mx-5 px-5 py-2 transition-colors" data-id="element-12785" data-mp="1w0pn7z99q67b">
                <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5" data-id="element-12786" data-mp="21028ifkhr57s">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12787" data-mp="251nnc6gl5tkm" />
                  
                </div>
                <div className="min-w-0 flex-1" data-id="element-12788" data-mp="3nvgv1irb5ncy">
                  <div className="flex items-center gap-2 mb-1" data-id="element-12789" data-mp="3nu5stndignny">
                    <span className="font-bold text-[15px] text-[#333333]" data-id="element-12790" data-mp="3iww306m7m9zl">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12791" data-mp="l90ziw6luom">
                      App
                    </span>
                    <span className="text-xs text-neutral-500 hover:underline cursor-pointer" data-id="element-12792" data-mp="2zyta3gqjjswi">
                      3:27 PM
                    </span>
                  </div>

                  <div className="text-[15px] text-[#333333] leading-relaxed space-y-3" data-id="element-12793" data-mp="3dvybv4j825ly">
                    <p data-id="element-12794" data-mp="391c9trz15pej">
                      Hi 👋, great to meet you. I am Giga, your new AI coworker.
                      Here are three ways to work with me:
                    </p>

                    <div className="space-y-1.5" data-id="element-12795" data-mp="o8qylv24nmzi">
                      <p data-id="element-12796" data-mp="wphsr2br2bcb">
                        💬 <span className="font-bold" data-id="element-12797" data-mp="wp84pka9me99">DM me here</span> — just
                        message me like a coworker. Research, analysis, reports,
                        automation — anything.
                      </p>
                      <p data-id="element-12798" data-mp="mty2avfyg4uy">
                        📣{' '}
                        <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium cursor-pointer" data-id="element-12799" data-mp="rrzhh5a9fk15">
                          @Giga
                        </span>{' '}
                        <span className="font-bold" data-id="element-12800" data-mp="1kg3sm6blopb8">in any channel</span> —
                        mention me in context and I'll jump in with the full
                        thread as background.
                      </p>
                      <p data-id="element-12801" data-mp="1aohi6c736u9j">
                        🔌{' '}
                        <span className="font-bold" data-id="element-12802" data-mp="1yfmy0rd11mju">
                          I connect to 3000+ tools
                        </span>{' '}
                        — no tools are connected yet. Need GitHub, Stripe,
                        HubSpot, Google Ads, or anything else? Just tell me and
                        I'll figure out access.
                      </p>
                      <p data-id="element-12803" data-mp="1n3t4ljn11v4i">
                        🔍{' '}
                        <span className="font-bold" data-id="element-12804" data-mp="210gcghgiq5p7">
                          I'm now reading through the workspace history
                        </span>{' '}
                        — I'll propose ideas for how I can help over the next
                        hours.
                      </p>
                    </div>

                    <p data-id="element-12805" data-mp="14cs8r4h7efp6">
                      Try one of these now — just copy, paste, and hit send:
                    </p>

                    <div className="space-y-4" data-id="element-12806" data-mp="1i4gij13zcs2z">
                      <div data-id="element-12807" data-mp="1daufm0nmc2kd">
                        <p className="italic text-neutral-600 mb-1" data-id="element-12808" data-mp="1dam164bukp72">
                          1. In a channel:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-12809" data-mp="lkzd12ar2gdr">
                          <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium" data-id="element-12810" data-mp="liwn0i9lkn91">
                            @Giga
                          </span>{' '}
                          summarize what's been discussed in this channel this
                          week
                        </div>
                      </div>

                      <div data-id="element-12811" data-mp="6gwj5x9mbf03">
                        <p className="italic text-neutral-600 mb-1" data-id="element-12812" data-mp="6i7s0bhx7cy2">
                          2. Or right here:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-12813" data-mp="z63jn1smuenz">
                          Research the top 5 trends in our industry and put
                          together a brief report
                        </div>
                      </div>
                    </div>

                    <p data-id="element-12814" data-mp="34zboaqwvurvp">
                      Or just tell me what you need — I'll take it from there 🚀
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Composer */}
            <div className="px-5 pb-6 pt-2 shrink-0" data-id="element-12815" data-mp="1gee8mk3rusqh">
              <div className="border border-neutral-400 rounded-xl overflow-hidden focus-within:border-neutral-500 focus-within:ring-1 focus-within:ring-neutral-500 transition-all shadow-sm" data-id="element-12816" data-mp="1bgmk802c5or1">
                {/* Toolbar */}
                <div className="bg-neutral-50 border-b border-neutral-200 px-2 py-1.5 flex items-center gap-1" data-id="element-12817" data-mp="11rl0byso6n7s">
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12818" data-mp="wsrpyf8ojcbu">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12819" data-mp="sp207mg9qop8">
                      
                      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-12820" data-mp="isqn4u8ututq"></path>
                      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-12821" data-mp="2i4t33ui9s72n"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12822" data-mp="2d6zyh5wm7l4h">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12823" data-mp="2kq96auph3brf">
                      
                      <line x1="19" y1="4" x2="10" y2="4" data-id="element-12824" data-mp="2uer556e6z8sf"></line>
                      <line x1="14" y1="20" x2="5" y2="20" data-id="element-12825" data-mp="2uhn14p6hbne8"></line>
                      <line x1="15" y1="4" x2="9" y2="20" data-id="element-12826" data-mp="2zd2g5kg83pc8"></line>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12827" data-mp="21sg27p344vsg">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12828" data-mp="26q849bxh1yad">
                      
                      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" data-id="element-12829" data-mp="2gonqp1lk0jdj"></path>
                      <line x1="4" y1="21" x2="20" y2="21" data-id="element-12830" data-mp="3n4raf6clunvt"></line>
                    </svg>
                  </button>
                  <div className="w-px h-4 bg-neutral-300 mx-1" data-id="element-12831" data-mp="3s1q27i8sqiqu" />
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12832" data-mp="3pnuo98bq0fxi">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12833" data-mp="3ln2g6iel4h2t">
                      
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" data-id="element-12834" data-mp="3unh3tof0u89h"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" data-id="element-12835" data-mp="chukawym5pzs"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12836" data-mp="8fhhohf5y878">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12837" data-mp="m6chl47opeyo">
                      
                      <line x1="8" y1="6" x2="21" y2="6" data-id="element-12838" data-mp="w6wnik65f7n8"></line>
                      <line x1="8" y1="12" x2="21" y2="12" data-id="element-12839" data-mp="r6qtwygbjb2i"></line>
                      <line x1="8" y1="18" x2="21" y2="18" data-id="element-12840" data-mp="r8legbnoh162"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6" data-id="element-12841" data-mp="1atllghkr5cri"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12" data-id="element-12842" data-mp="1eu5f3b649y8w"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18" data-id="element-12843" data-mp="1etchazp4yj2w"></line>
                    </svg>
                  </button>
                </div>

                {/* Input Area */}
                <div className="p-3 min-h-[80px] bg-white" data-id="element-12844" data-mp="15s5z8m6u907j">
                  <p className="text-neutral-400 text-[15px]" data-id="element-12845" data-mp="r4t9ela008bc">Message Giga</p>
                </div>

                {/* Bottom Actions */}
                <div className="bg-white px-2 pb-2 flex items-center justify-between" data-id="element-12846" data-mp="vw2xvijt95ln">
                  <div className="flex items-center gap-1" data-id="element-12847" data-mp="i4xt621l5n4g">
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-12848" data-mp="n2zfcvub62k5">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12849" data-mp="85p1xgy3umls">
                        
                        <line x1="12" y1="5" x2="12" y2="19" data-id="element-12850" data-mp="d6moe5q5e2t3"></line>
                        <line x1="5" y1="12" x2="19" y2="12" data-id="element-12851" data-mp="3q7497dxcubh3"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-12852" data-mp="3lczyqkovrlb9">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12853" data-mp="3gdexu4q5h2vw">
                        
                        <circle cx="12" cy="12" r="10" data-id="element-12854" data-mp="cwydx2chscum"></circle>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" data-id="element-12855" data-mp="819csuktqnw7"></path>
                        <line x1="9" y1="9" x2="9.01" y2="9" data-id="element-12856" data-mp="30lc1h6smsos"></line>
                        <line x1="15" y1="9" x2="15.01" y2="9" data-id="element-12857" data-mp="32y10eayy3bc"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-12858" data-mp="2nqu8f9t8j0uz">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12859" data-mp="2is9694ufa73w">
                        
                        <circle cx="12" cy="12" r="4" data-id="element-12860" data-mp="1391jy9er7gz3"></circle>
                        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" data-id="element-12861" data-mp="139l4lxvsgjgt"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-12862" data-mp="1cz4jytkalv6x">
                    <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded" data-id="element-12863" data-mp="1d0o4wxgw8uf1">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12864" data-mp="1i0aduupovoi1">
                        
                        <line x1="22" y1="2" x2="11" y2="13" data-id="element-12865" data-mp="1qwjcw89r8l3n"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2" data-id="element-12866" data-mp="1vwe82o0kbwui"></polygon>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 10: Welcome back (signed out / sign in to another workspace)
  if (step === 10) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen bg-[#FAF8F4] font-body flex flex-col items-center px-6 overflow-hidden" data-id="element-12867" data-mp="1yieyieg3jnh9">
        
        {/* Brand cream surface with a soft warm bloom so it stays consistent
                                                                  with the rest of the auth flow (the "Try Giga free" screen). The
                                                                  orange primary CTA reads boldly against the cream. */}
        <div aria-hidden="true" className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[760px] rounded-full bg-[#EA580C]/[0.07] blur-[160px]" data-id="element-12868" data-mp="1uegwnvzdzyki" />
        
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#EA580C]/[0.05] blur-[150px]" data-id="element-12869" data-mp="35xtcrle6bhzi" />
        

        {/* Return home */}
        <Link to="/" className="absolute z-20 top-6 left-6 flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-12870" data-mp="38i7rnffco3om">
          
          <ArrowLeft size={16} data-id="element-12871" data-mp="2zh19z30ysmts" />
          Back to home
        </Link>

        <div className="relative z-10 w-full max-w-md flex-1 flex flex-col items-center justify-center text-center" data-id="element-12872" data-mp="33ga5acbnksrg">
          {/* Logo */}
          <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-lg shadow-[#F97316]/20 mb-8" data-id="element-12873" data-mp="3r7ef1fr39jgr">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-9 h-9 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12874" data-mp="3ieayuzp26sif" />
            
          </div>

          <h1 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight mb-4" data-id="element-12875" data-mp="dxzzr6ncb9zg">
            {signedOut ? 'Sign in to Giga' : 'Welcome back'}
          </h1>

          <p className="text-sm text-neutral-500 mb-10" data-id="element-12876" data-mp="1k7bda9ujwnsy">
            {signedOut ? "You've been signed out of all workspaces." : 'Pick up right where you left off.'}
          </p>

          <div className="w-full space-y-3" data-id="element-12877" data-mp="1t16dr4kq9q29">
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-12878" data-mp="1t2hfyzyiypr9">
              
              <SlackIcon size={20} data-id="element-12879" data-mp="15bbykgcxrpeu" />
              <span className="font-semibold text-white" data-id="element-12880" data-mp="15cn52nxrnb9n">
                Continue with Slack
              </span>
            </button>
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border border-[#EAE4D9] bg-white transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-12881" data-mp="1f99n74ypjo7z">
              <GoogleIcon size={20} data-id="element-12882" data-mp="2qpq7bbl7it16" />
              <span className="font-semibold text-[#2B211A]" data-id="element-12883" data-mp="f0te49f0gtw3">
                Continue with Google
              </span>
            </button>
            {teamsWaitlistDone ? <div className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl bg-[#EA580C]/[0.08] border border-[#EA580C]/20 text-[#2B211A]" data-id="element-12884" data-mp="a3t981twdx6k">
                <Check size={18} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-12885" data-mp="b4a86fjabs4" />
                <span className="text-sm font-medium" data-id="element-12886" data-mp="3sghl4qp7gsxo">
                  You're on the Microsoft Teams waitlist — we'll email you when
                  it's ready.
                </span>
              </div> : teamsWaitlistOpen ? <form onSubmit={submitTeamsWaitlist} className="w-full flex flex-col sm:flex-row gap-2" data-id="element-12887" data-mp="3ii1xixpe01sx">
              
                <label htmlFor="teams-waitlist-email" className="sr-only" data-id="element-12888" data-mp="3dk0egcryo2hg">
                  Work email for the Microsoft Teams waitlist
                </label>
                <input id="teams-waitlist-email" type="email" required autoFocus value={teamsWaitlistEmail} onChange={e => setTeamsWaitlistEmail(e.target.value)} placeholder="you@company.com" className="flex-1 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] placeholder:text-neutral-400 shadow-sm focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30 transition-all" data-id="element-12889" data-mp="1brvoh6u0vf9d" />
              
                <button type="submit" className="shrink-0 flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-12890" data-mp="3187nzclk6qiu">
                
                  Notify me
                  <ArrowRight size={18} data-id="element-12891" data-mp="3fvstfd5wp1tx" />
                </button>
              </form> : <button onClick={() => setTeamsWaitlistOpen(true)} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] shadow-sm hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-12892" data-mp="3jxwfswu45qtz">
              
                <TeamsIcon size={20} data-id="element-12893" data-mp="20zrkcwkmsoec" />
                <span className="font-medium" data-id="element-12894" data-mp="20yq2a85771n4">
                  Join Microsoft Teams waitlist
                </span>
              </button>}
          </div>

          {/* Your Workspaces Section — hidden once the user signs out of all
                                                              workspaces, leaving a clean sign-in with just the providers above. */}
          {!signedOut && <div className="w-full mt-10" data-id="element-12895" data-mp="1i8gtugitidmz">
              <div className="flex items-center gap-4 mb-6" data-id="element-12896" data-mp="1i9jiehso43zq">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12897" data-mp="1daykip0w1weg" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-12898" data-mp="1rz321po7g1vn">
                  Your Workspaces
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12899" data-mp="1n7l47ahhahew" />
              </div>

              <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-between p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-white transition-all group text-left bg-white shadow-sm" data-id="element-12900" data-mp="zhqqkqpc4otl">
              
                <div className="flex items-center gap-4" data-id="element-12901" data-mp="1te0zj4hnx23">
                  <div className="w-10 h-10 rounded-lg bg-[#2B211A] border border-black/10 text-white flex items-center justify-center font-bold text-sm" data-id="element-12902" data-mp="1rt7lwtwuk5t">
                    FT
                  </div>
                  <div className="flex flex-col" data-id="element-12903" data-mp="bls70o2lnpu2">
                    <span className="font-semibold text-[#2B211A]" data-id="element-12904" data-mp="gjl9b60s7zp2">
                      Faith Test
                    </span>
                    <span className="text-sm text-neutral-500" data-id="element-12905" data-mp="lgbq5vkeuzbj">
                      alqahtani.faith03@gmail.com
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-12906" data-mp="uh9wikibnixa">
                  <Check size={20} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-12907" data-mp="2rb38r4muhbhv" />
                
                  <ArrowRight size={20} className="text-neutral-400 group-hover:text-[#EA580C] transition-colors" data-id="element-12908" data-mp="2w9oa57du217w" />
                
                </div>
              </button>

              <button onClick={() => setSignedOut(true)} className="mt-6 text-sm text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-12909" data-mp="2os89r03ewyu0">
              
                Sign out of all workspaces
              </button>
            </div>}

          <p className="mt-10 text-sm text-neutral-500" data-id="element-12910" data-mp="2a20xx37wue2a">
            No account?{' '}
            <button onClick={() => {
            // Send the user to the free-trial start ("Try Giga free")
            // screen — step 1 is the default signup entry.
            setSignedOut(false);
            setStep(1);
            navigate('/app-screen-mockups/signup');
          }} className="font-bold text-[#2B211A] underline hover:text-[#EA580C] transition-colors" data-id="element-12911" data-mp="37p128u4r9vna">
              
              Sign up
            </button>
          </p>
        </div>

        <p className="relative z-10 max-w-md text-center text-xs text-neutral-400 pb-8" data-id="element-12912" data-mp="1md9ccuotstyh">
          By logging in, you agree to the Giga{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12913" data-mp="1jqyynofmta9q">
            
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12914" data-mp="1ooilhcqeavda">
            
            Terms of Service
          </a>
          .
        </p>
      </motion.div>;
  }
  // Step 11: Trial Paywall
  if (step === 11) {
    const plans = [{
      id: 'plus',
      name: 'Plus',
      desc: 'The ultimate AI work assistant.',
      price: '$49.99/mo'
    }, {
      id: 'pro',
      name: 'Pro',
      desc: 'More usage for power users.',
      price: '$99.99/mo',
      popular: true
    }];
    const selectedPrice = plans.find(p => p.id === plan)?.price.split('/')[0] || '$49.99';
    const futureDate = new Date(Date.now() + 7 * 864e5).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    const selectedPlan = plans.find(p => p.id === plan) || plans[0];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen w-full bg-white font-body" data-id="element-12915" data-mp="m5krgowecv2u">
        
        {/* Real Stripe Checkout anatomy: full-height split, dark merchant
         summary rail on the left, white payment pane on the right. */}
        <div className="mx-auto grid min-h-screen w-full grid-cols-1 lg:grid-cols-2" data-id="element-12916" data-mp="7grr6dzjlz0e">
          {/* ── Left: dark order summary ─────────────────────────────────── */}
          <aside className="flex flex-col bg-[#1B130D] px-5 py-5 text-white sm:px-10 lg:py-16 lg:pl-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))] lg:pr-16" data-id="element-12917" data-mp="hf8lnkxowvr7">
            <div className="flex flex-1 flex-col" data-id="element-12918" data-mp="v640dduve72p">
              {/* back + merchant (logo in a white circle, exactly like Stripe) */}
              <div className="flex items-center gap-3" data-id="element-12919" data-mp="r7efz1bezp9k">
                <button type="button"
              // In the signup flow the paywall is reached from the tool
              // connections screen (step 7), not the sign-in screen
              // (step 10) — back should return to connections.
              onClick={() => setStep(7)} aria-label="Back" className="text-white/50 transition-colors hover:text-white" data-id="element-12920" data-mp="155l4vrydyex3">
                  
                  <ArrowLeft size={18} data-id="element-12921" data-mp="2gjvqqz7c0nt7" />
                </button>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white" data-id="element-12922" data-mp="2lhxc5qs8cz7u">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-4 w-4 object-contain" data-id="element-12923" data-mp="1nu6luari43mb" />
                  
                </span>
                <span className="text-[15px] font-medium text-white" data-id="element-12924" data-mp="1xq9pxm5wycbw">Giga</span>
              </div>

              {/* headline price */}
              <div className="mt-6 lg:mt-12" data-id="element-12925" data-mp="15475cc9q7wui">
                <p className="text-[14px] text-white/60 lg:text-[15px]" data-id="element-12926" data-mp="153f7svwkjp1l">
                  Subscribe to Giga {selectedPlan.name}
                </p>
                <div className="mt-1 flex items-start gap-2" data-id="element-12927" data-mp="12jsnqah60x1p">
                  <span className="text-[32px] font-bold leading-none tracking-tight lg:text-[44px]" data-id="element-12928" data-mp="12h6du46sy5gb">
                    {selectedPrice}
                  </span>
                  <span className="mt-0.5 text-[13px] leading-tight text-white/60 lg:mt-1 lg:text-[15px]" data-id="element-12929" data-mp="sjrzqb8cpsr6">
                    per
                    <br data-id="element-12930" data-mp="no2xu18uzzrp" />
                    month
                  </span>
                </div>
                <p className="mt-2 text-[13px] text-white/50 lg:mt-3 lg:text-[14px]" data-id="element-12931" data-mp="jmho2ormg1lq">
                  7-day free trial · US$0.00 due today
                </p>

                {/* plan segmented control */}
                <div className="mt-5 inline-flex items-center rounded-lg border border-white/15 p-0.5 text-[13px]" data-id="element-12932" data-mp="9s8ya8tz6mr9">
                  {plans.map(p => <button key={p.id} onClick={() => setPlan(p.id)} className={`flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 font-medium transition-colors ${plan === p.id ? 'bg-white text-[#1B130D]' : 'text-white/60 hover:text-white'}`} data-id="element-12933" data-mp="3w2bgohlkxc6o">
                    
                      {p.name}
                      {p.popular && <span className={`rounded px-1 text-[10px] font-bold uppercase ${plan === p.id ? 'bg-[#EA580C] text-white' : 'text-[#EA580C]'}`} data-id="element-12934" data-mp="2bxf7q05k87cm">
                      
                          Popular
                        </span>}
                    </button>)}
                </div>
              </div>

              {/* mobile-only details disclosure */}
              <button type="button" onClick={() => setPayDetailsOpen(o => !o)} aria-expanded={payDetailsOpen} className="mt-4 flex items-center gap-1.5 self-start text-[13px] font-medium text-white/60 transition-colors hover:text-white lg:hidden" data-id="element-12935" data-mp="1t75yi6bcvfbf">
                
                {payDetailsOpen ? 'Hide details' : 'View details'}
                <ChevronDown size={15} className={`transition-transform ${payDetailsOpen ? 'rotate-180' : ''}`} data-id="element-12936" data-mp="1f7o67gyupjxb" />
                
              </button>

              {/* breakdown */}
              <div className={`${payDetailsOpen ? 'block' : 'hidden'} lg:block`} data-id="element-12937" data-mp="3lw6nur48lqzm">
                <div className="mt-6 flex items-start justify-between gap-4 lg:mt-12" data-id="element-12938" data-mp="3qurqsyaviorr">
                  <div data-id="element-12939" data-mp="3qwkxgdlhl8zr">
                    <p className="text-[15px] font-medium text-white" data-id="element-12940" data-mp="3vtbccxlk1yjl">
                      Giga {selectedPlan.name}
                    </p>
                    <p className="mt-0.5 text-[13px] text-white/50" data-id="element-12941" data-mp="3pikp6sqzdwl">
                      Billed monthly
                    </p>
                  </div>
                  <p className="text-[15px] font-medium text-white" data-id="element-12942" data-mp="383qr281xip75">
                    {selectedPrice}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-6" data-id="element-12943" data-mp="3c4kez1fdg401">
                  <span className="text-[15px] font-medium text-white" data-id="element-12944" data-mp="3gxveehhutihx">
                    Subtotal
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-12945" data-mp="2og9l4i83f4l9">
                    {selectedPrice}
                  </span>
                </div>

                <div className="mt-5" data-id="element-12946" data-mp="3rpvqaawznmmq">
                  <button type="button" className="rounded-lg bg-white/[0.07] px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-white/10" data-id="element-12947" data-mp="3rou8lnlgw1w1">
                    
                    Add promotion code
                  </button>
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-5" data-id="element-12948" data-mp="dawc39sqq9j">
                  <span className="text-[15px] font-medium text-white" data-id="element-12949" data-mp="38skuj0akfbc1">
                    Total due today
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-12950" data-mp="3hsr4wdayvv6z">
                    US$0.00
                  </span>
                </div>
              </div>

              {/* footer pinned bottom — desktop only */}
              <div className="mt-auto hidden items-center gap-4 pt-14 text-[12px] text-white/40 lg:flex" data-id="element-12951" data-mp="x20e7tlfbivi">
                <span className="flex items-center gap-1.5" data-id="element-12952" data-mp="t054c0pffx64">
                  Powered by{' '}
                  <span className="font-bold text-white/70" data-id="element-12953" data-mp="10jo4hjqsy71x">stripe</span>
                </span>
                <span className="h-3 w-px bg-white/20" data-id="element-12954" data-mp="15k2c50ohrezf" />
                <button type="button" className="hover:text-white/70" data-id="element-12955" data-mp="15hfwsevczv6n">
                  Terms
                </button>
                <button type="button" className="hover:text-white/70" data-id="element-12956" data-mp="vlcrx194hig0">
                  Privacy
                </button>
              </div>
            </div>
          </aside>

          {/* ── Right: white payment form ─────────────────────────────────── */}
          <div className="px-5 py-8 sm:px-10 lg:py-16 lg:pl-16 lg:pr-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))]" data-id="element-12957" data-mp="1o9idfq803bgw">
            <div className="mx-auto w-full max-w-md" data-id="element-12958" data-mp="19jjjjznham78">
              {/* Express checkout — wallets above the card form */}
              <div className="grid grid-cols-2 gap-3" data-id="element-12959" data-mp="22ab9wghv4gmn">
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Apple Pay" className="flex h-11 items-center justify-center gap-1 rounded-lg bg-black text-white transition-opacity hover:opacity-85" data-id="element-12960" data-mp="228qj91yo5hu2">
                  
                  <svg viewBox="0 0 384 512" className="h-[17px] w-[17px] fill-current" aria-hidden="true" data-id="element-12961" data-mp="ecu0vu044znf">
                    
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" data-id="element-12962" data-mp="9f1zm9dkw16k" />
                  </svg>
                  <span className="text-[16px] font-medium" data-id="element-12963" data-mp="nd08iz94r5eo">Pay</span>
                </button>
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Google Pay" className="flex h-11 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white text-[#3c4043] transition-colors hover:bg-neutral-50" data-id="element-12964" data-mp="1kuh5y3rog5ec">
                  
                  <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google.svg" alt="" className="h-[18px] w-[18px]" data-id="element-12965" data-mp="1scf1t5gxrhz3" />
                  
                  <span className="text-[15px] font-medium" data-id="element-12966" data-mp="1xe4e0ofwa1or">Pay</span>
                </button>
              </div>

              <div className="my-6 flex items-center gap-4 text-[14px] text-[#6B7280]" data-id="element-12967" data-mp="228rwa5kb1zuw">
                <span className="h-px flex-1 bg-neutral-200" data-id="element-12968" data-mp="2784o74q19x4c" />
                Or pay with card
                <span className="h-px flex-1 bg-neutral-200" data-id="element-12969" data-mp="2b9pxkg3czt9s" />
              </div>

              <div className="space-y-4" data-id="element-12970" data-mp="2g32kmlse3s0f">
                <div data-id="element-12971" data-mp="2l1ckh9p27gsd">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12972" data-mp="2l1vr3u99eir3">
                    Email
                  </label>
                  <input type="email" placeholder="faith@company.com" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-12973" data-mp="2q2jrv7naidyi" />
                  
                </div>

                <div data-id="element-12974" data-mp="2f1hbel94hea6">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12975" data-mp="2f1ply6hrw7i7">
                    Card information
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-12976" data-mp="2a8o3xpm9rrue">
                    <div className="flex items-center border-b border-neutral-200" data-id="element-12977" data-mp="2j29lfgptjn7i">
                      <input placeholder="1234 1234 1234 1234" inputMode="numeric" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12978" data-mp="2xzadjlbx9k6i" />
                      
                      <div className="flex items-center gap-1 pr-3" aria-hidden="true" data-id="element-12979" data-mp="13mu64h7e4002">
                        
                        {['visa', 'mastercard', 'amex', 'discover'].map(brand => <img key={brand} src={`https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons/flat/${brand}.svg`} alt="" className="h-4 w-auto rounded-[2px]" data-id="element-12980" data-mp="3abm6ctvhrby1" />)}
                      </div>
                    </div>
                    <div className="flex" data-id="element-12981" data-mp="30khui314477f">
                      <input placeholder="MM / YY" className="w-1/2 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12982" data-mp="35jb59np0lk3w" />
                      
                      <input placeholder="CVC" inputMode="numeric" className="w-1/2 border-l border-neutral-200 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12983" data-mp="2vleyt970vdc4" />
                      
                    </div>
                  </div>
                </div>

                <div data-id="element-12984" data-mp="fusgfs88gpfs">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12985" data-mp="fvka2xi9sd3f">
                    Name on card
                  </label>
                  <input placeholder="Faith Anderson" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-12986" data-mp="krj2qxamsh9n" />
                  
                </div>

                <div data-id="element-12987" data-mp="2q0rr3415orjq">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12988" data-mp="2q1jomkebczcn">
                    Country or region
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-12989" data-mp="2l2yk48s13tie">
                    <div className="relative border-b border-neutral-200" data-id="element-12990" data-mp="2c1s4e1x7emq7">
                      <select defaultValue="United States" className="w-full appearance-none bg-transparent px-3 py-2.5 pr-9 text-[15px] text-[#30313D] outline-none" data-id="element-12991" data-mp="272nwrfpxgx1z">
                        
                        <option data-id="element-12992" data-mp="59zlcj34r3b4">United States</option>
                        <option data-id="element-12993" data-mp="58g70y11b2bz">Canada</option>
                        <option data-id="element-12994" data-mp="cutssbx5t32s">United Kingdom</option>
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-12995" data-mp="hsvcmz2et6fb" />
                      
                    </div>
                    <input placeholder="ZIP" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12996" data-mp="riycnj7y7kn5" />
                    
                  </div>
                </div>
              </div>

              <button onClick={() => setStep(finalStep)} className="mt-6 w-full rounded-lg bg-[#EA580C] py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[#d24e08]" data-id="element-12997" data-mp="3v0g80r6jaz6o">
                
                Subscribe
              </button>

              <p className="mt-4 text-center text-[12px] leading-relaxed text-[#8792A2]" data-id="element-12998" data-mp="1f8ix66jr2pyj">
                By confirming your subscription, you allow Giga to charge your
                card for this payment and future payments in accordance with
                their terms. Your 7-day trial is free — you won't be charged
                until {futureDate}, and you can cancel anytime.
              </p>

              {/* mobile-only Stripe footer */}
              <div className="mt-8 flex items-center justify-center gap-4 text-[12px] text-neutral-400 lg:hidden" data-id="element-12999" data-mp="20lkt1fu6yeq2">
                <span className="flex items-center gap-1.5" data-id="element-13000" data-mp="25je0ec78derx">
                  Powered by{' '}
                  <span className="font-bold text-neutral-500" data-id="element-13001" data-mp="1rk4j197agemd">stripe</span>
                </span>
                <span className="h-3 w-px bg-neutral-200" data-id="element-13002" data-mp="1wltugpyfauaz" />
                <button type="button" className="hover:text-neutral-600" data-id="element-13003" data-mp="1ho1ig7s34o1j">
                  Terms
                </button>
                <button type="button" className="hover:text-neutral-600" data-id="element-13004" data-mp="1mn4e51m6dhwu">
                  Privacy
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 1: Landing
  const LANDING_TESTIMONIALS = [{
    quote: "Nothing short of revolutionary. The best attempt I've ever had as a CEO to turn every employee into an AI-first operator.",
    name: 'Tony Ennis',
    title: 'CEO, Brightwave',
    image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/image_14.png"
  }, {
    quote: "Giga has completely transformed how our engineering team accesses context. It's like having a senior engineer who knows everything.",
    name: 'Sarah Chen',
    title: 'VP Engineering, Northwind',
    image: 'https://i.pravatar.cc/150?u=sarah'
  }, {
    quote: 'The seamless integration with Slack means my team actually uses it. No new tabs, just instant answers where we already work.',
    name: 'Marcus Lee',
    title: 'Product Lead, Acme Corp',
    image: 'https://i.pravatar.cc/150?u=marcus'
  }];
  return <div className="min-h-screen flex font-body bg-white" data-id="element-13005" data-mp="3uty2v1zc56mx">
      {/* Teams isn't live — the hover state on that button opens this. */}
      <AnimatePresence data-id="element-13006" data-mp="1yruzqsi959ck">
        {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => setShowWaitlist(false)} data-id="element-13007" data-mp="1yswcr2itw5wx" />}
      </AnimatePresence>

      {/* Left Pane - Product Scenery */}
      <div className="hidden lg:flex w-1/2 bg-[#2B211A] border-r border-neutral-800 relative overflow-hidden flex-col p-12" data-id="element-13008" data-mp="27tuetf9pi3cv">
        {/* Shared dark-green grid + subtle glow */}
        <DarkGreenTexture glow="top-left" data-id="element-13009" data-mp="2hpfgfcav51m3" />

        <Link to="/" aria-label="Giga home" className="relative z-10 flex items-center gap-2 mb-20 md:mb-24" data-id="element-13010" data-mp="2mo8qsvuusci1">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" data-id="element-13011" data-mp="2rgjt25d2gadr" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-13012" data-mp="30e2j5o5khewi" />
          
        </Link>

        {/* Product story — auto-cycling feature showcase (Integrations,
         Smart Memory, Autonomous work) */}
        <div className="relative z-10 flex flex-col items-center w-full" data-id="element-13013" data-mp="35lao92089u93">
          <SignupShowcase compactCounter counterPosition="bottom" headlinePosition="bottom" data-id="element-13014" data-mp="31kgzk6eyobf5" />
          
          {false && <motion.div initial="hidden" animate="show" variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.12,
              delayChildren: 0.1
            }
          }
        }} className="w-full max-w-sm flex flex-col items-center gap-6" data-id="element-13015" data-mp="23sh6wsa4r9q">
            
              {/* Speed chip */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: -8
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-4 py-1.5 text-[#2B211A] text-xs font-bold shadow-lg" data-id="element-13016" data-mp="kujnfpgz01jw">
              
                <motion.span animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.6, 1]
            }} transition={{
              duration: 1.4,
              repeat: Infinity
            }} className="w-2 h-2 rounded-full bg-[#2B211A]" data-id="element-13017" data-mp="5mpup969dn1o" />
              
                Live in under 60 seconds
              </motion.div>

              {/* Setup checklist — ticks off fast */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: 12
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="w-full bg-white border border-neutral-200 rounded-2xl p-5 shadow-xl shadow-black/5 space-y-3" data-id="element-13018" data-mp="ob4kjt4uav0w">
              
                {[{
              label: 'Connected to Slack',
              activeLabel: 'Connecting to Slack…',
              icon: 'slack'
            }, {
              label: 'Synced your tools',
              activeLabel: 'Syncing your tools…',
              tools: true
            }, {
              label: 'Giga is live',
              activeLabel: 'Booting Giga…',
              giga: true
            }].map((item, i) => {
              const isDone = setupStep > i;
              const isActive = setupStep === i;
              return <motion.div key={item.label} variants={{
                hidden: {
                  opacity: 0,
                  x: -10
                },
                show: {
                  opacity: 1,
                  x: 0
                }
              }} className={`relative flex items-center justify-between gap-3 rounded-xl px-2 py-1 transition-colors ${item.giga && isDone ? 'bg-[#F97316]/10' : ''}`} data-id="element-13019" data-mp="28svpog3t2gtu" data-mpi={i}>
                    
                      {/* Lime glow payoff on the final 'Giga is live' row */}
                      {item.giga && isDone && <motion.span aria-hidden="true" initial={{
                  opacity: 0
                }} animate={{
                  opacity: [0, 1, 0.5]
                }} transition={{
                  duration: 0.8
                }} className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-[#F97316]/50" data-id="element-13020" data-mp="3pa19mrwfai5y" />}
                      <div className="relative flex items-center gap-3" data-id="element-13021" data-mp="f3jj8ud89qz1">
                        <div className="w-6 h-6 shrink-0 flex items-center justify-center" data-id="element-13022" data-mp="f0njr1lqeu8h">
                          <AnimatePresence mode="wait" data-id="element-13023" data-mp="3dissnpxcz3py">
                            {isDone ? <motion.div key="done" initial={{
                        scale: 0
                      }} animate={{
                        scale: 1
                      }} transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 18
                      }} className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center" data-id="element-13024" data-mp="3iki3p5kkzhe1">
                            
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2B211A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-13025" data-mp="1gxulbbj9qhna">
                              
                                  <path d="M20 6L9 17l-5-5" data-id="element-13026" data-mp="17wwrfm2jnqif" />
                                </svg>
                              </motion.div> : isActive ? <motion.div key="active" className="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-[#EA580C] animate-spin" data-id="element-13027" data-mp="12xa58n4qmzxh" /> : <motion.div key="pending" className="w-5 h-5 rounded-full border-2 border-neutral-200" data-id="element-13028" data-mp="y0jnlvdabw9y" />}
                          </AnimatePresence>
                        </div>
                        <span className={`font-medium text-sm transition-colors ${isDone ? 'text-neutral-700' : isActive ? 'text-neutral-500' : 'text-neutral-300'}`} data-id="element-13029" data-mp="p7gdnx1um1k2">
                        
                          {isActive ? item.activeLabel : item.label}
                        </span>
                      </div>

                      {/* Right-side badges fade in once the row completes */}
                      <AnimatePresence data-id="element-13030" data-mp="1z4d5tsx8twsf">
                        {isDone && item.icon === 'slack' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm border border-neutral-100" data-id="element-13031" data-mp="235q9pxgrzpwp">
                        
                            <SlackIcon size={16} data-id="element-13032" data-mp="1k6kqp1vlzaer" />
                          </motion.div>}

                        {isDone && item.tools && <div className="flex -space-x-1.5" data-id="element-13033" data-mp="16fg00pa7xsgk">
                            {['notion', 'linear', 'google-drive', 'figma'].map((slug, j) => <motion.div key={slug} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      delay: j * 0.06
                    }} className="w-7 h-7 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden" data-id="element-13034" data-mp="1bfc2gk39pbu0" data-mpi={j}>
                            
                                  <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt={slug} className="w-4 h-4 object-contain" data-id="element-13035" data-mp="rsr22rvm7nca" />
                            
                                </motion.div>)}
                          </div>}
                      </AnimatePresence>
                    </motion.div>;
            })}
              </motion.div>
            </motion.div>}
        </div>

        {/* Social proof — anchored to the bottom of the brand pane so the
         form column stays focused on the CTAs and workspace list. */}
        <div className="relative z-10 mt-auto flex items-center gap-4 pt-10" data-id="element-13036" data-mp="15m895d7477lf">
          <div className="flex -space-x-2.5" data-id="element-13037" data-mp="1khegc3hy5cgc">
            {["https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_26.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_25.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_24.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_23.png"].map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-8 h-8 rounded-full object-cover border-2 border-[#2B211A] shadow-sm relative" style={{
            zIndex: 4 - i
          }} data-id="element-13038" data-mp="21x1p2z7fwmfi" data-mpi={i} />)}
          </div>
          <div className="flex flex-col gap-0.5" data-id="element-13039" data-mp="2ay82v0ipfj52">
            <div className="flex items-center gap-0.5" aria-hidden="true" data-id="element-13040" data-mp="li8qlajshdl7">
              {[0, 1, 2, 3, 4].map(i => <Star key={i} className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" data-id="element-13041" data-mp="gj5l5ozwd5cp" />)}
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/60" data-id="element-13042" data-mp="2s6itsodem7a7">
              Loved by early adopters
            </span>
          </div>
        </div>
      </div>

      {/* Right Pane - Signup Form */}
      <div className="w-full lg:w-1/2 max-lg:min-h-screen flex flex-col items-center justify-start pt-0 lg:p-12 relative bg-[#FAF8F4] overflow-hidden" data-id="element-13043" data-mp="2ihhgj5y8u3zx">
        {/* Mobile-only brand texture — a square-dot halftone field that fades in
         at the top and bottom edges and clears through the middle so the form
         stays clean. Hidden on desktop, where the left pane carries the brand. */}
        <div aria-hidden="true" className="lg:hidden dot-matrix z-0" style={{
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)',
        maskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)'
      }} data-id="element-13044" data-mp="1zimztrf7yth4" />
        

        {/* Mobile-only brand lockup — pinned to the very top-left of the screen.
         On desktop (lg+) the left pane already shows the Giga logo. */}
        <Link to="/" aria-label="Giga home" className="flex lg:hidden items-center gap-2 absolute top-6 left-6 z-20" data-id="element-13045" data-mp="3sepa5oko8gcp">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-8 h-8 object-contain" data-id="element-13046" data-mp="3neujnm4t5ss6" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-13047" data-mp="jy48ytya6rk5" />
          
        </Link>

        {/* Small top spacer on desktop — the pane now carries the workspaces
         list too, so the content block starts higher to fit evenly. */}
        <div className="hidden lg:block h-4 mb-4" aria-hidden="true" data-id="element-13048" data-mp="12q6t4fa4spz5" />

        <div className="relative z-10 w-full max-w-[400px] flex flex-col flex-1 lg:flex-none justify-center lg:justify-start px-6 md:px-8 lg:px-0 pt-16 lg:pt-0 pb-5 lg:pb-0" data-id="element-13049" data-mp="xyf0lz7f0a6d">
          {/* The eyebrow and the separate offer badge are gone — three stacked
           labels above the headline was the source of the crowding. The offer
           now lives in the headline itself, where it gets real weight, with
           the amount in orange so it still reads at a glance. */}
          <h1 className="font-title font-medium text-2xl md:text-4xl tracking-tight text-[#2B211A] mb-3 text-center lg:text-left" data-id="element-13050" data-mp="2zkj1ueqwipk5">
            {isLogin ? <>
                Your entire work stack,
                <br data-id="element-13051" data-mp="39acdffr1n4oe" />
                one chat away.
              </> : <>
                Try Giga free, with
                <br data-id="element-13052" data-mp="3dc7c0chlb3yh" />
                <span className="text-[#EA580C]" data-id="element-13053" data-mp="3damkkvqko14u">$100 in credits</span> included.
              </>}
          </h1>

          {/* Short product line rather than the old three-clause sentence.
           The headline now carries the offer, so this only has to say what
           Giga is — and the task feed beside it already demonstrates the
           "connect, ask, act" story better than a sentence can. */}
          <p className="text-[15px] md:text-base text-neutral-500 leading-relaxed mb-6 text-center lg:text-left" data-id="element-13054" data-mp="34na6qkkg2ctj">
            Every tool your company runs on, one brain.
          </p>

          {/* Mobile-only notification illustration. The avatar/star social
           proof that used to sit here was removed — on a screen whose only job
           is "pick an auth method", it was a third competing block above the
           fold. Desktop still carries it in the left brand pane. */}
          <div className="lg:hidden w-full flex justify-center mb-5" data-id="element-13055" data-mp="30nhrjp0jp8f7">
            <SignupShowcase variant="light" counterPosition="bottom" compactCounter data-id="element-13056" data-mp="2vnn640zjhb1j" />
          </div>

          <div className="flex flex-col gap-3" data-id="element-13057" data-mp="fvps57hgklp6">
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('slack');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(2);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl bg-[#2B211A] text-white font-bold transition-shadow hover:shadow-lg hover:shadow-black/5" data-id="element-13058" data-mp="fued2bikbrir">
              
              <SlackIcon size={20} data-id="element-13059" data-mp="165yknlas2zzl" />
              Continue with Slack
            </motion.button>
            {/* Google is a full second front door, not a fallback — plenty of
             teams have no Slack at all. It sits directly under Slack (equal
             weight, lighter fill) so neither reads as the only way in. */}
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('google');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(20);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl border border-[#EAE4D9] bg-white font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-13060" data-mp="1k4y6kl6wp0s2">
              <GoogleIcon size={20} data-id="element-13061" data-mp="1b2lskldgxcmb" />
              Continue with Google
            </motion.button>
            {/* Teams isn't live. Rather than a dead disabled row, hovering
             turns it into the one thing you can actually do: join the list. */}
            <button type="button" onClick={() => setShowWaitlist(true)} className="group relative flex w-full items-center justify-center gap-3 rounded-xl border border-[#EFEAE1] bg-white/60 px-4 py-3 font-bold text-neutral-400 transition-all hover:border-[#D4D0C8] hover:bg-white hover:text-[#2B211A] md:py-3.5" data-id="element-13062" data-mp="120cl64dhxzd5">
              <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg" alt="" aria-hidden="true" className="h-5 w-5 opacity-40 transition-opacity group-hover:opacity-100" data-id="element-13063" data-mp="i2p5s6ryjwh6" />
              <span className="group-hover:hidden" data-id="element-13064" data-mp="wxlgqrr5ixxy">Continue with Teams</span>
              <span className="hidden group-hover:inline" data-id="element-13065" data-mp="wvituglapkxg">Join the waitlist</span>
              <span className="absolute right-4 rounded-full bg-[#F0EBE2] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-500 transition-colors group-hover:bg-[#FFEDD5] group-hover:text-[#B4470F]" data-id="element-13066" data-mp="3vgsgou06w4is">
                Soon
              </span>
            </button>
          </div>

          {/* Book a demo — a real third option for buyers who want a walkthrough
           before connecting a workspace, but outlined and set apart by an "or"
           rule so the two auth buttons stay the primary path. */}
          {!isLogin && <>
              <div className="mt-4 flex items-center gap-4" aria-hidden="true" data-id="element-13067" data-mp="zodb6u1wbz5e">
                <div className="h-px flex-1 bg-neutral-200" data-id="element-13068" data-mp="vnbb6b05nj2d" />
                <span className="text-[13px] text-neutral-400" data-id="element-13069" data-mp="vp4h1o2y1z9b">or</span>
                <div className="h-px flex-1 bg-neutral-200" data-id="element-13070" data-mp="19o44iseq083w" />
              </div>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="mt-4 flex w-full items-center justify-center rounded-xl border border-[#EAE4D9] bg-white px-4 py-3 font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:bg-neutral-50" data-id="element-13071" data-mp="14ngfgca1y1cb">
                Book a demo
              </a>
              <p className="mt-4 text-center text-[13px] text-neutral-400" data-id="element-13072" data-mp="1sc94dos9b8xf">
                No credit card required
              </p>
            </>}

          {/* Your Workspaces — returning users with remembered sessions can
           jump straight into a workspace instead of re-authing. Only the most
           recent one shows by default: three near-identical rows made the
           screen feel busy, and the other two are one click away. */}
          {!signedOut && <div className="w-full mt-5 md:mt-6" data-id="element-13073" data-mp="2kzsca99k3n2h">
              <div className="flex items-center gap-4 mb-3" data-id="element-13074" data-mp="2kzinuq45tnyw">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-13075" data-mp="2712gcwyh3tfp" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-13076" data-mp="272n8kfxbf0ae">
                  Continue as
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-13077" data-mp="1x9er92pe2ohe" />
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-white divide-y divide-neutral-100 overflow-hidden" data-id="element-13078" data-mp="221g2qmeqgb6d">
                {[{
              initials: 'T',
              name: 'Tonic',
              email: 'faith@tonichq.com'
            }, ...(showAllWorkspaces ? [{
              initials: 'B',
              name: 'Brightwave',
              email: 'sarah.chen@brightwave.io'
            }, {
              initials: 'N',
              name: 'Northwind Labs',
              email: 'marcus.lee@northwindlabs.com'
            }] : [])].map(ws => <button key={ws.name} onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-[#FFFCF8] transition-colors group text-left" data-id="element-13079" data-mp="1f8dkwpb729ig">
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-[#2B211A] text-white flex items-center justify-center font-bold text-[13px]" data-id="element-13080" data-mp="1p6jiwvtvqvi1">
                      {ws.initials}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col" data-id="element-13081" data-mp="1y3anjwmhzio8">
                      <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-13082" data-mp="232ncqo0ud1tz">
                        {ws.name}
                      </span>
                      <span className="text-[13px] text-neutral-500 truncate" data-id="element-13083" data-mp="2ctrq5javcemp">
                        {ws.email}
                      </span>
                    </div>
                    <div className="flex w-6 items-center justify-end shrink-0" data-id="element-13084" data-mp="2gtjshe9o5ojm">
                      <ArrowRight size={16} className="text-neutral-300 group-hover:text-[#EA580C] group-hover:translate-x-0.5 transition-all" data-id="element-13085" data-mp="2lv9691oa0gbe" />
                    </div>
                  </button>)}
              </div>

              <div className="mt-3 flex items-center justify-center gap-2 text-[13px] text-neutral-400" data-id="element-13086" data-mp="5aduwtnchvec">
                {!showAllWorkspaces && <>
                    <button onClick={() => setShowAllWorkspaces(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-13087" data-mp="3sdqvkq2y7a0w">
                      Use another workspace
                    </button>
                    <span aria-hidden="true" data-id="element-13088" data-mp="ow0f52y35jv3">·</span>
                  </>}
                <button onClick={() => setSignedOut(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-13089" data-mp="k5jxt7r59e3g">
                  Sign out
                </button>
              </div>
            </div>}

          {/* Returning users — quiet sign-in link, but ONLY when there are no
           remembered workspaces on this device (the Your Workspaces list IS
           the signed-in path, so showing both would be redundant). Hidden on
           /login, which already is the sign-in. */}
          {!isLogin && signedOut && <p className="mt-8 text-center text-sm text-neutral-500" data-id="element-13090" data-mp="1vqzkb45mqhtx">
              Already have an account?{' '}
              <Link to="/app-screen-mockups/sign-in" className="font-bold text-[#2B211A] underline underline-offset-2 hover:text-[#EA580C] transition-colors" data-id="element-13091" data-mp="1hzuokd5du9ov">
              
                Sign in
              </Link>
            </p>}

          <p className="text-center text-[11px] text-neutral-400 mt-3 pt-0 mb-0 leading-relaxed max-w-[320px] mx-auto" data-id="element-13092" data-mp="17zkbqtn9f4a8">
            By continuing, you agree to Giga's{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-13093" data-mp="z18pf3tfesnl">
              
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-13094" data-mp="2tdotbxyr1upa">
              
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>;
}